import React, { useState, useEffect, createContext, useContext, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useParams, useNavigate, useSearchParams, useLocation, Outlet } from "react-router-dom";
import axios from "axios";
import { AttachmentsUploader, AttachmentsList } from "./components/attachments";
import { useAttachments } from "./hooks/useAttachments";
import { TierBadge, SlaStrip, VerificationStatusCard, TierFilters } from "./components/verification";
import { QueryProvider } from "./providers/QueryProvider";
import SellerAnalyticsPage from "./pages/SellerAnalyticsPage";
import PointsBadge from "./components/points/PointsBadge";
import PointsBreakdown from "./components/points/PointsBreakdown";
import AdminDisputesPage from "./pages/AdminDisputesPage";

// Enhanced Loading Skeleton Components with Better Accessibility
const SkeletonCard = () => (
  <Card className="overflow-hidden">
    <CardContent className="p-6">
      <div className="animate-pulse" role="status" aria-label="Loading data">
        <div className="flex justify-between items-start mb-4">
          <div className="space-y-3 flex-1">
            <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-1/3 bg-[length:200%_100%] animate-shimmer"></div>
            <div className="h-10 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-2/3 bg-[length:200%_100%] animate-shimmer"></div>
            <div className="h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-1/4 bg-[length:200%_100%] animate-shimmer"></div>
          </div>
          <div className="ml-4">
            <div className="h-8 w-16 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg bg-[length:200%_100%] animate-shimmer"></div>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

const SkeletonTable = ({ rows = 5 }) => (
  <Card className="overflow-hidden">
    <CardHeader>
      <div className="animate-pulse" role="status" aria-label="Loading table data">
        <div className="flex justify-between items-center">
          <div className="h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-1/3 bg-[length:200%_100%] animate-shimmer"></div>
          <div className="h-8 w-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded bg-[length:200%_100%] animate-shimmer"></div>
        </div>
      </div>
    </CardHeader>
    <CardContent>
      <div className="space-y-4">
        {/* Table header skeleton */}
        <div className="grid grid-cols-4 gap-4 pb-3 border-b border-gray-200">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded bg-[length:200%_100%] animate-shimmer"></div>
          ))}
        </div>
        
        {/* Table rows skeleton */}
        {[...Array(rows)].map((_, i) => (
          <div key={i} className="grid grid-cols-4 gap-4 py-3 border-b border-gray-100">
            <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded bg-[length:200%_100%] animate-shimmer"></div>
            <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded w-3/4 bg-[length:200%_100%] animate-shimmer"></div>
            <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded w-1/2 bg-[length:200%_100%] animate-shimmer"></div>
            <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded w-2/3 bg-[length:200%_100%] animate-shimmer"></div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const SkeletonChart = () => (
  <Card className="overflow-hidden">
    <CardHeader>
      <div className="animate-pulse" role="status" aria-label="Loading chart data">
        <div className="h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-1/3 bg-[length:200%_100%] animate-shimmer"></div>
      </div>
    </CardHeader>
    <CardContent>
      <div className="h-64 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded bg-[length:200%_100%] animate-shimmer flex items-center justify-center">
        <div className="text-gray-400 text-sm font-medium">Loading chart...</div>
      </div>
      <div className="flex justify-between items-center mt-4">
        <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-1/4 bg-[length:200%_100%] animate-shimmer"></div>
        <div className="h-8 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-20 bg-[length:200%_100%] animate-shimmer"></div>
      </div>
    </CardContent>
  </Card>
);

// Enhanced Empty State Components with Better Accessibility
const EmptyStateCard = ({ title, description, icon, action, className = "" }) => (
  <Card className={`border-dashed border-2 border-gray-300 ${className}`}>
    <CardContent className="p-12 text-center" role="status" aria-live="polite">
      <div className="mb-6">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
          <span className="text-4xl" role="img" aria-hidden="true">{icon}</span>
        </div>
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed mb-6 max-w-md mx-auto">{description}</p>
      {action && (
        <div className="flex justify-center">
          {action}
        </div>
      )}
    </CardContent>
  </Card>
);

const EmptyTable = ({ title, description, icon, action = null }) => (
  <div className="text-center py-16 px-4" role="status" aria-live="polite">
    <div className="mb-6">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
        <span className="text-3xl" role="img" aria-hidden="true">{icon}</span>
      </div>
    </div>
    <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed max-w-sm mx-auto">{description}</p>
    {action && (
      <div className="mt-6 flex justify-center">
        {action}
      </div>
    )}
  </div>
);

const EmptyChart = ({ title = "No Chart Data", description = "Data will appear here once available.", icon = "📈" }) => (
  <div className="bg-white p-6 rounded-lg border">
    <div className="h-6 bg-gray-100 rounded w-1/3 mb-4"></div>
    <div className="h-64 flex items-center justify-center bg-gray-50 rounded border-2 border-dashed border-gray-200">
      <div className="text-center">
        <div className="text-4xl mb-3">{icon}</div>
        <h4 className="text-lg font-medium text-gray-900 mb-1">{title}</h4>
        <p className="text-gray-500 text-sm">{description}</p>
      </div>
    </div>
    <div className="flex justify-between items-center mt-4">
      <div className="text-sm text-gray-500">No data to display</div>
      <Button variant="outline" size="sm" disabled>Export CSV</Button>
    </div>
  </div>
);

// Review System Components

// NailRatingInput - Controlled input for selecting 1-6 nail rating
const NailRatingInput = ({ value, onChange, disabled = false, className = "" }) => {
  const nailDescriptions = {
    1: "Poor - Major issues, would not recommend",
    2: "Below Average - Several problems, not satisfied", 
    3: "Average - Acceptable but could be better",
    4: "Good - Solid work, mostly satisfied",
    5: "Very Good - High quality, exceeded expectations",
    6: "Exceptional - Outstanding work, highly recommend"
  };

  return (
    <div className={`nail-rating-input ${className}`}>
      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Rating (1-6 Nails)
        </label>
        <p className="text-xs text-gray-500">6 nails = exceptional, 1 nail = poor</p>
      </div>
      
      <div 
        role="radiogroup" 
        aria-labelledby="nail-rating-label"
        className="flex gap-2 p-2 border rounded-lg bg-gray-50"
      >
        {[1, 2, 3, 4, 5, 6].map((rating) => (
          <label
            key={rating}
            className={`
              flex flex-col items-center p-2 rounded-md cursor-pointer transition-all
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white hover:shadow-sm'}
              ${value === rating ? 'bg-orange-100 border border-orange-300 shadow-sm' : 'bg-transparent'}
            `}
          >
            <input
              type="radio"
              name="nail-rating"
              value={rating}
              checked={value === rating}
              onChange={(e) => onChange(parseInt(e.target.value))}
              disabled={disabled}
              className="sr-only"
              aria-describedby={`nail-${rating}-desc`}
            />
            <div className="text-2xl mb-1">🔨</div>
            <span className="text-xs font-medium">{rating}</span>
            <span 
              id={`nail-${rating}-desc`}
              className="sr-only"
            >
              {nailDescriptions[rating]}
            </span>
          </label>
        ))}
      </div>
      
      {value && (
        <div className="mt-2 text-sm text-gray-600 p-2 bg-blue-50 rounded">
          <strong>{value} Nails:</strong> {nailDescriptions[value]}
        </div>
      )}
    </div>
  );
};

// NailRatingDisplay - Read-only display of rating with count
const NailRatingDisplay = ({ 
  rating = 0, 
  count = 0, 
  showCount = true, 
  size = "default",
  className = "" 
}) => {
  const sizes = {
    small: { nail: "text-sm", text: "text-xs" },
    default: { nail: "text-base", text: "text-sm" },
    large: { nail: "text-lg", text: "text-base" }
  };
  
  const displayRating = Math.round(rating * 10) / 10; // Round to 1 decimal
  const fullNails = Math.floor(displayRating);
  const hasHalfNail = displayRating % 1 >= 0.5;
  
  return (
    <div className={`nail-rating-display flex items-center gap-1 ${className}`}>
      <div className="flex items-center">
        {/* Only show nails up to the rating - filled nails */}
        {Array.from({ length: fullNails }, (_, i) => (
          <span key={i} className={`${sizes[size].nail} text-orange-500`}>🔨</span>
        ))}
        
        {/* Half nail if needed */}
        {hasHalfNail && (
          <span className={`${sizes[size].nail} text-orange-300`}>🔨</span>
        )}
      </div>
      
      <span className={`${sizes[size].text} text-gray-600 ml-1`}>
        {displayRating > 0 ? `${displayRating} / 6` : 'No reviews'}
      </span>
      
      {showCount && count > 0 && (
        <span className={`${sizes[size].text} text-gray-500`}>
          ({count} review{count !== 1 ? 's' : ''})
        </span>
      )}
    </div>
  );
};

// ReviewModal - Post-acceptance review modal
const ReviewModal = ({ isOpen, onClose, orderId, onReviewSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const maxChars = 2000;
  const remainingChars = maxChars - reviewText.length;

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const payload = {
        order_id: orderId,
        rating_nails: rating
      };
      
      if (reviewText.trim()) {
        payload.review_text = reviewText.trim();
      }

      await axios.post(`${API}/api/reviews`, payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Review submitted successfully!');
      onReviewSubmitted?.();
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit review';
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose();
    toast.info('You can leave a review later from your orders page');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-bold text-gray-900">Rate Your Experience</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <p className="text-gray-600 mb-6">
            How was your experience with this order? Your feedback helps other buyers and improves our platform.
          </p>

          <div className="mb-6">
            <NailRatingInput
              value={rating}
              onChange={setRating}
              disabled={isSubmitting}
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Review (Optional)
            </label>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              disabled={isSubmitting}
              maxLength={maxChars}
              placeholder="Tell others about your experience..."
              className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-50"
              rows={4}
            />
            <div className="flex justify-between items-center mt-1">
              <span className={`text-xs ${remainingChars < 100 ? 'text-red-500' : 'text-gray-500'}`}>
                {remainingChars} characters remaining
              </span>
              {remainingChars < 0 && (
                <span className="text-xs text-red-500">Too long!</span>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || rating === 0 || remainingChars < 0}
              className="flex-1 bg-orange-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Review'}
            </button>
            <button
              onClick={handleSkip}
              disabled={isSubmitting}
              className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg font-medium hover:bg-gray-300 disabled:opacity-50 transition-colors"
            >
              Skip for Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Review List Components

// Individual Review Item
const ReviewItem = ({ review, onReport }) => {
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [isReporting, setIsReporting] = useState(false);

  const handleReport = async () => {
    setIsReporting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      await axios.post(`${API}/api/reviews/${review.id}/report`, {
        reason: reportReason
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Review reported successfully');
      setShowReportModal(false);
      onReport?.(review.id);
    } catch (error) {
      toast.error('Failed to report review');
    } finally {
      setIsReporting(false);
    }
  };

  return (
    <div className="border-b border-gray-100 pb-4 mb-4 last:border-b-0">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium">
              {review.buyer?.name?.charAt(0)?.toUpperCase() || 'A'}
            </span>
          </div>
          <div>
            <p className="font-medium text-sm">{review.buyer?.name || 'Anonymous'}</p>
            <p className="text-xs text-gray-500">
              {new Date(review.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NailRatingDisplay rating={review.rating_nails} showCount={false} size="small" />
          <button
            onClick={() => setShowReportModal(true)}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            Report
          </button>
        </div>
      </div>

      {review.review_text && (
        <p className="text-gray-700 text-sm mb-3 leading-relaxed">
          {review.review_text}
        </p>
      )}

      {review.reply && (
        <div className="ml-6 mt-3 p-3 bg-gray-50 rounded-lg border-l-3 border-orange-200">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-gray-600">Seller Response</span>
            <span className="text-xs text-gray-500">
              {new Date(review.reply.created_at).toLocaleDateString()}
            </span>
          </div>
          <p className="text-sm text-gray-700">
            {review.reply.reply_text}
          </p>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Report Review</h3>
            <p className="text-gray-600 mb-4">
              Why are you reporting this review?
            </p>
            <textarea
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder="Please explain why this review should be removed..."
              className="w-full p-3 border rounded-lg mb-4"
              rows={3}
            />
            <div className="flex gap-2">
              <button
                onClick={handleReport}
                disabled={isReporting || !reportReason.trim()}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isReporting ? 'Reporting...' : 'Report'}
              </button>
              <button
                onClick={() => setShowReportModal(false)}
                className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Reviews Section Component
const ReviewsSection = ({ sellerId, listingId = null }) => {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);

  const limit = 10;

  const fetchRating = async () => {
    if (!sellerId) return;
    
    try {
      const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${API}/api/sellers/${sellerId}/rating`);
      setRating(response.data);
    } catch (error) {
      console.error('Failed to fetch seller rating:', error);
    }
  };

  const fetchReviews = async (loadMore = false) => {
    try {
      const currentOffset = loadMore ? offset : 0;
      const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const endpoint = listingId 
        ? `${API}/api/listings/${listingId}/reviews`
        : `${API}/api/sellers/${sellerId}/reviews`;
      
      const response = await axios.get(endpoint, {
        params: { limit, offset: currentOffset }
      });

      const newReviews = response.data;
      
      if (loadMore) {
        setReviews(prev => [...prev, ...newReviews]);
      } else {
        setReviews(newReviews);
      }
      
      setHasMore(newReviews.length === limit);
      setOffset(currentOffset + newReviews.length);
      
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (sellerId) {
      fetchRating();
      fetchReviews();
    }
  }, [sellerId, listingId]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    fetchReviews(true);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          {[1, 2, 3].map(i => (
            <div key={i} className="mb-4 p-4 border rounded">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Rating Summary */}
      {rating && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">
                {rating.avg_nails.toFixed(1)}
              </div>
              <div className="text-sm text-gray-500">out of 6</div>
            </div>
            <div className="flex-1">
              <NailRatingDisplay 
                rating={rating.avg_nails} 
                count={rating.review_count}
                size="large"
              />
              {rating.last_review_at && (
                <p className="text-sm text-gray-500 mt-1">
                  Last reviewed: {new Date(rating.last_review_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reviews List */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">
          Reviews ({rating?.review_count || 0})
        </h3>
        
        {reviews.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">📝</div>
            <p>No reviews yet</p>
            <p className="text-sm">Reviews will appear here after completed orders</p>
          </div>
        ) : (
          <>
            {reviews.map(review => (
              <ReviewItem
                key={review.id}
                review={review}
                onReport={(reviewId) => {
                  // Optionally refresh reviews after reporting
                  console.log('Review reported:', reviewId);
                }}
              />
            ))}
            
            {hasMore && (
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="w-full py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {loadingMore ? 'Loading...' : 'Load More Reviews'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Admin Review Management Components

const AdminReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    seller_id: '',
    listing_id: '', 
    order_id: '',
    reported_only: false,
    hidden_only: false,
    start_date: '',
    end_date: ''
  });
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 20,
    total_count: 0,
    has_more: false
  });
  const [selectedReview, setSelectedReview] = useState(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);

  const fetchReviews = async (resetPagination = false) => {
    try {
      setLoading(true);
      const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (filters.seller_id) params.append('seller_id', filters.seller_id);
      if (filters.listing_id) params.append('listing_id', filters.listing_id);
      if (filters.order_id) params.append('order_id', filters.order_id);
      if (filters.reported_only) params.append('reported_only', 'true');
      if (filters.hidden_only) params.append('hidden_only', 'true');
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const currentOffset = resetPagination ? 0 : pagination.offset;
      params.append('limit', pagination.limit.toString());
      params.append('offset', currentOffset.toString());

      const response = await axios.get(`${API}/api/admin/reviews?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (resetPagination) {
        setReviews(response.data.reviews);
        setPagination({
          ...pagination,
          offset: 0,
          total_count: response.data.total_count,
          has_more: response.data.has_more
        });
      } else {
        setReviews(prev => [...prev, ...response.data.reviews]);
        setPagination({
          ...pagination,
          offset: currentOffset + response.data.reviews.length,
          total_count: response.data.total_count,
          has_more: response.data.has_more
        });
      }
    } catch (error) {
      toast.error('Failed to load reviews');
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleHideUnhide = async (reviewId, isHidden) => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const action = isHidden ? 'unhide' : 'hide';
      await axios.put(`${API}/api/admin/reviews/${reviewId}/${action}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success(`Review ${action}d successfully`);
      
      // Update the review in the list
      setReviews(prev => prev.map(review => 
        review.id === reviewId 
          ? { ...review, is_hidden: !isHidden }
          : review
      ));
      
      // Update selected review if it's the same
      if (selectedReview?.id === reviewId) {
        setSelectedReview(prev => ({ ...prev, is_hidden: !isHidden }));
      }
      
    } catch (error) {
      toast.error(`Failed to ${isHidden ? 'unhide' : 'hide'} review`);
    }
  };

  const exportReviews = async () => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (filters.seller_id) params.append('seller_id', filters.seller_id);
      if (filters.listing_id) params.append('listing_id', filters.listing_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axios.get(`${API}/api/admin/reviews/export?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Convert to CSV and download
      const csvContent = [
        Object.keys(response.data.data[0]).join(','),
        ...response.data.data.map(row => Object.values(row).map(value => `"${value}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Reviews exported successfully');
    } catch (error) {
      toast.error('Failed to export reviews');
    }
  };

  useEffect(() => {
    fetchReviews(true);
  }, []);

  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">Review Management</h1>
        
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <Input
                placeholder="Seller ID"
                value={filters.seller_id}
                onChange={(e) => setFilters({...filters, seller_id: e.target.value})}
              />
              <Input
                placeholder="Listing ID"
                value={filters.listing_id}
                onChange={(e) => setFilters({...filters, listing_id: e.target.value})}
              />
              <Input
                placeholder="Order ID"
                value={filters.order_id}
                onChange={(e) => setFilters({...filters, order_id: e.target.value})}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Input
                type="date"
                placeholder="Start Date"
                value={filters.start_date}
                onChange={(e) => setFilters({...filters, start_date: e.target.value})}
              />
              <Input
                type="date"
                placeholder="End Date"
                value={filters.end_date}
                onChange={(e) => setFilters({...filters, end_date: e.target.value})}
              />
            </div>
            
            <div className="flex flex-wrap gap-4 mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.reported_only}
                  onChange={(e) => setFilters({...filters, reported_only: e.target.checked})}
                />
                <span>Reported Only</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.hidden_only}
                  onChange={(e) => setFilters({...filters, hidden_only: e.target.checked})}
                />
                <span>Hidden Only</span>
              </label>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={() => fetchReviews(true)}>Apply Filters</Button>
              <Button variant="outline" onClick={() => {
                setFilters({
                  seller_id: '', listing_id: '', order_id: '',
                  reported_only: false, hidden_only: false,
                  start_date: '', end_date: ''
                });
                fetchReviews(true);
              }}>Clear</Button>
              <Button variant="outline" onClick={exportReviews}>Export CSV</Button>
            </div>
          </CardContent>
        </Card>

        {/* Reviews Table */}
        <Card>
          <CardContent className="pt-6">
            {loading && reviews.length === 0 ? (
              <div className="text-center py-8">Loading...</div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No reviews found</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Rating</th>
                        <th className="text-left p-2">Buyer</th>
                        <th className="text-left p-2">Seller</th>
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Reports</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviews.map(review => (
                        <tr key={review.id} className="border-b hover:bg-gray-50">
                          <td className="p-2">
                            <NailRatingDisplay 
                              rating={review.rating_nails} 
                              showCount={false} 
                              size="small" 
                            />
                          </td>
                          <td className="p-2">{review.buyer?.name}</td>
                          <td className="p-2">{review.seller?.name}</td>
                          <td className="p-2">
                            {new Date(review.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-2">
                            {review.reported_count > 0 && (
                              <Badge variant="destructive">{review.reported_count}</Badge>
                            )}
                          </td>
                          <td className="p-2">
                            <Badge variant={review.is_hidden ? "secondary" : "default"}>
                              {review.is_hidden ? "Hidden" : "Visible"}
                            </Badge>
                          </td>
                          <td className="p-2">
                            <div className="flex gap-1">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setSelectedReview(review);
                                  setShowDetailDrawer(true);
                                }}
                              >
                                View
                              </Button>
                              <Button 
                                variant={review.is_hidden ? "default" : "secondary"}
                                size="sm"
                                onClick={() => handleHideUnhide(review.id, review.is_hidden)}
                              >
                                {review.is_hidden ? "Unhide" : "Hide"}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {pagination.has_more && (
                  <div className="mt-4 text-center">
                    <Button onClick={() => fetchReviews(false)} disabled={loading}>
                      {loading ? 'Loading...' : 'Load More'}
                    </Button>
                  </div>
                )}
                
                <div className="mt-4 text-sm text-gray-500">
                  Showing {reviews.length} of {pagination.total_count} reviews
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Drawer */}
      {showDetailDrawer && selectedReview && (
        <AdminReviewDetailDrawer
          review={selectedReview}
          isOpen={showDetailDrawer}
          onClose={() => {
            setShowDetailDrawer(false);
            setSelectedReview(null);
          }}
          onHideUnhide={handleHideUnhide}
        />
      )}
    </div>
  );
};

const AdminReviewDetailDrawer = ({ review, isOpen, onClose, onHideUnhide }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-end z-50">
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-bold">Review Details</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            {/* Rating */}
            <div>
              <h3 className="font-medium mb-2">Rating</h3>
              <NailRatingDisplay rating={review.rating_nails} showCount={false} />
            </div>

            {/* Review Text */}
            {review.review_text && (
              <div>
                <h3 className="font-medium mb-2">Review Text</h3>
                <p className="text-gray-700 bg-gray-50 p-3 rounded">{review.review_text}</p>
              </div>
            )}

            {/* Reply */}
            {review.reply && (
              <div>
                <h3 className="font-medium mb-2">Seller Reply</h3>
                <p className="text-gray-700 bg-blue-50 p-3 rounded border-l-3 border-blue-200">
                  {review.reply.reply_text}
                </p>
              </div>
            )}

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-medium">Buyer</h4>
                <p>{review.buyer?.name}</p>
                <p className="text-gray-500">{review.buyer?.email}</p>
              </div>
              <div>
                <h4 className="font-medium">Seller</h4>
                <p>{review.seller?.name}</p>
                <p className="text-gray-500">{review.seller?.email}</p>
              </div>
            </div>

            {/* Order Info */}
            <div>
              <h4 className="font-medium">Order</h4>
              <p>ID: {review.order_id}</p>
              <p>Status: {review.order?.status}</p>
            </div>

            {/* Listing Info */}
            <div>
              <h4 className="font-medium">Listing</h4>
              <p>{review.listing?.title}</p>
            </div>

            {/* Status */}
            <div>
              <h4 className="font-medium">Status</h4>
              <div className="flex items-center gap-2">
                <Badge variant={review.is_hidden ? "secondary" : "default"}>
                  {review.is_hidden ? "Hidden" : "Visible"}
                </Badge>
                {review.reported_count > 0 && (
                  <Badge variant="destructive">
                    {review.reported_count} Report{review.reported_count !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
            </div>

            {/* Timestamps */}
            <div className="text-sm text-gray-500">
              <p>Created: {new Date(review.created_at).toLocaleString()}</p>
              <p>Updated: {new Date(review.updated_at).toLocaleString()}</p>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t">
              <Button 
                variant={review.is_hidden ? "default" : "secondary"}
                onClick={() => onHideUnhide(review.id, review.is_hidden)}
                className="flex-1"
              >
                {review.is_hidden ? "Unhide Review" : "Hide Review"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Accessible Button Component with improved focus states
const AccessibleButton = ({ children, variant = "default", size = "default", disabled = false, onClick, className = "", ...props }) => {
  const baseClasses = "inline-flex items-center justify-center rounded-md font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";
  
  const variants = {
    default: "bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800",
    outline: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100",
    destructive: "bg-red-600 text-white hover:bg-red-700 active:bg-red-800"
  };
  
  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    default: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base"
  };
  
  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

// Enhanced Card with better accessibility
const AccessibleCard = ({ children, className = "", ...props }) => (
  <div 
    className={`bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow ${className}`}
    role="region"
    {...props}
  >
    {children}
  </div>
);

// Toast notifications with better accessibility
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Switch } from "./components/ui/switch";
import { Checkbox } from "./components/ui/checkbox";
import { Separator } from "./components/ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "./components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./components/ui/dropdown-menu";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./components/ui/accordion";
import { toast, Toaster } from "sonner";
import { Search, Filter, X, MapPin, Clock, Package, Tag, SlidersHorizontal, MoreVertical, CheckCircle, Loader, Award, AlertTriangle } from "lucide-react";
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  ArcElement,
  Title, 
  Tooltip, 
  Legend 
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dynamic service categories (loaded from API)
let DYNAMIC_CATEGORIES = {};
let CATEGORY_NAMES = {};
let SUBCATEGORY_NAMES = {};

// Legacy hardcoded categories (fallback only)
const LEGACY_SERVICE_CATEGORIES = {
  "lead_generation": ["exclusive_leads", "shared_leads", "pay_per_lead", "call_tracking_roi"],
  "paid_advertising": ["facebook_instagram_ads", "google_ads_ppc", "nextdoor_ads", "youtube_ott", "retargeting"],
  "website": ["website_design_dev", "landing_pages", "hosting_maintenance"],
  "seo": ["local_seo_gbp", "organic_seo", "schema_markup", "backlinking", "content_pages"],
  "directory_management": ["yelp", "bing", "angi", "multi_directory_sync"],
  "reviews_reputation": ["review_gen_email_sms", "review_monitoring_response", "reputation_reporting"],
  "branding": ["logo_brand_identity"],
  "print_marketing": ["door_hangers", "flyers", "business_cards"],
  "wraps_signage": ["vehicle_wraps", "yard_signs", "storefront_signage"],
  "social_media_mgmt": ["facebook", "instagram", "tiktok", "scheduling_automation"],
  "content_writing": ["blog_posts", "location_pages", "service_pages"],
  "video_production": ["testimonials", "before_after", "drone"],
  "photography": ["project_showcase", "team_branding"]
};

// Function to get current service categories (dynamic or fallback)
const getServiceCategories = () => {
  return Object.keys(DYNAMIC_CATEGORIES).length > 0 ? DYNAMIC_CATEGORIES : LEGACY_SERVICE_CATEGORIES;
};

// Function to get category display name (proper name instead of slug)
const getCategoryDisplayName = (slug) => {
  return CATEGORY_NAMES[slug] || formatCategoryName(slug);
};

// Function to get subcategory display name (proper name instead of slug)  
const getSubcategoryDisplayName = (slug) => {
  return SUBCATEGORY_NAMES[slug] || formatSubcategoryName(slug);
};

// Function to load categories from API
const loadDynamicCategories = async () => {
  try {
    const response = await axios.get(`${API}/categories/detailed`);
    DYNAMIC_CATEGORIES = response.data.categories;
    CATEGORY_NAMES = response.data.names;
    SUBCATEGORY_NAMES = response.data.subcategory_names;
    console.log('Loaded dynamic categories with names:', { DYNAMIC_CATEGORIES, CATEGORY_NAMES, SUBCATEGORY_NAMES });
    return DYNAMIC_CATEGORIES;
  } catch (error) {
    console.warn('Failed to load detailed categories, trying fallback endpoint:', error);
    try {
      const fallbackResponse = await axios.get(`${API}/categories`);
      DYNAMIC_CATEGORIES = fallbackResponse.data;
      console.log('Loaded dynamic categories (fallback):', DYNAMIC_CATEGORIES);
      return DYNAMIC_CATEGORIES;
    } catch (fallbackError) {
      console.warn('Both category endpoints failed, using legacy categories:', fallbackError);
      return LEGACY_SERVICE_CATEGORIES;
    }
  }
};

const PRICING_MODELS = ["per_lead", "fixed", "hourly", "retainer"];

// Helper functions
const formatCategoryName = (category) => {
  return category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const formatSubcategoryName = (subcategory) => {
  return subcategory.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const formatPricingModel = (model) => {
  const models = {
    'per_lead': 'Per Lead',
    'fixed': 'Fixed Price',
    'hourly': 'Hourly Rate',
    'retainer': 'Monthly Retainer'
  };
  return models[model] || model;
};

const getPriceDisplay = (listing) => {
  const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;
  
  if (listing.pricing_model === 'per_lead') {
    return `$${Math.round(listing.price_cents / 100)} / lead • G/B/B`;
  } else if (listing.pricing_model === 'hourly') {
    return `$${Math.round(listing.price_cents / 100)} / hr`;
  } else if (listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') {
    if (listing.packages && listing.packages.length > 0) {
      const enabledPackages = listing.packages.filter(pkg => pkg.enabled);
      if (enabledPackages.length > 0) {
        const minPrice = Math.min(...enabledPackages.map(p => p.price_cents));
        return `Starting at $${Math.round(minPrice / 100)}`;
      }
    }
    return `Starting at $${Math.round(listing.price_cents / 100)}`;
  }
  return formatPrice(listing.price_cents);
};

const getTierChips = (listing) => {
  if (listing.pricing_model !== 'fixed' && listing.pricing_model !== 'retainer') {
    return null;
  }
  
  if (!listing.packages || listing.packages.length === 0) {
    return null;
  }

  const enabledPackages = listing.packages.filter(pkg => pkg.enabled);
  if (enabledPackages.length === 0) {
    return null;
  }

  const tierOrder = { 'good': 0, 'better': 1, 'best': 2 };
  const sortedTiers = enabledPackages
    .map(pkg => pkg.tier)
    .sort((a, b) => tierOrder[a] - tierOrder[b]);

  const tierLabels = {
    'good': 'G',
    'better': 'B', 
    'best': 'B'
  };

  return sortedTiers.map(tier => tierLabels[tier]).join(' • ');
};

// 6-Nail Rating Component - Enhanced with better visibility
const NailRating = ({ avgRating, ratingsCount, showText = true, size = "sm" }) => {
  // Scale avg_rating from 0-5 star system to 0-6 nail system
  // Clamp to [0,6] and round to nearest 0.5
  const scaledRating = Math.min(6, Math.max(0, (avgRating / 5) * 6));
  const clampedRating = Math.round(scaledRating * 2) / 2; // Round to nearest 0.5
  
  if (ratingsCount === 0) {
    return showText ? (
      <span className="nail-rating-empty">Not rated yet</span>
    ) : null;
  }

  const nailSize = size === "lg" ? 20 : size === "md" ? 20 : 20; // Consistent 20px size
  
  const renderNail = (index) => {
    const nailValue = index + 1;
    let fillColor = 'transparent';
    let useGradient = false;
    
    if (clampedRating >= nailValue) {
      fillColor = '#F97316'; // --accent-orange
    } else if (clampedRating >= nailValue - 0.5) {
      useGradient = true;
    }
    
    return (
      <svg
        key={index}
        width={nailSize}
        height={nailSize}
        viewBox="0 0 24 24"
        className="inline-block"
        aria-hidden="true"
      >
        {/* Gradient definition for half-filled */}
        <defs>
          <linearGradient id={`nail-half-${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="50%" stopColor="#F97316" />
            <stop offset="50%" stopColor="transparent" />
          </linearGradient>
        </defs>
        
        {/* Nail shaft */}
        <rect
          x="11"
          y="8"
          width="2"
          height="12"
          fill={useGradient ? `url(#nail-half-${index})` : fillColor}
          stroke="#E2E8F0"
          strokeWidth="1.5"
        />
        
        {/* Nail head */}
        <circle
          cx="12"
          cy="6"
          r="3"
          fill={useGradient ? `url(#nail-half-${index})` : fillColor}
          stroke="#E2E8F0"
          strokeWidth="1.5"
        />
      </svg>
    );
  };

  return (
    <div 
      className="nail-rating"
      title={`Rated ${clampedRating} out of 6 nails`}
      aria-label={`Rated ${clampedRating} out of 6 nails`}
    >
      <div className="nail-rating-icons">
        {[0, 1, 2, 3, 4, 5].map(renderNail)}
      </div>
      {showText && (
        <span className="nail-rating-text">
          {clampedRating}/6 • {ratingsCount} reviews
        </span>
      )}
    </div>
  );
};

// Clock Icon Component for SLA
const ClockIcon = ({ hours, className = "" }) => (
  <div className={`sla-clock-icon ${className}`} aria-label={`Delivery SLA ${hours} hours`}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12,6 12,12 16,14"/>
    </svg>
    <span>{hours}h</span>
  </div>
);

// Auth Context
const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load dynamic categories on app start
    loadDynamicCategories();
    
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      setUser(userData);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData, force_password_reset, message } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      // Check if password change is required
      if (force_password_reset) {
        toast.warning(message || 'Password change required before accessing the system.');
        return { success: true, user: userData, force_password_reset: true };
      }
      
      toast.success('Login successful!');
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || 'Login failed';
      toast.error(errorMessage);
      return { success: false, user: null, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      
      // Auto-login sellers after registration
      if (userData.role === 'rnkee') {
        // Automatically log in the seller
        const loginSuccess = await login(userData.email, userData.password);
        if (loginSuccess) {
          toast.success('Registration successful! Please complete your profile.');
          return { success: true, shouldRedirectToProfile: true };
        } else {
          toast.success('Registration successful! Please login to complete your profile.');
          return { success: true, shouldRedirectToProfile: false };
        }
      } else {
        toast.success('Registration successful! Please login.');
        return { success: true, shouldRedirectToProfile: false };
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      return { success: false, shouldRedirectToProfile: false };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success('Logged out successfully');
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null, requiredRoles = null }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    // Store the intended URL in location state for post-login redirection
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check single role requirement
  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to="/" />;
  }
  
  // Check multiple roles requirement
  if (requiredRoles && !requiredRoles.includes(user?.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

// Navigation Component - Mobile Optimized
const Navigation = () => {
  const { user, logout, loading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAdminDropdown, setShowAdminDropdown] = useState(false);

  // Authentication state loaded from context

  return (
    <nav className="nav-professional text-white shadow-lg border-b border-slate-800 relative z-50">
      <div className="max-w-7xl mx-auto px-4 overflow-visible">
        <div className="flex justify-between items-center h-16 overflow-visible">
          <Link 
            to="/" 
            className="text-xl font-bold hover:text-orange-500 transition-colors"
            style={{ color: 'var(--accent-orange)' }}
          >
            Rnkem
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4 xl:space-x-6" style={{ overflow: 'visible' }}>
            <Link to="/browse" className="nav-link font-medium">
              Browse
            </Link>
            
            {user ? (
              <>
                {/* Admin Navigation Links */}
                {(user.role === 'admin' || user.role === 'gm') && (
                  <>
                    <Link to="/admin" className="nav-link bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded-md font-medium">
                      📊 Dashboard
                    </Link>
                    <div className="relative">
                      <button 
                        className="nav-link flex items-center gap-1 hover:text-orange-400"
                        onMouseEnter={() => setShowAdminDropdown(true)}
                        onMouseLeave={() => setShowAdminDropdown(false)}
                      >
                        Admin Tools
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <div 
                        className={`absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-xl border border-gray-200 py-1 transition-all duration-200 ${
                          showAdminDropdown ? 'opacity-100 visible' : 'opacity-0 invisible'
                        }`}
                        style={{
                          zIndex: 999999,
                          position: 'absolute',
                          right: 0,
                          top: '100%',
                          marginTop: '4px'
                        }}
                        onMouseEnter={() => setShowAdminDropdown(true)}
                        onMouseLeave={() => setShowAdminDropdown(false)}
                      >
                        <Link to="/admin/users" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                          👥 User Management
                        </Link>
                        <Link to="/admin/categories" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                          🏷️ Categories
                        </Link>
                        <Link to="/admin/commission" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                          💰 Commission
                        </Link>
                        <Link to="/admin/listings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                          📋 Listings
                        </Link>
                        <Link to="/admin/profiles" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                          👤 Profiles
                        </Link>
                        <Link to="/admin/seller-approvals" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                          ✅ Seller Approvals
                        </Link>
                        <Link to="/admin/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                          🏠 Landing Page Settings
                        </Link>
                        <Link to="/admin/stripe" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                          💳 Stripe Settings
                        </Link>
                        <Link to="/admin/reviews" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                          ⭐ Review Management
                        </Link>
                        <Link to="/admin/disputes" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                          ⚖️ Disputes
                        </Link>
                        <Link to="/admin/points" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                          🏆 Points Management
                        </Link>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Regular User Navigation Links */}
                {user.role === 'rnkee' && (
                  <>
                    <Link to="/sell" className="nav-link">
                      Sell
                    </Link>
                    <Link to="/profile/edit" className="nav-link">
                      Edit Profile
                    </Link>
                  </>
                )}
                {user.role === 'rnker' && (
                  <Link to="/buy/orders" className="nav-link">
                    My Orders
                  </Link>
                )}
                <Link to="/inbox" className="nav-link">
                  Inbox
                </Link>
                <Link to="/settings" className="nav-link">
                  Settings
                </Link>
                
                <div className="flex items-center space-x-2 xl:space-x-3">
                  <span className="text-sm text-gray-300 hidden lg:block">
                    {user.name}
                  </span>
                  <span className="text-xs text-gray-400 hidden xl:block">
                    ({user.role === 'admin' ? 'Admin' : user.role === 'gm' ? 'General Manager' : user.role === 'rnker' ? 'Contractor' : 'Service Provider'})
                  </span>
                  {(user.role === 'admin' || user.role === 'gm') && (
                    <span className="text-xs bg-red-600 text-white px-2 py-1 rounded-full font-medium hidden sm:block">
                      {user.role.toUpperCase()}
                    </span>
                  )}
                  <Button 
                    onClick={logout} 
                    variant="outline" 
                    size="sm"
                    className="border-gray-400 text-white hover:bg-slate-700 hover:text-white"
                  >
                    Logout
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-link">Login</Link>
                <Link to="/register">
                  <Button 
                    className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium"
                    size="sm"
                  >
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            {user && (
              <span className="text-sm text-gray-300 mr-3 truncate max-w-24">
                {user.name.split(' ')[0]}
              </span>
            )}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white hover:bg-slate-700 p-2"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </Button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-700 py-4">
            <div className="flex flex-col space-y-3">
              <Link 
                to="/browse" 
                className="nav-link font-medium block px-2 py-1"
                onClick={() => setMobileMenuOpen(false)}
              >
                Browse
              </Link>
              
              {user ? (
                <>
                  {/* Admin Mobile Menu */}
                  {(user.role === 'admin' || user.role === 'gm') && (
                    <>
                      <div className="px-2 py-2 border-b border-slate-600 mb-2">
                        <div className="text-sm font-semibold text-blue-300 mb-2">Admin Tools</div>
                        <Link 
                          to="/admin" 
                          className="nav-link block px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-md mb-2"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          📊 Dashboard
                        </Link>
                        <Link 
                          to="/admin/users" 
                          className="nav-link block px-3 py-1 text-sm"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          👥 User Management
                        </Link>
                        <Link 
                          to="/admin/categories" 
                          className="nav-link block px-3 py-1 text-sm"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          🏷️ Categories
                        </Link>
                        <Link 
                          to="/admin/commission" 
                          className="nav-link block px-3 py-1 text-sm"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          💰 Commission
                        </Link>
                        <Link 
                          to="/admin/listings" 
                          className="nav-link block px-3 py-1 text-sm"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          📋 Listings
                        </Link>
                        <Link 
                          to="/admin/profiles" 
                          className="nav-link block px-3 py-1 text-sm"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          👤 Profiles
                        </Link>
                        <Link 
                          to="/admin/seller-approvals" 
                          className="nav-link block px-3 py-1 text-sm"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          ✅ Seller Approvals
                        </Link>
                        <Link 
                          to="/admin/landing" 
                          className="nav-link block px-3 py-1 text-sm"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          🏠 Landing Page Settings
                        </Link>
                        <Link 
                          to="/admin/stripe" 
                          className="nav-link block px-3 py-1 text-sm"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          💳 Stripe Settings
                        </Link>
                        <Link 
                          to="/admin/disputes" 
                          className="nav-link block px-3 py-1 text-sm"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          ⚖️ Disputes
                        </Link>
                        <Link 
                          to="/admin/points" 
                          className="nav-link block px-3 py-1 text-sm"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          🏆 Points Management
                        </Link>
                      </div>
                    </>
                  )}
                  
                  {/* Regular User Mobile Menu */}
                  {user.role === 'rnkee' && (
                    <>
                      <Link 
                        to="/sell" 
                        className="nav-link block px-2 py-1"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Sell
                      </Link>
                      <Link 
                        to="/profile/edit" 
                        className="nav-link block px-2 py-1"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Edit Profile
                      </Link>
                    </>
                  )}
                  {user.role === 'rnker' && (
                    <Link 
                      to="/buy/orders" 
                      className="nav-link block px-2 py-1"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      My Orders
                    </Link>
                  )}
                  <Link 
                    to="/inbox" 
                    className="nav-link block px-2 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Inbox
                  </Link>
                  <Link 
                    to="/settings" 
                    className="nav-link block px-2 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Settings
                  </Link>
                  
                  <div className="px-2 py-2 border-t border-slate-700 mt-2">
                    <div className="text-sm text-gray-300 mb-2">
                      {user.name} ({user.role === 'rnker' ? 'Contractor' : 'Service Provider'})
                    </div>
                    <Button 
                      onClick={() => {
                        logout();
                        setMobileMenuOpen(false);
                      }}
                      variant="outline" 
                      size="sm"
                      className="border-gray-400 text-white hover:bg-slate-700 hover:text-white w-full"
                    >
                      Logout
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="nav-link block px-2 py-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link 
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button 
                      className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium w-full mx-2"
                      size="sm"
                    >
                      Sign Up
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Enhanced Skeleton Components with Better Accessibility
const ListingCardSkeleton = () => (
  <Card className="overflow-hidden">
    <CardHeader className="pb-3">
      <div className="animate-pulse" role="status" aria-label="Loading listing data">
        <div className="flex justify-between items-start mb-2">
          <div className="h-5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-3/4 bg-[length:200%_100%] animate-shimmer"></div>
          <div className="h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full w-16 bg-[length:200%_100%] animate-shimmer"></div>
        </div>
        <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-full bg-[length:200%_100%] animate-shimmer"></div>
        <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-2/3 bg-[length:200%_100%] animate-shimmer"></div>
      </div>
    </CardHeader>
    <CardContent>
      <div className="space-y-3">
        <div className="flex justify-between">
          <div className="h-8 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-20 bg-[length:200%_100%] animate-shimmer"></div>
          <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-16 bg-[length:200%_100%] animate-shimmer"></div>
        </div>
        <div className="flex justify-between text-sm">
          <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded w-16 bg-[length:200%_100%] animate-shimmer"></div>
          <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded w-12 bg-[length:200%_100%] animate-shimmer"></div>
        </div>
        <div className="flex gap-1">
          <div className="h-5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full w-12 bg-[length:200%_100%] animate-shimmer"></div>
          <div className="h-5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full w-16 bg-[length:200%_100%] animate-shimmer"></div>
        </div>
        <div className="pt-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded w-20 mb-1 bg-[length:200%_100%] animate-shimmer"></div>
              <div className="h-3 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded w-24 bg-[length:200%_100%] animate-shimmer"></div>
            </div>
            <div className="text-right">
              <div className="h-4 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded w-12 mb-1 bg-[length:200%_100%] animate-shimmer"></div>
              <div className="h-3 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded w-16 bg-[length:200%_100%] animate-shimmer"></div>
            </div>
          </div>
        </div>
        <div className="h-9 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-full bg-[length:200%_100%] animate-shimmer"></div>
      </div>
    </CardContent>
  </Card>
);

// Auth Pages
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    document.title = 'Login | Rnkem';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(email, password);
    
    if (result.success) {
      // Check if password change is required
      if (result.force_password_reset) {
        navigate('/settings?tab=password&force=true', { replace: true });
        return;
      }
      
      // Check if there's an intended URL from the location state
      const from = location.state?.from?.pathname || null;
      
      // Determine redirect destination
      let redirectPath;
      
      if (from) {
        // If user was trying to access a specific page, redirect them there
        // but only if they have permission
        if ((from.startsWith('/admin') && (result.user.role === 'admin' || result.user.role === 'gm')) ||
            (!from.startsWith('/admin'))) {
          redirectPath = from;
        } else {
          // User doesn't have permission for intended page, use default
          redirectPath = result.user.role === 'admin' || result.user.role === 'gm' ? '/admin' : '/browse';
        }
      } else {
        // No intended URL, use role-based default
        redirectPath = result.user.role === 'admin' || result.user.role === 'gm' ? '/admin' : '/browse';
      }
      
      navigate(redirectPath, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Login to Rnkem</CardTitle>
          <CardDescription>Access your contractor marketplace account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">
              Login
            </Button>
          </form>
          
          {/* Forgot Password Link */}
          <div className="mt-4 text-center">
            <Link 
              to="/forgot-password" 
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              Forgot your password?
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    document.title = 'Forgot Password | Rnkem';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/forgot-password`, { email });
      setEmailSent(true);
      toast.success('Password reset instructions sent to your email');
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Check Your Email</CardTitle>
            <CardDescription>
              We've sent password reset instructions to your email address.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center text-green-600">
              <p>If an account exists for {email}, you'll receive an email with instructions to reset your password.</p>
            </div>
            <div className="text-center">
              <Link 
                to="/login" 
                className="text-blue-600 hover:text-blue-800 hover:underline"
              >
                Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Forgot Password</CardTitle>
          <CardDescription>
            Enter your email address and we'll send you instructions to reset your password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-amber-600 hover:bg-amber-700"
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Reset Instructions'}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <Link 
              to="/login" 
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    document.title = 'Reset Password | Rnkem';
    
    // Validate token when component mounts
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/reset-password?token=${token}`);
        setTokenValid(true);
        setUserEmail(response.data.email);
      } catch (error) {
        console.error('Token validation error:', error);
        setTokenValid(false);
      }
    };
    
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/reset-password`, {
        token,
        new_password: password,
        confirm_password: confirmPassword
      });
      
      toast.success('Password reset successful! You can now log in with your new password.');
      navigate('/login');
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4">
        <div className="text-center">Validating reset token...</div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid Reset Link</CardTitle>
            <CardDescription>
              This password reset link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center text-red-600">
              <p>Please request a new password reset link.</p>
            </div>
            <div className="flex gap-2">
              <Link to="/forgot-password" className="flex-1">
                <Button variant="outline" className="w-full">
                  Request New Link
                </Button>
              </Link>
              <Link to="/login" className="flex-1">
                <Button className="w-full bg-amber-600 hover:bg-amber-700">
                  Back to Login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Reset Password</CardTitle>
          <CardDescription>
            Enter your new password for {userEmail}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-auto p-1"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </Button>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Password must be at least 8 characters long
              </p>
            </div>
            
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-auto p-1"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </Button>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-amber-600 hover:bg-amber-700"
              disabled={isLoading}
            >
              {isLoading ? 'Resetting Password...' : 'Reset Password'}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <Link 
              to="/login" 
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company: '',
    phone: '',
    location: '',
    role: 'rnker'
  });
  const { register } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    document.title = 'Sign Up | Rnkem';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await register(formData);
    if (result.success) {
      if (result.shouldRedirectToProfile) {
        // Redirect sellers to profile edit page
        navigate('/profile/edit');
      } else {
        // Redirect buyers to login
        navigate('/login');
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Join Rnkem</CardTitle>
          <CardDescription>Create your contractor marketplace account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="role">I am a...</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rnker">Contractor (Buyer)</SelectItem>
                  <SelectItem value="rnkee">Service Provider (Seller)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
              />
            </div>
            <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700">
              Create Account
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Modern Landing Page Component
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [landingSettings, setLandingSettings] = useState({
    landing_rotation_enabled: true,
    landing_rotate_interval_ms: 2500,
    landing_rotating_phrases: ['bad services again']
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  useEffect(() => {
    document.title = 'Home Services Marketplace | Rnkem';
    fetchLandingSettings();
  }, []);

  const fetchLandingSettings = async () => {
    try {
      // Add cache busting parameter for fresh settings
      const timestamp = Date.now();
      const response = await axios.get(`${API}/public/landing-settings?v=${timestamp}`);
      setLandingSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch landing settings:', error);
      // Keep default fallback settings if fetch fails
    } finally {
      setSettingsLoaded(true);
    }
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-blue-50 pt-20 pb-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Never waste money on
            <span className="block text-blue-600 min-h-[1.2em] flex items-center justify-center">
              {settingsLoaded ? (
                landingSettings.landing_rotation_enabled && landingSettings.landing_rotating_phrases.length > 0 ? (
                  <RotatingPhrase
                    phrases={landingSettings.landing_rotating_phrases}
                    intervalMs={landingSettings.landing_rotate_interval_ms}
                    className="block"
                  />
                ) : (
                  'bad services again'
                )
              ) : (
                // Placeholder to prevent layout shift while loading
                <span className="opacity-0">bad services again</span>
              )}
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-600 mb-8 max-w-4xl mx-auto leading-relaxed">
            A transparent home services marketplace where only top-performing vendors thrive
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              onClick={() => navigate('/register')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-semibold rounded-lg shadow-lg transform hover:scale-105 transition-all"
            >
              Join as Contractor
            </Button>
            <Button 
              onClick={() => navigate('/register')}
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 text-lg font-semibold rounded-lg shadow-lg transform hover:scale-105 transition-all"
            >
              Join as Service Provider
            </Button>
          </div>
          
          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center items-center gap-8 text-slate-500 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-green-600">✅</span>
              <span>Quality Assured</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-600">🔒</span>
              <span>Escrow Protected</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-600">⭐</span>
              <span>Transparent Ratings</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-600">📱</span>
              <span>Web & Mobile Ready</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-white" id="how-it-works">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-4">
            How Rnkem Works
          </h2>
          <p className="text-xl text-slate-600 text-center mb-12 max-w-3xl mx-auto">
            Simple, transparent process that protects both contractors and service providers
          </p>
          
          <div className="grid md:grid-cols-5 gap-8 items-center">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔍</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Browse Vendors</h3>
              <p className="text-sm text-slate-600">Filter by location, pricing, and ratings</p>
            </div>
            
            <div className="hidden md:block text-center">
              <div className="text-blue-400 text-2xl">→</div>
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💰</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Purchase Services</h3>
              <p className="text-sm text-slate-600">Secure payment held in escrow</p>
            </div>
            
            <div className="hidden md:block text-center">
              <div className="text-blue-400 text-2xl">→</div>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📋</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">In-App Delivery</h3>
              <p className="text-sm text-slate-600">Receive services instantly with full details</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mt-8">
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⭐</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Rate & Reorder</h3>
              <p className="text-sm text-slate-600">One-click reorders from trusted vendors</p>
            </div>
            
            <div className="text-center">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔒</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Escrow Release</h3>
              <p className="text-sm text-slate-600">Payments released after quality confirmation</p>
            </div>
            
            <div className="text-center">
              <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔄</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Automated System</h3>
              <p className="text-sm text-slate-600">Dispute resolution and quality assurance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Rating System Explanation */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Transparent Rating System
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Our unique 6-nail rating system ensures you know exactly what you're getting. Every service is rated for quality, accuracy, and performance potential.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    {[1,2,3,4,5,6].map(i => (
                      <span key={i} className="text-orange-400 text-lg">🔨</span>
                    ))}
                  </div>
                  <span className="font-semibold">6/6 Nails - Premium Quality</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    {[1,2,3,4].map(i => (
                      <span key={i} className="text-orange-400 text-lg">🔨</span>
                    ))}
                  </div>
                  <span className="font-semibold">4/6 Nails - Good Quality</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    {[1,2].map(i => (
                      <span key={i} className="text-orange-400 text-lg">🔨</span>
                    ))}
                  </div>
                  <span className="font-semibold">2/6 Nails - Basic Quality</span>
                </div>
              </div>
              
              <p className="text-slate-600 mt-6">
                Only vendors with consistent high ratings get premium placement and access to exclusive contracts.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Quality Metrics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span>Service Quality</span>
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map(i => <span key={i} className="text-orange-400">🔨</span>)}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span>Response Time</span>
                  <div className="flex gap-1">
                    {[1,2,3,4,5,6].map(i => <span key={i} className="text-orange-400">🔨</span>)}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span>Conversion Rate</span>
                  <div className="flex gap-1">
                    {[1,2,3,4].map(i => <span key={i} className="text-orange-400">🔨</span>)}
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Overall Rating</span>
                  <span className="text-lg font-bold text-blue-600">5.0/6 🔨</span>
                </div>
                <p className="text-sm text-slate-500 mt-2">Based on 247 reviews</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Contractors (Rnkers) */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                For Contractors (Rnkers)
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Stop wasting money on bad services. Get quality-assured home services from vetted vendors with full transparency.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">🎯</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Quality-First Services</h3>
                    <p className="text-slate-600">Buy services from vendors with proven track records and transparent ratings</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-green-100 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">🔄</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">One-Click Reorders</h3>
                    <p className="text-slate-600">Found a great vendor? Reorder instantly with your preferred settings</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">🔍</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Market-Specific Filtering</h3>
                    <p className="text-slate-600">Filter by location, roof type, damage type, and budget requirements</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-yellow-100 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">🔒</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Risk-Free with Escrow</h3>
                    <p className="text-slate-600">Your payment is protected until you confirm service quality</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-indigo-100 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">📊</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">All-in-One Platform</h3>
                    <p className="text-slate-600">Manage services, track performance, and export reports from one dashboard</p>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate('/register')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-semibold rounded-lg mt-8"
              >
                Start Buying Quality Services
              </Button>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Success Story</h3>
              <blockquote className="text-slate-700 mb-4 italic">
                "Since joining Rnkem, our service quality has improved by 300%. We only work with 5-6 nail rated vendors now, and our close rate has never been better."
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                  JM
                </div>
                <div>
                  <p className="font-semibold text-slate-900">John Martinez</p>
                  <p className="text-slate-600 text-sm">Martinez Roofing Solutions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Lead Generators (Rnkees) */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="bg-white p-8 rounded-2xl shadow-xl">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Vendor Spotlight</h3>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold">
                    SW
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">StormWatch Pro</p>
                    <div className="flex gap-1">
                      {[1,2,3,4,5,6].map(i => <span key={i} className="text-orange-400">🔨</span>)}
                      <span className="text-sm text-slate-600 ml-2">6.0 • 1,247 sales</span>
                    </div>
                  </div>
                </div>
                <blockquote className="text-slate-700 mb-4 italic">
                  "Rnkem has transformed our business. We've built such a strong reputation that contractors come to us directly now. Premium pricing for premium quality."
                </blockquote>
                <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">$847K</p>
                    <p className="text-sm text-slate-600">Total Earnings</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">98%</p>
                    <p className="text-sm text-slate-600">Satisfaction</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">3.2x</p>
                    <p className="text-sm text-slate-600">Premium Rate</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 md:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                For Service Providers (Rnkees)
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Build your reputation, command premium prices, and grow your business on the most trusted home services marketplace.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-green-100 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">✅</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Pre-Vetted Contractors</h3>
                    <p className="text-slate-600">Sell only to verified contractors who pay on time and value quality</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">🏆</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Build Public Reputation</h3>
                    <p className="text-slate-600">Transparent ratings system showcases your quality to attract premium clients</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">💰</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Command Premium Prices</h3>
                    <p className="text-slate-600">High-rated vendors earn up to 3x more per service than competitors</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">⚡</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Automated Payments</h3>
                    <p className="text-slate-600">Instant payouts when services are approved - no chasing invoices</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="bg-indigo-100 p-2 rounded-lg flex-shrink-0">
                    <span className="text-xl">📈</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Expand Without Marketing</h3>
                    <p className="text-slate-600">Let your reputation bring customers to you - no more cold calling</p>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate('/register')}
                className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 text-lg font-semibold rounded-lg mt-8"
              >
                Start Selling Premium Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Complete Platform Features
          </h2>
          <p className="text-xl text-slate-600 mb-12 max-w-3xl mx-auto">
            Everything you need to buy and sell home services efficiently, available on web and mobile
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💬</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">In-App Messaging</h3>
              <p className="text-sm text-slate-600">Direct communication with vendors</p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔨</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">6-Nail Ratings</h3>
              <p className="text-sm text-slate-600">Transparent quality scoring</p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔄</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Instant Reorders</h3>
              <p className="text-sm text-slate-600">One-click repeat purchases</p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔒</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Escrow Payments</h3>
              <p className="text-sm text-slate-600">Secure transaction protection</p>
            </div>
            
            <div className="text-center">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔍</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Advanced Search</h3>
              <p className="text-sm text-slate-600">Smart filtering and sorting</p>
            </div>
            
            <div className="text-center">
              <div className="bg-cyan-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Exportable Reports</h3>
              <p className="text-sm text-slate-600">Data insights and analytics</p>
            </div>
          </div>
          
          <div className="mt-12 p-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-left">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Available Everywhere</h3>
                <p className="text-slate-600">Access Rnkem on web, iOS, and Android devices</p>
              </div>
              <div className="flex gap-4">
                <div className="bg-white p-3 rounded-lg shadow-md">
                  <span className="text-2xl">💻</span>
                  <p className="text-sm font-semibold mt-1">Web App</p>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-md">
                  <span className="text-2xl">📱</span>
                  <p className="text-sm font-semibold mt-1">iOS App</p>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-md">
                  <span className="text-2xl">📲</span>
                  <p className="text-sm font-semibold mt-1">Android App</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Safety */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Trust & Safety First
          </h2>
          <p className="text-xl text-slate-600 mb-12 max-w-3xl mx-auto">
            Built with enterprise-grade security and automated systems to protect both contractors and service providers
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🛡️</span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Quality Assurance</h3>
              <p className="text-slate-600 mb-4">
                Every vendor is pre-screened and continuously monitored for quality standards
              </p>
              <ul className="text-sm text-slate-600 text-left space-y-2">
                <li>• Background verification</li>
                <li>• Performance tracking</li>
                <li>• Continuous monitoring</li>
                <li>• Quality score updates</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⚖️</span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Dispute Resolution</h3>
              <p className="text-slate-600 mb-4">
                Automated systems and expert mediation resolve issues quickly and fairly
              </p>
              <ul className="text-sm text-slate-600 text-left space-y-2">
                <li>• 24/7 automated support</li>
                <li>• Expert mediation team</li>
                <li>• Fair resolution process</li>
                <li>• Money-back guarantee</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔒</span>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Platform Safety</h3>
              <p className="text-slate-600 mb-4">
                Enterprise-grade security protects your data, payments, and business information
              </p>
              <ul className="text-sm text-slate-600 text-left space-y-2">
                <li>• Encrypted data transmission</li>
                <li>• Secure payment processing</li>
                <li>• Privacy protection</li>
                <li>• Fraud prevention</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Home Services Business?
          </h2>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Join thousands of contractors and service providers who trust Rnkem for quality services
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              onClick={() => navigate('/register')}
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-lg shadow-lg transform hover:scale-105 transition-all"
            >
              Join as Contractor
            </Button>
            <Button 
              onClick={() => navigate('/register')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg font-semibold rounded-lg shadow-lg transform hover:scale-105 transition-all"
            >
              Join as Service Provider
            </Button>
          </div>
          
          {/* Guarantee line removed per user request */}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Rnkem</h3>
              <p className="text-slate-400 text-sm">
                The trusted marketplace for home services, connecting quality contractors with vetted service providers.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">For Contractors</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>Find Quality Services</li>
                <li>Transparent Ratings</li>
                <li>Secure Payments</li>
                <li>One-Click Reorders</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">For Service Providers</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>Sell Premium Services</li>
                <li>Build Reputation</li>
                <li>Automated Payments</li>
                <li>Expand Business</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>Help Center</li>
                <li>Contact Support</li>
                <li>Terms of Service</li>
                <li>Privacy Policy</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-400 text-sm">
            <p>&copy; 2024 Rnkem. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// FAQ Accordion Component
const FAQAccordion = ({ faqs }) => {
  if (!faqs || faqs.length === 0) return null;
  
  return (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-slate-900 mb-4">
        Frequently Asked Questions
      </h3>
      <Accordion type="single" collapsible className="w-full">
        {faqs.map((faq, index) => (
          <AccordionItem key={faq.id || index} value={`item-${index}`}>
            <AccordionTrigger className="text-left">
              {faq.question}
            </AccordionTrigger>
            <AccordionContent className="text-slate-600">
              {faq.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

// Enhanced Browse Page
const BrowsePage = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  // Filter states - simplified to keep only required filters
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState({
    service_category: searchParams.get('service_category') || '',
    service_subcategory: searchParams.get('service_subcategory') || '',
    pricing_model: searchParams.get('pricing_model') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    max_sla: searchParams.get('max_sla') || '',
    platforms: searchParams.get('platforms') || ''
  });
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'best_match');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [totalResults, setTotalResults] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const itemsPerPage = 25;

  const fetchListings = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        skip: (page - 1) * itemsPerPage,
        limit: itemsPerPage,
        page: page,
        _v: Date.now() // Cache buster
      };

      // Add search
      if (searchTerm) params.keyword = searchTerm;
      
      // Add simplified filters only
      if (filters.service_category) params.service_category = filters.service_category;
      if (filters.service_subcategory) params.service_subcategory = filters.service_subcategory;
      if (filters.pricing_model) params.pricing_model = filters.pricing_model;
      if (filters.min_price) params.min_price = parseInt(filters.min_price);
      if (filters.max_price) params.max_price = parseInt(filters.max_price);
      if (filters.max_sla) params.max_sla = parseInt(filters.max_sla);
      if (filters.platforms) params.platforms = filters.platforms;
      
      // Add sort parameter - map frontend sort values to backend values
      if (sortBy === 'best_match' || sortBy === 'rank') {
        params.sort = 'rank';
      } else if (sortBy === 'price_low') {
        params.sort = 'price';
      } else if (sortBy === 'price_high') {
        params.sort = 'price';
        // We'll reverse this client-side since backend only does ascending price sort
      } else if (sortBy === 'newest') {
        params.sort = 'newest';
      } else if (sortBy === 'sla_fastest') {
        // No backend support, will sort client-side
        params.sort = 'rank';
      }

      const response = await axios.get(`${API}/listings`, { params });
      const data = response.data;
      
      // Handle new response format with sponsored listings
      let results = [];
      let sponsoredTop = [];
      let sponsoredMid = [];
      
      if (data.organic !== undefined) {
        // New format with sponsored listings
        results = data.organic || [];
        sponsoredTop = data.sponsored_top || [];
        sponsoredMid = data.sponsored_mid || [];
        
        // Insert sponsored listings into results for display
        // Top 3 sponsored listings go at the beginning
        const displayListings = [...sponsoredTop];
        
        // Insert organic listings with mid sponsored after position 5
        const midInsertAfter = data.insertion?.mid_insert_after || 5;
        results.forEach((listing, index) => {
          displayListings.push(listing);
          if (index === midInsertAfter - 1 && sponsoredMid.length > 0) {
            displayListings.push(...sponsoredMid);
          }
        });
        
        results = displayListings;
      } else {
        // Backward compatibility - old format
        results = data;
      }

      // Apply client-side sorting only for cases backend doesn't handle
      if (sortBy === 'price_high') {
        // Backend does price ascending, reverse for descending
        // Only sort non-sponsored listings
        const sponsored = results.filter(l => l.sponsored);
        const organic = results.filter(l => !l.sponsored);
        organic.reverse();
        results = [...sponsored.slice(0, 3), ...organic]; // Keep top sponsored at top
      } else if (sortBy === 'sla_fastest') {
        // Backend doesn't have SLA sort, do it client-side
        const sponsored = results.filter(l => l.sponsored);
        const organic = results.filter(l => !l.sponsored);
        organic.sort((a, b) => a.delivery_sla_hours - b.delivery_sla_hours);
        results = [...sponsored.slice(0, 3), ...organic];
      }
      // For 'best_match'/'rank', 'price_low', 'newest' - use backend sort as-is

      setListings(results);
      setTotalResults(results.length); // In a real app, this would come from the API
    } catch (error) {
      toast.error('Failed to fetch listings');
    } finally {
      setLoading(false);
    }
  };

  // Update URL params - simplified
  const updateSearchParams = () => {
    const newParams = new URLSearchParams();
    if (searchTerm) newParams.set('search', searchTerm);
    if (filters.service_category) newParams.set('service_category', filters.service_category);
    if (filters.service_subcategory) newParams.set('service_subcategory', filters.service_subcategory);
    if (filters.pricing_model) newParams.set('pricing_model', filters.pricing_model);
    if (filters.min_price) newParams.set('min_price', filters.min_price);
    if (filters.max_price) newParams.set('max_price', filters.max_price);
    if (filters.max_sla) newParams.set('max_sla', filters.max_sla);
    if (filters.platforms) newParams.set('platforms', filters.platforms);
    if (sortBy !== 'best_match') newParams.set('sort', sortBy);
    if (currentPage > 1) newParams.set('page', currentPage.toString());
    
    setSearchParams(newParams);
  };

  useEffect(() => {
    fetchListings(currentPage);
  }, [currentPage]);

  useEffect(() => {
    updateSearchParams();
  }, [searchTerm, filters, sortBy, currentPage]);

  useEffect(() => {
    document.title = 'Browse Services | Rnkem';
    // Ensure categories are loaded
    if (Object.keys(DYNAMIC_CATEGORIES).length === 0) {
      loadDynamicCategories().then(() => setCategoriesLoaded(true));
    } else {
      setCategoriesLoaded(true);
    }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchListings(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      service_category: '',
      service_subcategory: '',
      pricing_model: '',
      min_price: '',
      max_price: '',
      max_sla: '',
      platforms: ''
    });
    setSortBy('best_match');
    setCurrentPage(1);
    setSearchParams(new URLSearchParams());
    fetchListings(1);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchListings(1);
    setMobileFiltersOpen(false);
  };

  const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;

  const renderListingCard = (listing) => {
    // Handle sponsored listing clicks
    const handleSponsoredClick = async (e) => {
      if (listing.sponsored) {
        // Record impression when listing is viewed
        try {
          await axios.post(`${API}/ads/imp`, {
            campaign_id: listing.campaign_id,
            listing_id: listing.id,
            placement: listing.placement
          });
        } catch (error) {
          console.error('Failed to record impression:', error);
        }
      }
    };

    const handleCardClick = async () => {
      if (listing.sponsored) {
        // Record click
        try {
          await axios.post(`${API}/ads/click`, {
            campaign_id: listing.campaign_id,
            listing_id: listing.id,
            placement: listing.placement
          });
        } catch (error) {
          console.error('Failed to record click:', error);
        }
      }
    };

    return (
      <Card key={listing.id} className="card-modern group" onMouseEnter={handleSponsoredClick}>
        <CardHeader className="pb-4">
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1 pr-3">
              {/* Sponsored badge */}
              {listing.sponsored && (
                <Badge 
                  className="mb-2 font-semibold text-xs"
                  style={{ 
                    backgroundColor: '#FEF3C7',
                    color: '#92400E',
                    border: '1px solid #FCD34D'
                  }}
                  aria-label="Sponsored"
                >
                  Sponsored
                </Badge>
              )}
              <Link 
                to={`/listing/${listing.id}`}
                className="font-semibold text-lg hover:text-orange-600 transition-colors line-clamp-2 leading-tight block"
                style={{ color: 'var(--brand-navy)' }}
                aria-label={`View listing: ${listing.title}`}
                onClick={handleCardClick}
              >
                {listing.title}
              </Link>
            </div>
            {/* Single primary badge with trades colors */}
            {listing.service_category === 'lead_generation' ? (
              <Badge 
                className={listing.exclusivity === 'exclusive' 
                  ? 'badge-exclusive shrink-0 font-medium' 
                  : 'badge-shared shrink-0 font-medium'
                }
              >
                {listing.exclusivity}
              </Badge>
            ) : (
              <Badge 
                variant="outline" 
                className="text-sm shrink-0 font-medium border-2"
                style={{ 
                  backgroundColor: '#F8FAFC',
                  color: 'var(--brand-navy)',
                  borderColor: 'var(--border-strong)'
                }}
              >
                {formatCategoryName(listing.service_category)}
              </Badge>
            )}
          </div>
        
        {/* Service type and pricing model in subtitle */}
        <div className="text-sm mb-2" style={{ color: 'var(--steel-500)' }}>
          {formatCategoryName(listing.service_category)} • {formatPricingModel(listing.pricing_model)}
        </div>
        
        <CardDescription className="text-sm line-clamp-2 leading-relaxed" style={{ color: 'var(--steel-500)' }}>
          {listing.description}
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          {/* Price and key info with clock icon */}
          <div className="flex justify-between items-center">
            <div className="flex flex-col gap-1">
              <span className="text-2xl font-bold" style={{ color: 'var(--accent-orange)' }}>
                {getPriceDisplay(listing)}
              </span>
              {/* Optional tier chips */}
              {getTierChips(listing) && (
                <div className="text-xs font-medium text-gray-500">
                  {getTierChips(listing)}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-1">
              {listing.pricing_model === 'per_lead' && (
                <div className="text-sm" style={{ color: 'var(--steel-500)' }}>
                  Min: {listing.min_qty}
                </div>
              )}
              <ClockIcon hours={listing.delivery_sla_hours} />
            </div>
          </div>
          
          {/* Highlights - only show top 2 */}
          {listing.highlights && listing.highlights.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {listing.highlights.slice(0, 2).map((highlight, index) => (
                <Badge 
                  key={index} 
                  variant="outline" 
                  className="text-xs px-2 py-1 border"
                  style={{ 
                    color: 'var(--steel-500)',
                    borderColor: 'var(--border-strong)',
                    backgroundColor: '#F8FAFC'
                  }}
                >
                  {highlight}
                </Badge>
              ))}
              {listing.highlights.length > 2 && (
                <Badge 
                  variant="outline" 
                  className="text-xs px-2 py-1"
                  style={{ color: 'var(--steel-500)' }}
                >
                  +{listing.highlights.length - 2} more
                </Badge>
              )}
            </div>
          )}
          
          {/* Seller info - simplified with better nail rating placement */}
          {listing.seller && (
            <div className="pt-3 border-t" style={{ borderColor: 'var(--border-strong)' }}>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div>
                    <Link 
                      to={`/seller/${listing.seller.id}`}
                      className="font-medium text-sm hover:text-orange-600 transition-colors"
                      style={{ color: 'var(--brand-navy)' }}
                    >
                      {listing.seller.name}
                    </Link>
                    <p className="text-xs" style={{ color: 'var(--steel-500)' }}>
                      {listing.seller.company}
                    </p>
                  </div>
                  <PointsBadge 
                    points={listing.seller_profile?.points_total || 0}
                    size="sm"
                  />
                </div>
                {/* 6-nail rating on its own line for better visibility */}
                {listing.seller_profile && (
                  <div className="flex justify-end">
                    <NailRatingDisplay 
                      rating={listing.seller_profile.avg_rating || 0}
                      count={listing.seller_profile.ratings_count || 0}
                      showCount={true}
                      size="small"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Action buttons with improved mobile responsiveness */}
          <div className="flex gap-2 pt-3">
            <Link to={`/listing/${listing.id}`} className="flex-1">
              <Button 
                variant="outline" 
                className="w-full text-sm sm:text-base py-2.5 sm:py-2 border-gray-300 hover:border-gray-400"
              >
                View Details
              </Button>
            </Link>
            
            {user && user.role === 'rnker' && listing.rnkee_id !== user.id ? (
              <Link to={`/listing/${listing.id}`} className="flex-1">
                <Button 
                  className="w-full text-sm sm:text-base py-2.5 sm:py-2 btn-primary font-semibold"
                  style={{ 
                    backgroundColor: 'var(--accent-orange)',
                    borderColor: 'var(--accent-orange)'
                  }}
                >
                  Buy Now
                </Button>
              </Link>
            ) : (
              <Button 
                disabled 
                className="flex-1 text-sm sm:text-base py-2.5 sm:py-2 opacity-50" 
                title={!user ? "Sign in as Contractor to buy" : "Cannot buy your own listing"}
              >
                {!user ? "Sign in to buy" : "Buy Now"}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
    );
  };

  const FilterSidebar = ({ isMobile = false }) => (
    <div className="filter-sidebar">
      {/* Service Category & Subcategory */}
      <div className="filter-section">
        <h3>Service Category</h3>
        <Select 
          value={filters.service_category} 
          onValueChange={(value) => {
            setFilters({
              ...filters, 
              service_category: value === 'all' ? '' : value,
              service_subcategory: '' // Reset subcategory when category changes
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {Object.keys(getServiceCategories()).map(category => (
              <SelectItem key={category} value={category}>
                {getCategoryDisplayName(category)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filters.service_category && (
        <div className="filter-section">
          <h3>Subcategory</h3>
          <Select 
            value={filters.service_subcategory} 
            onValueChange={(value) => setFilters({...filters, service_subcategory: value === 'all' ? '' : value})}
          >
            <SelectTrigger>
              <SelectValue placeholder="All subcategories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All subcategories</SelectItem>
              {getServiceCategories()[filters.service_category]?.map(subcategory => (
                <SelectItem key={subcategory} value={subcategory}>
                  {getSubcategoryDisplayName(subcategory)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Pricing Model */}
      <div className="filter-section">
        <h3>Pricing Model</h3>
        <Select 
          value={filters.pricing_model} 
          onValueChange={(value) => setFilters({...filters, pricing_model: value === 'all' ? '' : value})}
        >
          <SelectTrigger>
            <SelectValue placeholder="All pricing models" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All pricing models</SelectItem>
            {PRICING_MODELS.map(model => (
              <SelectItem key={model} value={model}>
                {formatPricingModel(model)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Price Range */}
      <div className="filter-section">
        <h3>Price Range</h3>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor="min_price" className="text-xs" style={{ color: 'var(--steel-500)' }}>
              Min (cents)
            </Label>
            <Input
              id="min_price"
              type="number"
              placeholder="Min"
              value={filters.min_price}
              onChange={(e) => setFilters({...filters, min_price: e.target.value})}
              className="text-sm"
            />
          </div>
          <div>
            <Label htmlFor="max_price" className="text-xs" style={{ color: 'var(--steel-500)' }}>
              Max (cents)
            </Label>
            <Input
              id="max_price"
              type="number"
              placeholder="Max"
              value={filters.max_price}
              onChange={(e) => setFilters({...filters, max_price: e.target.value})}
              className="text-sm"
            />
          </div>
        </div>
      </div>

      {/* Max Delivery SLA */}
      <div className="filter-section">
        <h3>Max Delivery SLA (≤ hours)</h3>
        <Input
          id="max_sla"
          type="number"
          placeholder="Max hours"
          value={filters.max_sla}
          onChange={(e) => setFilters({...filters, max_sla: e.target.value})}
        />
      </div>

      {/* Platforms */}
      <div className="filter-section">
        <h3>Platforms</h3>
        <Input
          id="platforms"
          placeholder="facebook, instagram, google..."
          value={filters.platforms}
          onChange={(e) => setFilters({...filters, platforms: e.target.value})}
        />
      </div>

      <div className="space-y-2">
        <Button 
          onClick={applyFilters} 
          className="w-full btn-primary font-semibold"
          style={{ backgroundColor: 'var(--accent-orange)' }}
        >
          Apply Filters
        </Button>
        
        <button
          onClick={clearFilters}
          className="w-full text-sm font-medium underline hover:no-underline transition-all"
          style={{ color: 'var(--steel-500)' }}
        >
          Clear filters
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Top Toolbar */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 max-w-lg">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search listings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-10"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </form>

            {/* Sort & Mobile Filters */}
            <div className="flex items-center gap-3">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="best_match">Best Match</SelectItem>
                  <SelectItem value="price_low">Price: Low → High</SelectItem>
                  <SelectItem value="price_high">Price: High → Low</SelectItem>
                  <SelectItem value="sla_fastest">SLA: Fastest</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                </SelectContent>
              </Select>

              {/* Mobile Filter Button */}
              <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden">
                    <SlidersHorizontal className="w-4 h-4 mr-2" />
                    Filters
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80">
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                    <SheetDescription>
                      Narrow down your search results
                    </SheetDescription>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterSidebar isMobile={true} />
                  </div>
                </SheetContent>
              </Sheet>

              <Button variant="ghost" onClick={clearFilters} className="text-sm text-slate-600 hover:text-slate-900">
                Clear filters
              </Button>
            </div>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Left Sidebar - Desktop Only */}
          <div className="hidden lg:block w-64 shrink-0">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FilterSidebar />
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Results Info */}
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-slate-600">
                {loading ? 'Loading...' : `${totalResults} listings found`}
              </p>
            </div>

            {/* Listings Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <ListingCardSkeleton key={i} />
                ))}
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-12">
                <div className="max-w-md mx-auto">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No listings found</h3>
                  <p className="text-slate-600 mb-4">
                    No listings match your current filters. Try adjusting your search criteria.
                  </p>
                  <Button onClick={clearFilters} variant="outline">
                    Clear Filters
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {listings.map(renderListingCard)}
              </div>
            )}

            {/* Pagination */}
            {totalResults > itemsPerPage && (
              <div className="flex justify-center mt-8">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="px-4 py-2 text-sm text-slate-600">
                    Page {currentPage} of {Math.ceil(totalResults / itemsPerPage)}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(Math.min(Math.ceil(totalResults / itemsPerPage), currentPage + 1))}
                    disabled={currentPage >= Math.ceil(totalResults / itemsPerPage)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Listing Detail Page
const ListingDetailPage = () => {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [buyerRequirements, setBuyerRequirements] = useState({});
  const [buyerNotes, setBuyerNotes] = useState('');
  const [buyerCommissionRate, setBuyerCommissionRate] = useState(5); // Default 5%
  const [isVipBuyer, setIsVipBuyer] = useState(false);
  const [stripeMode, setStripeMode] = useState('test');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchListing();
  }, [id]);

  useEffect(() => {
    if (user && user.role === 'rnker') {
      fetchBuyerCommissionInfo();
    }
  }, [user]);
  
  useEffect(() => {
    if (listing) {
      document.title = `${listing.title} | Rnkem`;
      // Initialize with first enabled package for fixed/retainer pricing
      if ((listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') && listing.packages?.length > 0) {
        const enabledPackages = listing.packages.filter(pkg => pkg.enabled);
        if (enabledPackages.length > 0) {
          setSelectedPackage(enabledPackages[0]);
        }
      }
      // Initialize buyer requirements
      if (listing.buyer_requirements?.length > 0) {
        const initialRequirements = {};
        listing.buyer_requirements.forEach((req, index) => {
          initialRequirements[index] = '';
        });
        setBuyerRequirements(initialRequirements);
      }
    }
  }, [listing]);

  // Initialize quantity based on package selection and pricing model
  useEffect(() => {
    if (listing && buyModalOpen) {
      if (listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') {
        setQty(1); // Always 1 for package-based pricing
      } else if (listing.pricing_model === 'per_lead' && selectedPackage?.bundle_qty) {
        setQty(selectedPackage.bundle_qty); // Prefill with bundle quantity
      } else {
        setQty(listing.min_qty || 1);
      }
    }
  }, [listing, selectedPackage, buyModalOpen]);

  const fetchListing = async () => {
    try {
      const [listingResponse, faqResponse] = await Promise.all([
        axios.get(`${API}/listings/${id}`),
        axios.get(`${API}/listings/${id}/faqs`)
      ]);
      
      setListing(listingResponse.data);
      setFaqs(faqResponse.data || []);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Listing not found');
      } else {
        toast.error('Failed to load listing');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBuyerCommissionInfo = async () => {
    try {
      // Get buyer commission rate from platform settings
      const commissionResponse = await axios.get(`${API}/admin/platform-settings/buyer_commission_percent`);
      setBuyerCommissionRate(parseFloat(commissionResponse.data.value || 5));

      // Check if buyer is VIP
      const profileResponse = await axios.get(`${API}/profiles/buyer/me`);
      setIsVipBuyer(profileResponse.data?.is_vip_no_commission || false);

      // Get Stripe mode
      const stripeResponse = await axios.get(`${API}/admin/platform-settings/stripe_mode`);
      setStripeMode(stripeResponse.data.value || 'test');
    } catch (error) {
      console.error('Error fetching buyer commission info:', error);
      // Use defaults
    }
  };

  const calculateBuyerFee = (subtotalCents) => {
    if (isVipBuyer) return 0;
    return Math.round(subtotalCents * (buyerCommissionRate / 100));
  };

  const getSubtotal = () => {
    if ((listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') && selectedPackage) {
      return selectedPackage.price_cents;
    }
    return qty * listing.price_cents;
  };

  const getBuyerFee = () => {
    return calculateBuyerFee(getSubtotal());
  };

  const getTotalWithFees = () => {
    return getSubtotal() + getBuyerFee();
  };

  const handleBuy = async () => {
    try {
      // Validate package selection for fixed/retainer pricing
      if ((listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') && !selectedPackage) {
        toast.error('Please select a package first');
        return;
      }

      // Validate requirements
      const errors = [];
      
      if (listing.buyer_requirements?.length > 0) {
        listing.buyer_requirements.forEach((req, index) => {
          if (!buyerRequirements[index]?.trim()) {
            errors.push(`Please answer: ${req}`);
          }
        });
      }

      if (errors.length > 0) {
        toast.error(`Missing required information: ${errors.slice(0, 2).join(', ')}${errors.length > 2 ? '...' : ''}`);
        return;
      }

      // Calculate pricing based on model
      let unit_price_cents, price_cents_total, final_qty;

      if (listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') {
        // Fixed/Retainer: package price, quantity always 1
        unit_price_cents = selectedPackage.price_cents;
        final_qty = 1;
        price_cents_total = selectedPackage.price_cents;
      } else if (listing.pricing_model === 'per_lead') {
        // Per Lead: listing price per lead, quantity editable (prefilled with bundle_qty)
        unit_price_cents = listing.price_cents;
        final_qty = qty;
        price_cents_total = qty * listing.price_cents;
      } else if (listing.pricing_model === 'hourly') {
        // Hourly: listing price per hour, quantity = hours
        unit_price_cents = listing.price_cents;
        final_qty = qty;
        price_cents_total = qty * listing.price_cents;
      }

      const orderData = {
        listing_id: id,
        qty: final_qty,
        buyer_notes: buyerNotes,
        buyer_requirements: buyerRequirements,
        unit_price_cents: unit_price_cents,
        price_cents_total: price_cents_total
      };

      // Add package snapshot fields
      if ((listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') && selectedPackage) {
        orderData.package_tier = selectedPackage.tier;
        orderData.package_name = selectedPackage.display_name;
        orderData.delivery_days = selectedPackage.delivery_days;
      } else if (listing.pricing_model === 'per_lead') {
        orderData.package_tier = selectedPackage?.tier || 'standard';
        orderData.package_name = selectedPackage?.display_name || 'Standard Package';
        orderData.delivery_days = selectedPackage?.delivery_days || Math.round((listing.delivery_sla_hours || 72) / 24);
      } else if (listing.pricing_model === 'hourly') {
        orderData.package_tier = 'hourly';
        orderData.package_name = 'Hourly Service';
        orderData.delivery_days = Math.round((listing.delivery_sla_hours || 72) / 24);
      }

      // Create Stripe checkout session instead of direct order
      const checkoutData = {
        listing_id: id,
        qty: final_qty,
        package_name: orderData.package_name,
        package_tier: orderData.package_tier,
        unit_price_cents: unit_price_cents,
        delivery_days: orderData.delivery_days,
        requirements_text: buyerRequirements.join('\n')
      };

      const response = await axios.post(`${API}/stripe/checkout/create-session`, checkoutData);
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    }
  };

  const getCurrentPrice = () => {
    if ((listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') && selectedPackage) {
      return selectedPackage.price_cents;
    }
    return listing.price_cents;
  };

  const getTotalPrice = () => {
    if ((listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') && selectedPackage) {
      // For fixed/retainer, quantity should always be 1 for package pricing
      return selectedPackage.price_cents;
    }
    return qty * listing.price_cents;
  };

  const getMinQty = () => {
    if (listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') {
      return 1; // Always 1 for package-based pricing
    }
    if (listing.pricing_model === 'per_lead' && selectedPackage?.bundle_qty) {
      return selectedPackage.bundle_qty; // Prefill with bundle quantity
    }
    return listing.min_qty || 1;
  };

  const getMaxQty = () => {
    // Set reasonable max based on pricing model
    if (listing.pricing_model === 'per_lead') {
      return 1000; // Allow up to 1000 leads
    }
    return 50; // For other types, limit to 50
  };

  // Handle contact seller
  const handleContactSeller = async () => {
    if (!user) {
      // Redirect to login with return URL
      navigate(`/login?next=/listing/${id}#contact`);
      return;
    }
    
    if (user.role !== 'rnker') {
      toast.error('Switch to Buyer role to contact sellers');
      return;
    }
    
    if (user.id === listing.rnkee_id) {
      // This should be hidden in UI, but just in case
      return;
    }
    
    try {
      // Create or find existing conversation
      const response = await axios.post(`${API}/conversations`, {
        seller_id: listing.rnkee_id,
        listing_id: listing.id,
        initial_message: "Hi, I'm interested in your services. Can you provide more details?"
      });
      
      const conversationId = response.data.id;
      navigate(`/inbox/conversation/${conversationId}`);
    } catch (error) {
      // If conversation already exists, backend should return it
      if (error.response?.data?.conversation_id) {
        navigate(`/inbox/conversation/${error.response.data.conversation_id}`);
      } else {
        console.error('Failed to create conversation:', error);
        toast.error('Failed to start conversation');
      }
    }
  };

  const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;

  // Package Comparison Table Component (moved inside to access formatPrice)
  const PackageComparisonTable = ({ packages, selectedPackage, onPackageSelect }) => {
    const [showComparison, setShowComparison] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const checkMobile = () => setIsMobile(window.innerWidth < 768);
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Build unique union of all deliverables (case-insensitive)
    const getAllDeliverables = () => {
      const allDeliverables = new Set();
      packages.forEach(pkg => {
        if (pkg.deliverables) {
          pkg.deliverables.forEach(deliverable => {
            allDeliverables.add(deliverable.trim().toLowerCase());
          });
        }
      });
      return Array.from(allDeliverables);
    };

    // Check if deliverable exists in package (case-insensitive)
    const hasDeliverable = (pkg, deliverable) => {
      if (!pkg.deliverables) return false;
      return pkg.deliverables.some(d => 
        d.trim().toLowerCase() === deliverable.toLowerCase()
      );
    };

    // Get original case deliverable from package
    const getOriginalDeliverable = (deliverable) => {
      for (const pkg of packages) {
        if (pkg.deliverables) {
          const found = pkg.deliverables.find(d => 
            d.trim().toLowerCase() === deliverable.toLowerCase()
          );
          if (found) return found;
        }
      }
      return deliverable;
    };

    const allDeliverables = getAllDeliverables();

    // Mobile view - collapsible per tier
    if (isMobile) {
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Compare Packages</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowComparison(!showComparison)}
              className="text-xs"
            >
              {showComparison ? 'Hide' : 'Show'} Comparison
            </Button>
          </div>

          {showComparison && (
            <div className="space-y-4">
              {packages.map((pkg, index) => (
                <Card key={pkg.tier} className={`${
                  pkg.tier === 'best' ? 'border-amber-500 shadow-lg relative' : 'border-gray-200'
                } ${selectedPackage?.tier === pkg.tier ? 'ring-2 ring-amber-300' : ''}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="text-center">
                      <h4 className="font-semibold text-lg text-gray-900">{pkg.display_name}</h4>
                      <p className="text-sm text-gray-600 mt-1">{pkg.short_description}</p>
                      <div className="text-2xl font-bold text-amber-600 mt-2">
                        {formatPrice(pkg.price_cents)}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 py-3 border-y border-gray-200">
                      <div className="text-center">
                        <div className="font-semibold">{pkg.delivery_days}</div>
                        <div className="text-xs text-gray-600">Days</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold">{pkg.revisions}</div>
                        <div className="text-xs text-gray-600">Revisions</div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h5 className="font-medium text-sm text-gray-900">Features:</h5>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {allDeliverables.map((deliverable) => (
                          <div key={deliverable} className="flex items-center gap-2 text-sm">
                            <span className={`w-4 h-4 flex items-center justify-center ${
                              hasDeliverable(pkg, deliverable)
                                ? 'text-green-600'
                                : 'text-gray-300'
                            }`}>
                              {hasDeliverable(pkg, deliverable) ? '✓' : '—'}
                            </span>
                            <span className={hasDeliverable(pkg, deliverable) ? 'text-gray-900' : 'text-gray-400'}>
                              {getOriginalDeliverable(deliverable)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button
                      onClick={() => onPackageSelect(pkg)}
                      className={`w-full ${
                        selectedPackage?.tier === pkg.tier
                          ? 'bg-amber-600 hover:bg-amber-700'
                          : 'bg-gray-600 hover:bg-gray-700'
                      }`}
                    >
                      {selectedPackage?.tier === pkg.tier ? 'Selected' : 'Select'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Desktop view - comparison table
    return (
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900">Compare Packages</h3>
        
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="w-full">
            {/* Sticky Header */}
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 w-1/4">
                  Features
                </th>
                {packages.map((pkg) => (
                  <th key={pkg.tier} className={`px-6 py-4 text-center text-sm font-medium relative ${
                    pkg.tier === 'best' ? 'bg-amber-50 border-b-2 border-amber-500' : 'text-gray-500'
                  }`}>
                    <div className="space-y-1 mt-2">
                      <div className="font-semibold text-gray-900">{pkg.display_name}</div>
                      <div className="text-xs text-gray-600">{pkg.short_description}</div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {/* Price Row */}
              <tr className="bg-white">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  Price
                </td>
                {packages.map((pkg) => (
                  <td key={pkg.tier} className={`px-6 py-4 text-center ${
                    pkg.tier === 'best' ? 'bg-amber-50' : ''
                  }`}>
                    <div className="text-lg font-bold text-amber-600">
                      {formatPrice(pkg.price_cents)}
                    </div>
                  </td>
                ))}
              </tr>

              {/* Delivery Time Row */}
              <tr className="bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  Delivery Time
                </td>
                {packages.map((pkg) => (
                  <td key={pkg.tier} className={`px-6 py-4 text-center ${
                    pkg.tier === 'best' ? 'bg-amber-50' : ''
                  }`}>
                    <div className="text-sm font-medium">
                      {pkg.delivery_days} day{pkg.delivery_days !== 1 ? 's' : ''}
                    </div>
                  </td>
                ))}
              </tr>

              {/* Revisions Row */}
              <tr className="bg-white">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  Revisions
                </td>
                {packages.map((pkg) => (
                  <td key={pkg.tier} className={`px-6 py-4 text-center ${
                    pkg.tier === 'best' ? 'bg-amber-50' : ''
                  }`}>
                    <div className="text-sm font-medium">
                      {pkg.revisions} revision{pkg.revisions !== 1 ? 's' : ''}
                    </div>
                  </td>
                ))}
              </tr>

              {/* Dynamic Deliverables Rows */}
              {allDeliverables.map((deliverable, index) => (
                <tr key={deliverable} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {getOriginalDeliverable(deliverable)}
                  </td>
                  {packages.map((pkg) => (
                    <td key={pkg.tier} className={`px-6 py-4 text-center ${
                      pkg.tier === 'best' ? 'bg-amber-50' : ''
                    }`}>
                      <span className={`text-lg ${
                        hasDeliverable(pkg, deliverable)
                          ? 'text-green-600'
                          : 'text-gray-300'
                      }`}>
                        {hasDeliverable(pkg, deliverable) ? '✓' : '—'}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}

              {/* Select Buttons Row */}
              <tr className="bg-gray-50 sticky bottom-0">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  Select Package
                </td>
                {packages.map((pkg) => (
                  <td key={pkg.tier} className={`px-6 py-4 text-center ${
                    pkg.tier === 'best' ? 'bg-amber-50' : ''
                  }`}>
                    <Button
                      onClick={() => onPackageSelect(pkg)}
                      size="sm"
                      className={`${
                        selectedPackage?.tier === pkg.tier
                          ? 'bg-amber-600 hover:bg-amber-700'
                          : pkg.tier === 'best'
                          ? 'bg-amber-500 hover:bg-amber-600'
                          : 'bg-gray-600 hover:bg-gray-700'
                      }`}
                    >
                      {selectedPackage?.tier === pkg.tier ? 'Selected' : 'Select'}
                    </Button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="flex items-center justify-center py-12">Loading...</div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="flex items-center justify-center py-12">Listing not found</div>
      </div>
    );
  }

  const canBuy = user && user.role === 'rnker' && listing && listing.rnkee_id !== user.id;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <Link to="/browse" className="text-sm text-slate-600 hover:text-amber-600">
            ← Back to listings
          </Link>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="space-y-4">
                  <div>
                    <CardTitle className="text-2xl mb-2">{listing.title}</CardTitle>
                    
                    {/* Meta Bar */}
                    <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                      <Badge 
                        variant={listing.exclusivity === 'exclusive' ? 'default' : 'secondary'}
                        className={listing.exclusivity === 'exclusive' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-green-100 text-green-800'
                        }
                      >
                        {listing.exclusivity}
                      </Badge>
                      
                      <div className="flex items-center gap-1 text-sm">
                        <span className="text-slate-500 text-sm">
                          {listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer' ? 'Starting at' : 
                           listing.pricing_model === 'hourly' ? 'Rate' : 'Per lead'}
                        </span>
                        <span className="font-bold text-amber-600 text-lg ml-1">
                          {(() => {
                            if (listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') {
                              if (listing.packages && listing.packages.length > 0) {
                                const enabledPackages = listing.packages.filter(pkg => pkg.enabled);
                                if (enabledPackages.length > 0) {
                                  const minPrice = Math.min(...enabledPackages.map(p => p.price_cents));
                                  return `$${Math.round(minPrice / 100)}`;
                                }
                              }
                              return `$${Math.round(listing.price_cents / 100)}`;
                            } else if (listing.pricing_model === 'hourly') {
                              return `$${Math.round(listing.price_cents / 100)} / hr`;
                            } else if (listing.pricing_model === 'per_lead') {
                              return `$${Math.round(listing.price_cents / 100)}`;
                            }
                            return `$${Math.round(listing.price_cents / 100)}`;
                          })()}
                        </span>
                        {listing.pricing_model === 'per_lead' && selectedPackage?.bundle_qty && (
                          <span className="text-xs text-slate-500 ml-1">
                            (Suggested: {selectedPackage.bundle_qty} leads)
                          </span>
                        )}
                      </div>
                      
                      <Separator orientation="vertical" className="h-4" />
                      
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Package className="w-4 h-4" />
                        Min {listing.min_qty}
                      </div>
                      
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Clock className="w-4 h-4" />
                        {listing.delivery_sla_hours}h SLA
                      </div>

                      {listing.tags && listing.tags.length > 0 && (
                        <>
                          <Separator orientation="vertical" className="h-4" />
                          <div className="flex flex-wrap gap-1">
                            {listing.tags.map((tag, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-3 text-slate-900">Description</h3>
                  <p className="text-slate-700 leading-relaxed">{listing.description}</p>
                </div>

                {/* Service Highlights */}
                {listing.highlights && listing.highlights.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3 text-slate-900">Service Highlights</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {listing.highlights.map((highlight, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0" />
                          <span className="text-slate-700">{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Package Selector for Fixed/Retainer Pricing */}
                {(listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') && listing.packages && listing.packages.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h3 className="font-semibold mb-4 text-slate-900">Select Your Package</h3>
                      
                      {/* Package Tabs */}
                      <div className="flex border-b border-gray-200 mb-4">
                        {listing.packages.filter(pkg => pkg.enabled).map((pkg, index) => (
                          <button
                            key={pkg.tier}
                            onClick={() => setSelectedPackage(pkg)}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                              selectedPackage?.tier === pkg.tier
                                ? 'border-amber-500 text-amber-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                          >
                            {pkg.display_name}
                          </button>
                        ))}
                      </div>

                      {/* Selected Package Details */}
                      {selectedPackage && (
                        <Card className="border-amber-200 bg-amber-50">
                          <CardContent className="pt-6">
                            <div className="space-y-4">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <h4 className="font-semibold text-lg text-gray-900">{selectedPackage.display_name}</h4>
                                  <p className="text-gray-600 mt-1">{selectedPackage.short_description}</p>
                                </div>
                                <div className="text-right">
                                  <div className="text-2xl font-bold text-amber-600">
                                    {listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer' 
                                      ? formatPrice(selectedPackage.price_cents)
                                      : `$${Math.round(listing.price_cents / 100)} / hr`
                                    }
                                  </div>
                                  {listing.pricing_model === 'per_lead' && selectedPackage.bundle_qty && (
                                    <div className="text-sm text-gray-600 mt-1">
                                      Suggested: {selectedPackage.bundle_qty} leads
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Deliverables */}
                              {selectedPackage.deliverables && selectedPackage.deliverables.length > 0 && (
                                <div>
                                  <h5 className="font-medium text-gray-900 mb-2">What's included:</h5>
                                  <ul className="space-y-1">
                                    {selectedPackage.deliverables.map((deliverable, index) => (
                                      <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                                        <span className="text-green-600 text-xs mt-0.5">✓</span>
                                        <span>{deliverable}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Package Details */}
                              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-amber-200">
                                <div className="text-center">
                                  <div className="text-lg font-semibold text-gray-900">{selectedPackage.delivery_days}</div>
                                  <div className="text-sm text-gray-600">day{selectedPackage.delivery_days !== 1 ? 's' : ''} delivery</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-lg font-semibold text-gray-900">{selectedPackage.revisions}</div>
                                  <div className="text-sm text-gray-600">revision{selectedPackage.revisions !== 1 ? 's' : ''}</div>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>

                    {/* Package Comparison Table */}
                    <Separator />
                    <PackageComparisonTable 
                      packages={listing.packages.filter(pkg => pkg.enabled)}
                      selectedPackage={selectedPackage}
                      onPackageSelect={setSelectedPackage}
                    />
                  </>
                )}
                
                {/* FAQ Accordion Section */}
                {faqs && faqs.length > 0 && (
                  <>
                    <Separator />
                    <FAQAccordion faqs={faqs} />
                  </>
                )}
                
                {/* Reviews Section */}
                <Separator />
                <div>
                  <h3 className="font-semibold mb-4 text-slate-900">Reviews</h3>
                  <ReviewsSection sellerId={listing.rnkee_id} listingId={id} />
                </div>
                
                <Separator />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-2 text-slate-900">Service Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Minimum Quantity:</span>
                        <span className="font-medium">{listing.min_qty} services</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Delivery SLA:</span>
                        <span className="font-medium">{listing.delivery_sla_hours} hours</span>
                      </div>
                      {listing.storm_date && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Storm Date:</span>
                          <span className="font-medium">{new Date(listing.storm_date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {(listing.roof_age_min || listing.roof_age_max || (listing.zips && listing.zips.length > 0)) && (
                    <div>
                      <h4 className="font-medium mb-2 text-slate-900">Coverage Details</h4>
                      <div className="space-y-2 text-sm">
                        {(listing.roof_age_min || listing.roof_age_max) && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Roof Age:</span>
                            <span className="font-medium">
                              {listing.roof_age_min || 0} - {listing.roof_age_max || '∞'} years
                            </span>
                          </div>
                        )}
                        {listing.zips && listing.zips.length > 0 && (
                          <div>
                            <span className="text-slate-600 block mb-1">ZIP Codes:</span>
                            <div className="flex flex-wrap gap-1">
                              {listing.zips.slice(0, 8).map((zip, index) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {zip}
                                </Badge>
                              ))}
                              {listing.zips && listing.zips.length > 8 && (
                                <Badge variant="outline" className="text-xs">
                                  +{(listing.zips?.length || 0) - 8} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Seller Info */}
            {listing.seller && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Service Provider</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-slate-200 rounded-full mx-auto mb-3 flex items-center justify-center">
                        <span className="text-xl font-bold text-slate-600">
                          {listing.seller.name.charAt(0)}
                        </span>
                      </div>
                      <Link 
                        to={`/seller/${listing.seller.id}`}
                        className="font-semibold hover:text-orange-600 transition-colors"
                        style={{ color: 'var(--brand-navy)' }}
                      >
                        {listing.seller.name}
                      </Link>
                      <p className="text-sm" style={{ color: 'var(--steel-500)' }}>
                        {listing.seller.company}
                      </p>
                      {listing.seller.location && (
                        <p className="text-sm flex items-center justify-center gap-1 mt-1" style={{ color: 'var(--steel-500)' }}>
                          <MapPin className="w-3 h-3" />
                          {listing.seller.location}
                        </p>
                      )}
                    </div>
                    
                    {listing.seller_profile && (
                      <div className="space-y-3 pt-4 border-t">
                        <PointsBreakdown sellerId={listing.rnkee_id} initialExpanded={false} />
                        <div className="text-center mt-3">
                          <NailRating 
                            avgRating={listing.seller_profile.avg_rating}
                            ratingsCount={listing.seller_profile.ratings_count}
                            showText={true}
                            size="lg"
                          />
                          <div className="text-xs text-slate-500 mt-1">
                            6-Nail Review System
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Buy CTA */}
            <Card className="sticky top-6">
              <CardContent className="pt-6">
                {canBuy ? (
                  <div className="space-y-3">
                    <Dialog open={buyModalOpen} onOpenChange={setBuyModalOpen}>
                      <DialogTrigger asChild>
                        <Button 
                          className="w-full bg-amber-600 hover:bg-amber-700 text-base sm:text-lg py-4 sm:py-6"
                          aria-label={`Buy Now: ${listing.title}`}
                          disabled={
                            ((listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') && !selectedPackage)
                          }
                        >
                          {((listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') && !selectedPackage)
                            ? 'Select Package First'
                            : 'Buy Now'
                          }
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Complete Your Order</DialogTitle>
                          <DialogDescription>
                            Review your selection and provide any required information
                          </DialogDescription>
                        </DialogHeader>
                        
                        <div className="space-y-6">
                          
                          {/* Selected Package/Service Display */}
                          <Card className="border-amber-200 bg-amber-50">
                            <CardContent className="pt-6">
                              <div className="space-y-3">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-lg text-gray-900">
                                      {selectedPackage?.display_name || listing.title}
                                    </h4>
                                    <p className="text-gray-600 mt-1">
                                      {selectedPackage?.short_description || listing.description?.substring(0, 100) + '...'}
                                    </p>
                                  </div>
                                  <div className="text-right ml-4">
                                    <div className="text-lg font-bold text-amber-600">
                                      {listing.pricing_model === 'hourly' 
                                        ? `$${Math.round(listing.price_cents / 100)} / hr`
                                        : formatPrice(getCurrentPrice())
                                      }
                                    </div>
                                    {selectedPackage?.delivery_days && (
                                      <div className="text-sm text-gray-600 mt-1">
                                        {selectedPackage.delivery_days} day{selectedPackage.delivery_days !== 1 ? 's' : ''} delivery
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Quantity/Hours Input - Hidden for fixed/retainer */}
                          {listing.pricing_model !== 'fixed' && listing.pricing_model !== 'retainer' && (
                            <div>
                              <Label htmlFor="qty" className="text-base font-medium">
                                {listing.pricing_model === 'hourly' ? 'Hours Required *' : 'Quantity (Number of leads) *'}
                              </Label>
                              <div className="flex items-center gap-3 mt-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => setQty(Math.max(getMinQty(), qty - 1))}
                                  disabled={qty <= getMinQty()}
                                  className="h-10 w-10 p-0"
                                >
                                  -
                                </Button>
                                <Input
                                  id="qty"
                                  type="number"
                                  min={getMinQty()}
                                  max={getMaxQty()}
                                  value={qty}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value) || getMinQty();
                                    setQty(Math.max(getMinQty(), Math.min(getMaxQty(), value)));
                                  }}
                                  className="text-center w-20"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => setQty(Math.min(getMaxQty(), qty + 1))}
                                  disabled={qty >= getMaxQty()}
                                  className="h-10 w-10 p-0"
                                >
                                  +
                                </Button>
                                <span className="text-sm text-gray-500">
                                  {listing.pricing_model === 'per_lead' && selectedPackage?.bundle_qty 
                                    ? `Suggested: ${selectedPackage.bundle_qty}` 
                                    : listing.min_qty > 1 
                                    ? `Min: ${listing.min_qty}` 
                                    : ''}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Buyer Requirements */}
                          {listing.buyer_requirements?.length > 0 && (
                            <div>
                              <Label className="text-base font-medium">Required Information</Label>
                              <div className="space-y-3 mt-2">
                                {listing.buyer_requirements.map((requirement, index) => (
                                  <div key={index}>
                                    <Label htmlFor={`req-${index}`} className="text-sm">
                                      {requirement} <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                      id={`req-${index}`}
                                      value={buyerRequirements[index] || ''}
                                      onChange={(e) => setBuyerRequirements(prev => ({
                                        ...prev,
                                        [index]: e.target.value
                                      }))}
                                      placeholder="Please provide details..."
                                      required
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Additional Notes */}
                          <div>
                            <Label htmlFor="buyer-notes" className="text-base font-medium">
                              Additional Notes (Optional)
                            </Label>
                            <Textarea
                              id="buyer-notes"
                              value={buyerNotes}
                              onChange={(e) => setBuyerNotes(e.target.value)}
                              placeholder="Any additional requirements or specifications..."
                              rows={3}
                              className="mt-2"
                            />
                          </div>

                          {/* Price Breakdown */}
                          <div className="bg-slate-50 p-4 rounded-lg">
                            <h4 className="font-medium mb-3">Price Breakdown</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="font-medium">Service:</span>
                                <span>
                                  {selectedPackage?.display_name || listing.title}
                                </span>
                              </div>
                              
                              <div className="flex justify-between">
                                <span>Unit Price:</span>
                                <span>
                                  {formatPrice(getCurrentPrice())}
                                  {listing.pricing_model === 'hourly' && ' / hr'}
                                  {listing.pricing_model === 'per_lead' && ' / lead'}
                                </span>
                              </div>
                              
                              {(listing.pricing_model === 'per_lead' || listing.pricing_model === 'hourly') && (
                                <div className="flex justify-between">
                                  <span>
                                    {listing.pricing_model === 'hourly' ? 'Hours:' : 'Quantity:'}
                                  </span>
                                  <span>×{qty}</span>
                                </div>
                              )}
                              
                              <Separator />
                              
                              {/* Subtotal */}
                              <div className="flex justify-between">
                                <span>Subtotal:</span>
                                <span>{formatPrice(getSubtotal())}</span>
                              </div>
                              
                              {/* Buyer Fee */}
                              <div className="flex justify-between">
                                <span>
                                  {isVipBuyer ? (
                                    <span className="text-green-600">Buyer fee waived (VIP):</span>
                                  ) : (
                                    `Rnkem Service Fee (${buyerCommissionRate}%):`
                                  )}
                                </span>
                                <span className={isVipBuyer ? "text-green-600" : ""}>
                                  {isVipBuyer ? "FREE" : formatPrice(getBuyerFee())}
                                </span>
                              </div>
                              
                              <Separator />
                              
                              {/* Total */}
                              <div className="flex justify-between items-center font-bold text-lg">
                                <span>Total charged today:</span>
                                <span className="text-amber-600">
                                  {formatPrice(getTotalWithFees())}
                                </span>
                              </div>
                              
                              {/* Stripe Notice */}
                              <div className="mt-3 pt-2 border-t border-slate-200">
                                <div className="flex items-center justify-between text-xs text-slate-600">
                                  <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                    </svg>
                                    <span>Secure checkout by Stripe</span>
                                  </div>
                                  {stripeMode === 'test' && (
                                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                                      TEST MODE
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              onClick={() => setBuyModalOpen(false)} 
                              className="flex-1"
                            >
                              Cancel
                            </Button>
                            <Button 
                              onClick={handleBuy} 
                              className="flex-1 bg-amber-600 hover:bg-amber-700"
                              disabled={
                                ((listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') && !selectedPackage)
                              }
                            >
                              Confirm Purchase
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    {/* Contact Seller Button */}
                    <Button 
                      variant="outline"
                      onClick={handleContactSeller}
                      className="w-full text-base sm:text-lg py-4 sm:py-6 border-gray-300 hover:border-gray-400"
                      aria-label={`Contact Seller about ${listing.title}`}
                    >
                      Contact Seller
                    </Button>
                  </div>
                ) : (
                  <div className="text-center space-y-3">
                    <Button disabled className="w-full text-base sm:text-lg py-4 sm:py-6 mb-3">
                      {!user ? 'Sign In Required' : 'Cannot Buy Own Listing'}
                    </Button>
                    <p className="text-sm text-slate-500">
                      {!user 
                        ? 'Sign in as a contractor to purchase services' 
                        : user.role !== 'rnker'
                        ? 'Only contractors can purchase services'
                        : 'You cannot purchase your own listings'
                      }
                    </p>
                    
                    {/* Contact Seller for non-buyers (if not owner) */}
                    {user && user.id !== listing.rnkee_id && (
                      <>
                        {user.role === 'rnker' ? (
                          <Button 
                            variant="outline"
                            onClick={handleContactSeller}
                            className="w-full text-base sm:text-lg py-4 sm:py-6 border-gray-300 hover:border-gray-400"
                            aria-label={`Contact Seller about ${listing.title}`}
                          >
                            Contact Seller
                          </Button>
                        ) : (
                          <Button 
                            variant="outline"
                            disabled
                            className="w-full text-base sm:text-lg py-4 sm:py-6 opacity-50"
                            title="Switch to Buyer role to contact sellers"
                          >
                            Contact Seller
                          </Button>
                        )}
                      </>
                    )}
                    
                    {/* Show contact for non-logged-in users */}
                    {!user && (
                      <Button 
                        variant="outline"
                        onClick={handleContactSeller}
                        className="w-full text-base sm:text-lg py-4 sm:py-6 border-gray-300 hover:border-gray-400"
                        aria-label={`Contact Seller about ${listing.title}`}
                      >
                        Contact Seller
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Mobile Sticky CTA */}
      {canBuy && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 lg:hidden z-50">
          <Button 
            onClick={() => setBuyModalOpen(true)}
            className="w-full bg-amber-600 hover:bg-amber-700 text-base font-semibold py-4"
            disabled={
              ((listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') && !selectedPackage)
            }
          >
            {((listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') && !selectedPackage)
              ? 'Select Package First'
              : selectedPackage
              ? `Buy ${selectedPackage.display_name} - ${formatPrice(selectedPackage.price_cents)}`
              : `Buy Now - Starting at $${Math.round(listing.price_cents / 100)}`
            }
          </Button>
        </div>
      )}
    </div>
  );
};

// 6-Step Listing Wizard Component
const ListingWizard = ({ 
  isOpen, 
  onClose, 
  editingListing, 
  listingForm, 
  setListingForm, 
  onSubmit, 
  wizardStep, 
  setWizardStep 
}) => {
  const addArrayItem = (field) => {
    setListingForm({
      ...listingForm,
      [field]: [...listingForm[field], '']
    });
  };

  const removeArrayItem = (field, index) => {
    setListingForm({
      ...listingForm,
      [field]: listingForm[field].filter((_, i) => i !== index)
    });
  };

  const updateArrayItem = (field, index, value) => {
    const newArray = [...listingForm[field]];
    newArray[index] = value;
    setListingForm({
      ...listingForm,
      [field]: newArray
    });
  };

  const updatePackage = (packageIndex, field, value) => {
    const newPackages = [...listingForm.packages];
    newPackages[packageIndex] = {
      ...newPackages[packageIndex],
      [field]: value
    };
    setListingForm({
      ...listingForm,
      packages: newPackages
    });
  };

  const updatePackageFeature = (packageIndex, featureIndex, value) => {
    const newPackages = [...listingForm.packages];
    const newFeatures = [...newPackages[packageIndex].features];
    newFeatures[featureIndex] = value;
    newPackages[packageIndex] = {
      ...newPackages[packageIndex],
      features: newFeatures
    };
    setListingForm({
      ...listingForm,
      packages: newPackages
    });
  };

  const addPackageFeature = (packageIndex) => {
    const newPackages = [...listingForm.packages];
    newPackages[packageIndex] = {
      ...newPackages[packageIndex],
      features: [...newPackages[packageIndex].features, '']
    };
    setListingForm({
      ...listingForm,
      packages: newPackages
    });
  };

  const removePackageFeature = (packageIndex, featureIndex) => {
    const newPackages = [...listingForm.packages];
    newPackages[packageIndex] = {
      ...newPackages[packageIndex],
      features: newPackages[packageIndex].features.filter((_, i) => i !== featureIndex)
    };
    setListingForm({
      ...listingForm,
      packages: newPackages
    });
  };

  const nextStep = () => {
    if (wizardStep < 6) setWizardStep(wizardStep + 1);
  };

  const prevStep = () => {
    if (wizardStep > 1) setWizardStep(wizardStep - 1);
  };

  const canProceed = () => {
    switch (wizardStep) {
      case 1:
        return listingForm.title.trim() && listingForm.categories.length > 0;
      case 2:
        // Step 2: Pricing & Packages (combined step)
        if (!listingForm.pricing_model) return false;
        
        // For per_lead and hourly: validate base price
        if (listingForm.pricing_model === 'per_lead' || listingForm.pricing_model === 'hourly') {
          return listingForm.base_price_dollars > 0;
        }
        
        // For fixed/retainer: validate packages
        if (listingForm.pricing_model === 'fixed' || listingForm.pricing_model === 'retainer') {
          if (!listingForm.packages || listingForm.packages.length !== 3) return false;
          
          const enabledPackages = listingForm.packages.filter(pkg => pkg.enabled);
          if (enabledPackages.length === 0) return false;
          
          return enabledPackages.every(pkg => 
            pkg.display_name && pkg.display_name.trim() && 
            pkg.short_description && pkg.short_description.trim() && 
            pkg.deliverables && pkg.deliverables.length >= 1 && 
            pkg.deliverables.every(d => d && d.trim()) &&
            pkg.price_dollars > 0 &&
            pkg.delivery_days > 0
          );
        }
        
        return true;
      case 3:
        return true; // FAQ step is optional
      case 4:
        return true; // Requirements step is optional  
      case 5:
        return true; // Review step
      default:
        return false;
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Service Title *</Label>
            <Input
              id="title"
              value={listingForm.title}
              onChange={(e) => setListingForm({...listingForm, title: e.target.value})}
              placeholder="e.g., Premium Hail Damage Leads - Dallas Metro"
              required
            />
          </div>
          
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={listingForm.description}
              onChange={(e) => setListingForm({...listingForm, description: e.target.value})}
              rows={4}
              placeholder="Describe your service in detail..."
            />
          </div>
          
          <div>
            <Label>Service Categories *</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 max-h-48 overflow-y-auto border rounded-lg p-3">
              {Object.keys(getServiceCategories()).map(category => (
                <div key={category} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`category-${category}`}
                    checked={listingForm.categories.includes(category)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setListingForm({ 
                          ...listingForm, 
                          categories: [...listingForm.categories, category] 
                        });
                      } else {
                        setListingForm({ 
                          ...listingForm, 
                          categories: listingForm.categories.filter(c => c !== category) 
                        });
                      }
                    }}
                    className="rounded border-gray-300"
                  />
                  <Label 
                    htmlFor={`category-${category}`} 
                    className="text-sm font-normal cursor-pointer"
                  >
                    {formatCategoryName(category)}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {listingForm.categories.length > 0 && (
            <div>
              <Label>Subcategories</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 max-h-32 overflow-y-auto border rounded-lg p-3">
                {listingForm.categories.flatMap(cat => 
                  getServiceCategories()[cat]?.map(subcat => (
                    <div key={subcat} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`subcat-${subcat}`}
                        checked={listingForm.subcategories.includes(subcat)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setListingForm({ 
                              ...listingForm, 
                              subcategories: [...listingForm.subcategories, subcat] 
                            });
                          } else {
                            setListingForm({ 
                              ...listingForm, 
                              subcategories: listingForm.subcategories.filter(s => s !== subcat) 
                            });
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      <Label 
                        htmlFor={`subcat-${subcat}`} 
                        className="text-sm font-normal cursor-pointer"
                      >
                        {formatSubcategoryName(subcat)}
                      </Label>
                    </div>
                  )) || []
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Helper functions for package management (used by enhanced Step 2)
  const updatePackageField = (tierIndex, field, value) => {
    const newPackages = [...listingForm.packages];
    newPackages[tierIndex] = {
      ...newPackages[tierIndex],
      [field]: value
    };
    setListingForm({
      ...listingForm,
      packages: newPackages
    });
  };

  const addDeliverable = (tierIndex) => {
    const newPackages = [...listingForm.packages];
    if (!newPackages[tierIndex].deliverables) {
      newPackages[tierIndex].deliverables = [];
    }
    newPackages[tierIndex].deliverables.push('');
    setListingForm({
      ...listingForm,
      packages: newPackages
    });
  };

  const removeDeliverable = (tierIndex, deliverableIndex) => {
    const newPackages = [...listingForm.packages];
    newPackages[tierIndex].deliverables = newPackages[tierIndex].deliverables.filter((_, i) => i !== deliverableIndex);
    setListingForm({
      ...listingForm,
      packages: newPackages
    });
  };

  const updateDeliverable = (tierIndex, deliverableIndex, value) => {
    const newPackages = [...listingForm.packages];
    newPackages[tierIndex].deliverables[deliverableIndex] = value;
    setListingForm({
      ...listingForm,
      packages: newPackages
    });
  };

  // Enhanced Step 2: Pricing Model Selection Only (streamlined)
  const renderStep2Enhanced = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Pricing Model</h3>
        <p className="text-gray-600 mb-4">Choose how you want to price your service</p>
        
        <div className="space-y-4">
          {/* Pricing Model Selection */}
          <div>
            <Label>Pricing Model *</Label>
            <Select 
              value={listingForm.pricing_model} 
              onValueChange={(value) => setListingForm({...listingForm, pricing_model: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Fixed Price Packages</SelectItem>
                <SelectItem value="per_lead">Per Lead</SelectItem>
                <SelectItem value="hourly">Hourly Rate</SelectItem>
                <SelectItem value="retainer">Monthly Retainer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Simple pricing for non-package models */}
          {listingForm.pricing_model === 'per_lead' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price per Lead ($) *</Label>
                <Input
                  type="number"
                  value={listingForm.base_price_dollars}
                  onChange={(e) => setListingForm({...listingForm, base_price_dollars: parseFloat(e.target.value) || 0})}
                  placeholder="10.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <div>
                <Label>Delivery SLA (Days) *</Label>
                <Input
                  type="number"
                  value={listingForm.delivery_sla_days}
                  onChange={(e) => setListingForm({...listingForm, delivery_sla_days: parseInt(e.target.value) || 1})}
                  placeholder="3"
                  min="1"
                  max="30"
                />
              </div>
            </div>
          )}

          {listingForm.pricing_model === 'hourly' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hourly Rate ($) *</Label>
                <Input
                  type="number"
                  value={listingForm.base_price_dollars}
                  onChange={(e) => setListingForm({...listingForm, base_price_dollars: parseFloat(e.target.value) || 0})}
                  placeholder="50.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <div>
                <Label>Minimum Hours *</Label>
                <Input
                  type="number"
                  value={listingForm.min_qty}
                  onChange={(e) => setListingForm({...listingForm, min_qty: parseInt(e.target.value) || 1})}
                  placeholder="1"
                  min="1"
                  max="100"
                />
              </div>
            </div>
          )}

          {/* Info for package-based pricing */}
          {(listingForm.pricing_model === 'fixed' || listingForm.pricing_model === 'retainer') && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">
                {listingForm.pricing_model === 'retainer' ? 'Retainer Packages' : 'Package Pricing'}
              </h4>
              <p className="text-sm text-blue-700">
                You'll configure your Good, Better, Best packages in the next step with detailed deliverables and pricing.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Pricing & Packages</h3>
        
        <div className="space-y-4">
          <div>
            <Label>Pricing Model *</Label>
            <Select 
              value={listingForm.pricing_model} 
              onValueChange={(value) => setListingForm({...listingForm, pricing_model: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Fixed Price Packages</SelectItem>
                <SelectItem value="per_lead">Per Lead</SelectItem>
                <SelectItem value="hourly">Hourly Rate</SelectItem>
                <SelectItem value="retainer">Monthly Retainer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {listingForm.pricing_model === 'fixed' || listingForm.pricing_model === 'retainer' ? (
            <div>
              <Label>{listingForm.pricing_model === 'retainer' ? 'Retainer Packages' : 'Service Packages'}</Label>
              <div className="space-y-4 mt-2">
                {listingForm.packages.map((pkg, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <Label>Package Name</Label>
                        <Input
                          value={pkg.name}
                          onChange={(e) => updatePackage(index, 'name', e.target.value)}
                          placeholder={`${pkg.name} Package`}
                        />
                      </div>
                      <div>
                        <Label>{listingForm.pricing_model === 'retainer' ? 'Monthly Price ($)' : 'Price ($)'}</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={pkg.price_dollars}
                          onChange={(e) => updatePackage(index, 'price_dollars', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <Label>Description</Label>
                      <Textarea
                        value={pkg.description}
                        onChange={(e) => updatePackage(index, 'description', e.target.value)}
                        rows={2}
                        placeholder="What's included in this package?"
                      />
                    </div>
                    <div>
                      <Label>Features</Label>
                      {pkg.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex gap-2 mt-2">
                          <Input
                            value={feature}
                            onChange={(e) => updatePackageFeature(index, featureIndex, e.target.value)}
                            placeholder="Package feature"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removePackageFeature(index, featureIndex)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addPackageFeature(index)}
                        className="mt-2"
                      >
                        Add Feature
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <Label>Base Price ($) *</Label>
              <Input
                type="number"
                min="1"
                step="0.01"
                value={listingForm.base_price_dollars || ''}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  setListingForm({
                    ...listingForm, 
                    base_price_dollars: isNaN(value) ? 0 : value
                  });
                }}
                placeholder="25.00"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Delivery SLA (days) *</Label>
              <Input
                type="number"
                min="1"
                value={listingForm.delivery_sla_days}
                onChange={(e) => setListingForm({...listingForm, delivery_sla_days: parseInt(e.target.value) || 1})}
              />
            </div>
            <div>
              <Label>Minimum Quantity</Label>
              <Input
                type="number"
                min="1"
                value={listingForm.min_qty}
                onChange={(e) => setListingForm({...listingForm, min_qty: parseInt(e.target.value) || 1})}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Initialize packages with proper 3-tier structure
  const initializePackages = () => {
    if (!listingForm.packages || listingForm.packages.length !== 3) {
      const defaultPackages = [
        {
          tier: 'good',
          display_name: 'Good Package',
          short_description: 'Essential service package',
          deliverables: ['Basic deliverable'],
          price_cents: (listingForm.base_price_dollars || 25) * 100,
          delivery_days: listingForm.delivery_sla_days || 3,
          revisions: 0,
          bundle_qty: listingForm.pricing_model === 'per_lead' ? (listingForm.min_qty || 5) : null,
          enabled: true
        },
        {
          tier: 'better',
          display_name: 'Better Package',
          short_description: 'Enhanced service package',
          deliverables: ['Enhanced deliverable', 'Additional feature'],
          price_cents: (listingForm.base_price_dollars || 25) * 100,
          delivery_days: Math.max(1, (listingForm.delivery_sla_days || 3) - 1),
          revisions: 1,
          bundle_qty: listingForm.pricing_model === 'per_lead' ? (listingForm.min_qty || 5) * 2 : null,
          enabled: true
        },
        {
          tier: 'best',
          display_name: 'Best Package',
          short_description: 'Premium service package',
          deliverables: ['Premium deliverable', 'Priority support', 'Extra features'],
          price_cents: (listingForm.base_price_dollars || 25) * 100,
          delivery_days: Math.max(1, (listingForm.delivery_sla_days || 3) - 2),
          revisions: 2,
          bundle_qty: listingForm.pricing_model === 'per_lead' ? (listingForm.min_qty || 5) * 3 : null,
          enabled: true
        }
      ];
      
      setListingForm({
        ...listingForm,
        packages: defaultPackages
      });
    }
  };

  const renderStep3Packages = () => {
    return (
      <div className="space-y-6">
        {/* Pricing Model Selection */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Pricing & Packages</h3>
          
          <div className="mb-6">
            <Label>Pricing Model *</Label>
            <Select 
              value={listingForm.pricing_model} 
              onValueChange={(value) => {
                setListingForm({...listingForm, pricing_model: value});
                // Initialize packages when switching to fixed/retainer
                if ((value === 'fixed' || value === 'retainer') && (!listingForm.packages || listingForm.packages.length !== 3)) {
                  initializePackages();
                }
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Fixed Price Packages</SelectItem>
                <SelectItem value="per_lead">Per Lead</SelectItem>
                <SelectItem value="hourly">Hourly Rate</SelectItem>
                <SelectItem value="retainer">Monthly Retainer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* For Per Lead and Hourly - Simple Pricing */}
        {listingForm.pricing_model === 'per_lead' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-4">Per Lead Pricing</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price per Lead ($) *</Label>
                <Input
                  type="number"
                  value={listingForm.base_price_dollars}
                  onChange={(e) => setListingForm({...listingForm, base_price_dollars: parseFloat(e.target.value) || 0})}
                  placeholder="10.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <div>
                <Label>Delivery SLA (Days) *</Label>
                <Input
                  type="number"
                  value={listingForm.delivery_sla_days}
                  onChange={(e) => setListingForm({...listingForm, delivery_sla_days: parseInt(e.target.value) || 1})}
                  placeholder="3"
                  min="1"
                  max="30"
                />
              </div>
            </div>
          </div>
        )}

        {listingForm.pricing_model === 'hourly' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-900 mb-4">Hourly Rate Pricing</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hourly Rate ($) *</Label>
                <Input
                  type="number"
                  value={listingForm.base_price_dollars}
                  onChange={(e) => setListingForm({...listingForm, base_price_dollars: parseFloat(e.target.value) || 0})}
                  placeholder="50.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <div>
                <Label>Minimum Hours *</Label>
                <Input
                  type="number"
                  value={listingForm.min_qty}
                  onChange={(e) => setListingForm({...listingForm, min_qty: parseInt(e.target.value) || 1})}
                  placeholder="1"
                  min="1"
                  max="100"
                />
              </div>
            </div>
          </div>
        )}

        {/* Good/Better/Best Packages for Fixed and Retainer */}
        {(listingForm.pricing_model === 'fixed' || listingForm.pricing_model === 'retainer') && (
          <>
            {/* Initialize packages if needed */}
            {(() => {
              if (!listingForm.packages || listingForm.packages.length !== 3) {
                initializePackages();
                return <div>Initializing packages...</div>;
              }
              return null;
            })()}
            
            {listingForm.packages && listingForm.packages.length === 3 && (
              <div>
                <h4 className="font-medium mb-4">
                  {listingForm.pricing_model === 'retainer' ? 'Monthly Retainer Packages' : 'Service Packages'}
                </h4>
                <p className="text-sm text-gray-600 mb-6">
                  Create Good, Better, Best packages to give buyers options. 
                  {listingForm.pricing_model === 'retainer' ? ' Set monthly retainer prices.' : ' Set one-time project prices.'}
                </p>
                
                <div className="space-y-6">
                  {listingForm.packages.map((pkg, tierIndex) => (
                    <Card key={tierIndex} className="border">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">
                            {pkg.tier === 'basic' && '🥉 Good'} 
                            {pkg.tier === 'standard' && '🥈 Better'} 
                            {pkg.tier === 'premium' && '🥇 Best'}
                          </CardTitle>
                          <div className="flex items-center gap-2">
                            <Label htmlFor={`enabled-${tierIndex}`} className="text-sm">Enable</Label>
                            <input
                              id={`enabled-${tierIndex}`}
                              type="checkbox"
                              checked={pkg.enabled}
                              onChange={(e) => updatePackageField(tierIndex, 'enabled', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                          </div>
                        </div>
                      </CardHeader>
                      
                      {pkg.enabled && (
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Package Name *</Label>
                              <Input
                                value={pkg.display_name || ''}
                                onChange={(e) => updatePackageField(tierIndex, 'display_name', e.target.value)}
                                placeholder={`${pkg.tier} Package`}
                              />
                            </div>
                            <div>
                              <Label>{listingForm.pricing_model === 'retainer' ? 'Monthly Price ($) *' : 'Price ($) *'}</Label>
                              <Input
                                type="number"
                                value={pkg.price_dollars || ''}
                                onChange={(e) => updatePackageField(tierIndex, 'price_dollars', parseFloat(e.target.value) || 0)}
                                placeholder="25.00"
                                step="0.01"
                                min="0"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <Label>Short Description *</Label>
                            <Input
                              value={pkg.short_description || ''}
                              onChange={(e) => updatePackageField(tierIndex, 'short_description', e.target.value)}
                              placeholder="Brief description of this package"
                            />
                          </div>
                          
                          <div>
                            <Label>Delivery Time (Days) *</Label>
                            <Input
                              type="number"
                              value={pkg.delivery_days || ''}
                              onChange={(e) => updatePackageField(tierIndex, 'delivery_days', parseInt(e.target.value) || 1)}
                              placeholder="3"
                              min="1"
                              max="90"
                            />
                          </div>
                          
                          <div>
                            <Label>Deliverables *</Label>
                            <p className="text-xs text-gray-600 mb-2">What will the buyer receive?</p>
                            {pkg.deliverables && pkg.deliverables.map((deliverable, deliverableIndex) => (
                              <div key={deliverableIndex} className="flex gap-2 mb-2">
                                <Input
                                  value={deliverable}
                                  onChange={(e) => updateDeliverable(tierIndex, deliverableIndex, e.target.value)}
                                  placeholder="Deliverable description"
                                />
                                {pkg.deliverables.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => removeDeliverable(tierIndex, deliverableIndex)}
                                  >
                                    Remove
                                  </Button>
                                )}
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addDeliverable(tierIndex)}
                            >
                              Add Deliverable
                            </Button>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Enhanced Step 2 Component - Package Configuration (removed old content)

  const renderStep4FAQ = () => {
    // Initialize FAQ array if needed
    if (!listingForm.faqs) {
      setListingForm({
        ...listingForm,
        faqs: []
      });
      return <div>Loading FAQ...</div>;
    }

    const addFAQ = () => {
      if (listingForm.faqs.length < 5) {
        setListingForm({
          ...listingForm,
          faqs: [...listingForm.faqs, { question: '', answer: '', sort_order: listingForm.faqs.length + 1 }]
        });
      }
    };

    const removeFAQ = (index) => {
      const newFaqs = listingForm.faqs.filter((_, i) => i !== index);
      // Update sort_order
      newFaqs.forEach((faq, i) => {
        faq.sort_order = i + 1;
      });
      setListingForm({
        ...listingForm,
        faqs: newFaqs
      });
    };

    const updateFAQ = (index, field, value) => {
      const newFaqs = [...listingForm.faqs];
      newFaqs[index] = {
        ...newFaqs[index],
        [field]: value
      };
      setListingForm({
        ...listingForm,
        faqs: newFaqs
      });
    };

    const moveFAQ = (fromIndex, toIndex) => {
      const newFaqs = [...listingForm.faqs];
      const [movedItem] = newFaqs.splice(fromIndex, 1);
      newFaqs.splice(toIndex, 0, movedItem);
      
      // Update sort_order
      newFaqs.forEach((faq, i) => {
        faq.sort_order = i + 1;
      });
      
      setListingForm({
        ...listingForm,
        faqs: newFaqs
      });
    };

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Frequently Asked Questions</h3>
          <p className="text-sm text-gray-600 mb-6">
            Add up to 5 frequently asked questions to help buyers understand your service better.
          </p>
        </div>

        {listingForm.faqs.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-gray-500 mb-4">No FAQs added yet</p>
            <Button onClick={addFAQ} className="bg-amber-600 hover:bg-amber-700">
              Add Your First FAQ
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {listingForm.faqs.map((faq, index) => (
              <Card key={index} className="border border-gray-200">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">FAQ #{index + 1}</CardTitle>
                    <div className="flex items-center gap-2">
                      {index > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => moveFAQ(index, index - 1)}
                          title="Move up"
                        >
                          ↑
                        </Button>
                      )}
                      {index < listingForm.faqs.length - 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => moveFAQ(index, index + 1)}
                          title="Move down"
                        >
                          ↓
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeFAQ(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        ✕
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Question *</Label>
                    <Input
                      value={faq.question}
                      onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                      placeholder="What question do customers frequently ask?"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Answer *</Label>
                    <textarea
                      value={faq.answer}
                      onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                      placeholder="Provide a clear and helpful answer"
                      rows={3}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {listingForm.faqs.length > 0 && listingForm.faqs.length < 5 && (
          <div className="text-center">
            <Button 
              type="button"
              onClick={addFAQ}
              variant="outline"
              className="border-dashed border-2"
            >
              + Add Another FAQ ({listingForm.faqs.length}/5)
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderStep5 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Requirements & Details</h3>
        
        <div className="space-y-4">
          <div>
            <Label>Buyer Requirements</Label>
            <p className="text-sm text-gray-500 mb-2">Questions you want buyers to answer when ordering</p>
            {listingForm.buyer_requirements.map((req, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <Input
                  value={req}
                  onChange={(e) => updateArrayItem('buyer_requirements', index, e.target.value)}
                  placeholder="e.g., What's your target area?"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeArrayItem('buyer_requirements', index)}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addArrayItem('buyer_requirements')}
            >
              Add Requirement
            </Button>
          </div>

          <div>
            <Label>Service Highlights</Label>
            <p className="text-sm text-gray-500 mb-2">Key benefits or features of your service</p>
            {listingForm.highlights.map((highlight, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <Input
                  value={highlight}
                  onChange={(e) => updateArrayItem('highlights', index, e.target.value)}
                  placeholder="e.g., 24-hour delivery"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeArrayItem('highlights', index)}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addArrayItem('highlights')}
            >
              Add Highlight
            </Button>
          </div>

          <div>
            <Label>Portfolio URLs</Label>
            <p className="text-sm text-gray-500 mb-2">Links to examples of your work</p>
            {listingForm.portfolio_urls.map((url, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <Input
                  value={url}
                  onChange={(e) => updateArrayItem('portfolio_urls', index, e.target.value)}
                  placeholder="https://example.com/portfolio"
                  type="url"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeArrayItem('portfolio_urls', index)}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addArrayItem('portfolio_urls')}
            >
              Add URL
            </Button>
          </div>

          <div>
            <Label>Service Type</Label>
            <Select 
              value={listingForm.exclusivity} 
              onValueChange={(value) => setListingForm({...listingForm, exclusivity: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="shared">Shared Service</SelectItem>
                <SelectItem value="exclusive">Exclusive Service</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Review & Publish</h3>
        
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Service Summary</h4>
            <p><strong>Title:</strong> {listingForm.title}</p>
            <p><strong>Categories:</strong> {listingForm.categories.map(formatCategoryName).join(', ')}</p>
            <p><strong>Pricing:</strong> {formatPricingModel(listingForm.pricing_model)}</p>
            {listingForm.pricing_model === 'fixed' ? (
              <div>
                <strong>Packages:</strong>
                <ul className="ml-4 mt-1">
                  {listingForm.packages.filter(p => p.price_dollars > 0).map((pkg, index) => (
                    <li key={index}>
                      {pkg.name}: ${pkg.price_dollars.toFixed(2)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p><strong>Price:</strong> ${listingForm.base_price_dollars.toFixed(2)}</p>
            )}
            <p><strong>Delivery:</strong> {listingForm.delivery_sla_days} days</p>
          </div>

          <div>
            <Label htmlFor="tags">Tags (comma separated)</Label>
            <Input
              id="tags"
              value={listingForm.tags}
              onChange={(e) => setListingForm({...listingForm, tags: e.target.value})}
              placeholder="hail, wind, exclusive"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={listingForm.is_active}
              onCheckedChange={(checked) => setListingForm({...listingForm, is_active: checked})}
            />
            <Label htmlFor="is_active">Publish immediately</Label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (wizardStep) {
      case 1: return renderStep1();
      case 2: return renderStep3Packages(); // Good/Better/Best packages with pricing model selection
      case 3: return renderStep4FAQ(); // FAQ Management 
      case 4: return renderStep5(); // Requirements & Details
      case 5: return renderStep6(); // Review & Publish
      default: return renderStep1();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingListing ? 'Edit Listing' : 'Create New Listing'}
          </DialogTitle>
          
          {/* Step Indicator */}
          <div className="flex items-center justify-center space-x-2 mt-4 overflow-x-auto">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className="flex items-center">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                    step === wizardStep 
                      ? 'bg-amber-600 text-white' 
                      : step < wizardStep 
                        ? 'bg-green-600 text-white' 
                        : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step < wizardStep ? '✓' : step}
                </div>
                {step < 5 && (
                  <div 
                    className={`w-8 h-1 mx-1 ${
                      step < wizardStep ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          
          <div className="text-center text-xs text-gray-500 mt-2">
            Step {wizardStep} of 5: {
              wizardStep === 1 ? 'Basic Information' :
              wizardStep === 2 ? 'Pricing & Packages' :
              wizardStep === 3 ? 'FAQ Management' :
              wizardStep === 4 ? 'Requirements & Details' :
              'Review & Publish'
            }
          </div>
        </DialogHeader>
        
        <form onSubmit={onSubmit} className="space-y-6">
          {renderStepContent()}
          
          <div className="flex justify-between pt-6 border-t">
            <div>
              {wizardStep > 1 && (
                <Button type="button" variant="outline" onClick={prevStep}>
                  Previous
                </Button>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              
              {wizardStep < 5 ? (
                <Button 
                  type="button" 
                  onClick={nextStep}
                  disabled={!canProceed()}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Next
                </Button>
              ) : (
                <Button 
                  type="submit" 
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {editingListing ? 'Update' : 'Create'} Listing
                </Button>
              )}
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Enhanced Seller Dashboard with 4-Step Listing Wizard
const SellerDashboard = () => {
  const [listings, setListings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  
  // Wizard state management
  const [wizardStep, setWizardStep] = useState(1);
  const [listingForm, setListingForm] = useState({
    // Step 1: Basic Info
    title: '',
    description: '',
    categories: [],
    subcategories: [],
    
    // Step 2: Pricing & Packages
    pricing_model: 'fixed', // fixed, per_lead, hourly, retainer
    base_price_dollars: 25.00,
    delivery_sla_days: 1,
    packages: [
      { name: 'Basic', description: '', price_dollars: 25.00, features: [''] },
      { name: 'Standard', description: '', price_dollars: 50.00, features: [''] },
      { name: 'Premium', description: '', price_dollars: 100.00, features: [''] }
    ],
    
    // Step 3: Requirements & Details
    buyer_requirements: [''], // Questions seller wants buyers to answer
    highlights: [''],
    portfolio_urls: [''],
    exclusivity: 'shared',
    min_qty: 1,
    
    // Step 4: Review & Publish
    tags: '',
    is_active: true
  });

  const handleCreateListing = async (e) => {
    e.preventDefault();
    
    // Comprehensive validation
    const errors = [];
    
    if (!listingForm.title.trim()) errors.push('Title is required');
    if (listingForm.categories.length === 0) errors.push('Please select at least one service category');
    if (!listingForm.base_price_dollars) errors.push('Base price is required');
    
    // Validate packages
    if (listingForm.packages && listingForm.packages.length === 3) {
      const enabledPackages = listingForm.packages.filter(pkg => pkg.enabled);
      if (enabledPackages.length === 0) {
        errors.push('At least one package must be enabled');
      } else {
        enabledPackages.forEach((pkg, index) => {
          if (!pkg.display_name.trim()) errors.push(`Package ${index + 1} name is required`);
          if (!pkg.short_description.trim()) errors.push(`Package ${index + 1} description is required`);
          if (pkg.deliverables.length === 0 || !pkg.deliverables.some(d => d.trim())) {
            errors.push(`Package ${index + 1} must have at least one deliverable`);
          }
          if (pkg.price_cents < 0) errors.push(`Package ${index + 1} price cannot be negative`);
          if (pkg.delivery_days < 1) errors.push(`Package ${index + 1} delivery days must be at least 1`);
        });
      }
    }
    
    // Validate FAQs
    if (listingForm.faqs && listingForm.faqs.length > 0) {
      listingForm.faqs.forEach((faq, index) => {
        if (!faq.question.trim()) errors.push(`FAQ ${index + 1} question is required`);
        if (!faq.answer.trim()) errors.push(`FAQ ${index + 1} answer is required`);
      });
    }
    
    if (errors.length > 0) {
      toast.error(errors.join(', '));
      return;
    }
    
    try {
      // Prepare data for backend
      const formData = {
        title: listingForm.title,
        description: listingForm.description,
        service_category: listingForm.categories[0], // Primary category
        service_subcategory: listingForm.subcategories[0] || '',
        pricing_model: listingForm.pricing_model,
        exclusivity: listingForm.exclusivity,
        min_qty: parseInt(listingForm.min_qty),
        delivery_sla_hours: parseInt(listingForm.delivery_sla_days) * 24,
        highlights: listingForm.highlights.filter(h => h.trim()),
        portfolio_urls: listingForm.portfolio_urls.filter(u => u.trim()),
        buyer_requirements: listingForm.buyer_requirements.filter(r => r.trim()),
        tags: listingForm.tags.split(',').map(t => t.trim()).filter(t => t),
        is_active: listingForm.is_active,
        price_cents: Math.round(listingForm.base_price_dollars * 100)
      };

      // Add 3-tier packages structure
      if (listingForm.packages && listingForm.packages.length === 3) {
        formData.packages = listingForm.packages.map(pkg => ({
          tier: pkg.tier,
          display_name: pkg.display_name,
          short_description: pkg.short_description,
          deliverables: pkg.deliverables.filter(d => d.trim()),
          price_cents: Math.round((pkg.price_dollars || 0) * 100), // Convert dollars to cents
          delivery_days: pkg.delivery_days,
          revisions: pkg.revisions,
          bundle_qty: pkg.bundle_qty,
          enabled: pkg.enabled
        }));
      }

      let listingResponse;
      if (editingListing) {
        listingResponse = await axios.put(`${API}/listings/${editingListing.id}`, formData);
        toast.success('Listing updated successfully');
      } else {
        listingResponse = await axios.post(`${API}/listings`, formData);
        toast.success('Listing created successfully');
      }

      // Handle FAQ submission
      const listingId = editingListing?.id || listingResponse.data.id;
      
      if (listingForm.faqs && listingForm.faqs.length > 0) {
        try {
          // Delete existing FAQs for the listing
          await axios.delete(`${API}/listings/${listingId}/faqs`);
          
          // Create new FAQs
          for (const faq of listingForm.faqs) {
            await axios.post(`${API}/listings/${listingId}/faqs`, {
              question: faq.question,
              answer: faq.answer,
              sort_order: faq.sort_order
            });
          }
        } catch (faqError) {
          console.error('FAQ save error:', faqError);
          toast.error('Listing saved, but FAQ updates failed');
        }
      }

      setCreateModalOpen(false);
      setEditingListing(null);
      resetWizard();
      fetchData();
    } catch (error) {
      console.error('Listing save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save listing');
    }
  };

  useEffect(() => {
    document.title = 'Seller Dashboard | Rnkem';
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [listingsRes, ordersRes, profileRes, stripeRes] = await Promise.all([
        axios.get(`${API}/listings/my`),
        axios.get(`${API}/orders/my`),
        axios.get(`${API}/profiles/me`),
        axios.get(`${API}/stripe/connect/account-status`).catch(() => null) // Don't fail if Stripe not available
      ]);
      setListings(listingsRes.data);
      setOrders(ordersRes.data);
      setProfile(profileRes.data);
      if (stripeRes) {
        setStripeStatus(stripeRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const resetWizard = () => {
    setWizardStep(1);
    setListingForm({
      title: '',
      description: '',
      categories: [],
      subcategories: [],
      pricing_model: 'per_lead',
      base_price_dollars: 25.00,
      delivery_sla_days: 3,
      packages: [
        {
          tier: 'good',
          display_name: 'Good Package',
          short_description: 'Essential service package',
          deliverables: ['Basic deliverable'],
          price_cents: 2500,
          delivery_days: 3,
          revisions: 0,
          bundle_qty: null,
          enabled: true
        },
        {
          tier: 'better',
          display_name: 'Better Package',
          short_description: 'Enhanced service package',
          deliverables: ['Enhanced deliverable', 'Additional feature'],
          price_cents: 2500,
          delivery_days: 2,
          revisions: 1,
          bundle_qty: null,
          enabled: true
        },
        {
          tier: 'best',
          display_name: 'Best Package',
          short_description: 'Premium service package',
          deliverables: ['Premium deliverable', 'Priority support', 'Extra features'],
          price_cents: 2500,
          delivery_days: 1,
          revisions: 2,
          bundle_qty: null,
          enabled: true
        }
      ],
      faqs: [],
      buyer_requirements: [''],
      highlights: [''],
      portfolio_urls: [''],
      exclusivity: 'shared',
      min_qty: 1,
      tags: '',
      is_active: true
    });
  };

  const handleEditListing = async (listing) => {
    setEditingListing(listing);
    setWizardStep(1);
    
    // Load FAQs for the listing
    let faqs = [];
    try {
      const faqResponse = await axios.get(`${API}/listings/${listing.id}/faqs`);
      faqs = faqResponse.data;
    } catch (error) {
      console.error('Failed to load FAQs:', error);
    }
    
    setListingForm({
      title: listing.title || '',
      description: listing.description || '',
      categories: [listing.service_category] || [],
      subcategories: listing.subcategory ? [listing.subcategory] : [],
      pricing_model: listing.pricing_model || 'per_lead',
      base_price_dollars: (listing.price_cents / 100) || 25.00,
      delivery_sla_days: Math.round((listing.delivery_sla_hours || 24) / 24),
      packages: listing.packages?.length === 3 ? listing.packages.map(pkg => ({
        ...pkg,
        price_dollars: (pkg.price_cents || pkg.price_dollars * 100 || 2500) / 100 // Convert cents to dollars for form
      })) : [
        {
          tier: 'good',
          display_name: 'Good Package',
          short_description: 'Essential service package',
          deliverables: ['Basic deliverable'],
          price_cents: listing.price_cents || 2500,
          delivery_days: Math.round((listing.delivery_sla_hours || 72) / 24),
          revisions: 0,
          bundle_qty: listing.pricing_model === 'per_lead' ? (listing.min_qty || 5) : null,
          enabled: true
        },
        {
          tier: 'better',
          display_name: 'Better Package',
          short_description: 'Enhanced service package',
          deliverables: ['Enhanced deliverable', 'Additional feature'],
          price_cents: listing.price_cents || 2500,
          delivery_days: Math.max(1, Math.round((listing.delivery_sla_hours || 72) / 24) - 1),
          revisions: 1,
          bundle_qty: listing.pricing_model === 'per_lead' ? (listing.min_qty || 5) * 2 : null,
          enabled: true
        },
        {
          tier: 'best',
          display_name: 'Best Package',
          short_description: 'Premium service package',
          deliverables: ['Premium deliverable', 'Priority support', 'Extra features'],
          price_cents: listing.price_cents || 2500,
          delivery_days: Math.max(1, Math.round((listing.delivery_sla_hours || 72) / 24) - 2),
          revisions: 2,
          bundle_qty: listing.pricing_model === 'per_lead' ? (listing.min_qty || 5) * 3 : null,
          enabled: true
        }
      ],
      faqs: faqs,
      buyer_requirements: listing.buyer_requirements?.length ? listing.buyer_requirements : [''],
      highlights: listing.highlights?.length ? listing.highlights : [''],
      portfolio_urls: listing.portfolio_urls?.length ? listing.portfolio_urls : [''],
      exclusivity: listing.exclusivity || 'shared',
      min_qty: listing.min_qty || 1,
      tags: listing.tags ? listing.tags.join(', ') : '',
      is_active: listing.is_active !== false
    });
    setCreateModalOpen(true);
  };

  const handleDeleteListing = async (listingId) => {
    if (window.confirm('Are you sure you want to delete this listing?')) {
      try {
        await axios.delete(`${API}/listings/${listingId}`);
        toast.success('Listing deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete listing');
      }
    }
  };

  const handleToggleActive = async (listing) => {
    // Check approval status before activating listing
    if (!listing.is_active && profile?.approval_status !== 'approved') {
      toast.error('Your profile must be approved before you can publish listings');
      return;
    }
    
    try {
      await axios.put(`${API}/listings/${listing.id}`, {
        is_active: !listing.is_active
      });
      toast.success(`Listing ${listing.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update listing');
    }
  };

  const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;

  if (loading) {
    return <div className="min-h-screen bg-slate-50"><Navigation /><div className="flex items-center justify-center py-12">Loading...</div></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Seller Dashboard</h1>
          <div className="flex gap-3">
            <Link to="/sell/analytics">
              <Button 
                variant="outline" 
                className="border-2"
                style={{ 
                  borderColor: 'var(--border-strong)',
                  color: 'var(--steel-700)'
                }}
              >
                Analytics
              </Button>
            </Link>
            <Link to="/profile/edit">
              <Button 
                variant="outline" 
                className="border-2"
                style={{ 
                  borderColor: 'var(--border-strong)',
                  color: 'var(--steel-700)'
                }}
              >
                Edit Profile
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Account Review Message for Non-Approved Sellers */}
        {profile && profile.approval_status !== 'approved' && (
          <div className="mb-6">
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Your account is being reviewed
                    </h3>
                    <p className="text-gray-600">
                      Once approved you will be able to create your first listing! Approvals typically take less than 12 hours.
                    </p>
                    {profile.approval_status === 'draft' && (
                      <div className="mt-3">
                        <Link to="/profile/edit">
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                            Complete Profile Submission
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Stripe Onboarding Banner */}
        {stripeStatus && !stripeStatus.stripe_onboarded && (
          <div className="mb-6">
            <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Finish Stripe payout setup to receive funds
                    </h3>
                    <p className="text-gray-600">
                      Complete your payout information to start receiving payments from your services.
                    </p>
                    <div className="mt-3">
                      <Link to="/sell/payouts">
                        <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700">
                          Complete Payout Setup
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Verification Status Card */}
        <div className="mb-6">
          <VerificationStatusCard />
        </div>
        
        <Tabs defaultValue="listings" className="space-y-6">
          <TabsList>
            <TabsTrigger value="listings">My Listings</TabsTrigger>
            <TabsTrigger value="orders">Orders</TabsTrigger>
            <TabsTrigger value="points">Points History</TabsTrigger>
          </TabsList>
          
          <TabsContent value="listings">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>My Listings</CardTitle>
                  <Button 
                    onClick={() => {resetWizard(); setCreateModalOpen(true);}} 
                    className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                    disabled={profile?.approval_status !== 'approved'}
                    title={profile?.approval_status !== 'approved' ? 'Your profile must be approved before you can post listings' : ''}
                  >
                    Create Listing
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {listings.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    You haven't created any listings yet.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Pricing</TableHead>
                        <TableHead>Delivery</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {listings.map((listing) => (
                        <TableRow key={listing.id}>
                          <TableCell className="font-medium">{listing.title}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {formatCategoryName(listing.service_category || 'N/A')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div>
                              {formatPrice(listing.price_cents)}
                              <div className="text-sm text-gray-500">
                                {formatPricingModel(listing.pricing_model || 'fixed')}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{Math.round((listing.delivery_sla_hours || 24) / 24)}d</TableCell>
                          <TableCell>
                            <Badge className={listing.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                              {listing.is_active !== false ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleEditListing(listing);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleToggleActive(listing)}
                                disabled={!listing.is_active && profile?.approval_status !== 'approved'}
                                title={!listing.is_active && profile?.approval_status !== 'approved' ? 'Your profile must be approved before you can publish listings' : ''}
                                className={!listing.is_active && profile?.approval_status !== 'approved' ? 'disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed' : ''}
                              >
                                {listing.is_active !== false ? 'Deactivate' : 'Activate'}
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteListing(listing.id)}
                              >
                                Delete
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle>Orders</CardTitle>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No orders yet.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Order ID</TableHead>
                        <TableHead>Listing</TableHead>
                        <TableHead>Buyer</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell className="font-mono text-xs">
                            <Link to={`/orders/${order.id}`} className="text-blue-600 hover:underline">
                              {order.id.slice(-8)}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{order.listing ? order.listing.title : 'N/A'}</div>
                              {order.package_name && (
                                <Badge variant="outline" className="text-xs mt-1">
                                  {order.package_name}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {order.rnker ? order.rnker.name : 'N/A'}
                          </TableCell>
                          <TableCell>{order.qty}</TableCell>
                          <TableCell>{formatPrice(order.price_cents_total)}</TableCell>
                          <TableCell>
                            <Badge 
                              className={
                                order.status === 'completed' ? 'bg-green-100 text-green-800' :
                                order.status === 'delivered' ? 'bg-blue-100 text-blue-800' :
                                order.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }
                            >
                              {order.status || 'pending'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {new Date(order.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              asChild
                            >
                              <Link to={`/orders/${order.id}`}>
                                View
                              </Link>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="points">
            <Card>
              <CardHeader>
                <CardTitle>Points History</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Track your points earned from orders, reorders, and reviews
                </p>
              </CardHeader>
              <CardContent>
                <PointsHistory />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Enhanced 4-Step Listing Wizard */}
        <ListingWizard
          isOpen={createModalOpen}
          onClose={() => {
            setCreateModalOpen(false);
            setEditingListing(null);
            resetWizard();
          }}
          editingListing={editingListing}
          listingForm={listingForm}
          setListingForm={setListingForm}
          onSubmit={handleCreateListing}
          wizardStep={wizardStep}
          setWizardStep={setWizardStep}
        />
      </div>
    </div>
  );
};

// Seller Payouts Page - Stripe Connect Express
const SellerPayoutsPage = () => {
  const [stripeStatus, setStripeStatus] = useState({
    account_id: null,
    stripe_onboarded: false,
    stripe_payouts_enabled: false,
    stripe_charges_enabled: false,
    stripe_requirements_due: [],
    last_sync: null
  });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [stripeMode, setStripeMode] = useState('test');
  
  const { user } = useAuth();

  useEffect(() => {
    document.title = 'Payouts | Rnkem';
    fetchStripeSettings();
    fetchStripeStatus();
  }, []);

  // Check for returning from Stripe onboarding
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('account_id')) {
      // User returned from Stripe onboarding, sync status
      syncStripeStatus();
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchStripeSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/stripe`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStripeMode(data.stripe_mode || 'test');
      }
    } catch (error) {
      console.error('Error fetching Stripe settings:', error);
    }
  };

  const fetchStripeStatus = async () => {
    try {
      const response = await fetch(`${API}/stripe/connect/account-status`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStripeStatus(data);
      } else {
        console.error('Failed to fetch Stripe status');
      }
    } catch (error) {
      console.error('Error fetching Stripe status:', error);
    } finally {
      setLoading(false);
    }
  };

  const createStripeAccount = async () => {
    setCreating(true);
    try {
      const response = await fetch(`${API}/stripe/connect/create-account`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Stripe account response:', data); // Debug logging
        
        if (data.onboarding_url) {
          // Redirect to Stripe onboarding
          toast.success('Redirecting to Stripe to complete your setup...');
          window.location.href = data.onboarding_url;
        } else {
          // Account already exists, update status and provide feedback
          setStripeStatus(data);
          
          if (data.stripe_requirements_due && data.stripe_requirements_due.length > 0) {
            toast.error('Unable to generate Stripe link. Please try again or contact support if the issue persists.');
          } else if (data.stripe_onboarded) {
            toast.success('Stripe account is fully set up!');
          } else {
            toast.info('Account status refreshed.');
          }
        }
      } else {
        const errorData = await response.json();
        console.error('Stripe account creation error:', errorData);
        toast.error(errorData.detail || 'Failed to create Stripe account');
      }
    } catch (error) {
      console.error('Error creating Stripe account:', error);
      toast.error('Failed to create Stripe account');
    } finally {
      setCreating(false);
    }
  };

  const syncStripeStatus = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API}/stripe/connect/sync-status`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStripeStatus(data);
        if (data.stripe_onboarded) {
          toast.success('Stripe account successfully connected!');
        }
      } else {
        toast.error('Failed to sync Stripe status');
      }
    } catch (error) {
      console.error('Error syncing Stripe status:', error);
      toast.error('Failed to sync Stripe status');
    } finally {
      setSyncing(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const response = await fetch(`${API}/stripe/express-login`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        window.open(data.url, '_blank', 'noopener');
      } else if (response.status === 429) {
        toast.error("You've requested too many dashboard links. Try again soon.");
      } else {
        toast.error("Couldn't open the Stripe dashboard. Please try again.");
      }
    } catch (error) {
      console.error('Error opening Stripe dashboard:', error);
      toast.error("Couldn't open the Stripe dashboard. Please try again.");
    }
  };

  const formatRequirements = (requirements) => {
    if (!requirements || requirements.length === 0) {
      return 'None';
    }
    
    return requirements.map(req => 
      req.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    ).join(', ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Payouts</h1>
              <p className="text-gray-600">Manage your Stripe payout settings</p>
            </div>
            {stripeMode === 'test' && (
              <span className="px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                TEST MODE
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!stripeStatus.account_id ? (
          /* No Stripe Account - Setup Flow */
          <Card className="p-8 text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 0h4a2 2 0 002-2v-4m-6 0l3-3m0 0l3 3m-3-3v12" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Set up payouts with Stripe</h2>
              <p className="text-gray-600 mb-6">
                Connect your bank account to receive payments from your services. 
                This process is secure and handled entirely by Stripe.
              </p>
            </div>
            
            <Button 
              onClick={createStripeAccount}
              disabled={creating}
              className="bg-blue-600 hover:bg-blue-700 px-8 py-3 text-lg"
            >
              {creating ? 'Creating Account...' : 'Set up payouts with Stripe'}
            </Button>
            
            <div className="mt-6 text-sm text-gray-500">
              <p>You'll be redirected to Stripe to complete the setup process.</p>
            </div>
          </Card>
        ) : (
          /* Has Stripe Account - Status Panel */
          <div className="space-y-6">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold">Stripe Account Status</h2>
                <Button
                  onClick={syncStripeStatus}
                  disabled={syncing}
                  variant="outline"
                  size="sm"
                >
                  {syncing ? 'Syncing...' : 'Refresh Status'}
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payouts Enabled
                  </label>
                  <div className="flex items-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      stripeStatus.stripe_payouts_enabled 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {stripeStatus.stripe_payouts_enabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Charges Enabled
                  </label>
                  <div className="flex items-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      stripeStatus.stripe_charges_enabled 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {stripeStatus.stripe_charges_enabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Requirements Due
                </label>
                <p className="text-sm text-gray-600">
                  {formatRequirements(stripeStatus.stripe_requirements_due)}
                </p>
              </div>

              {stripeStatus.last_sync && (
                <div className="mt-4 text-xs text-gray-500">
                  Last updated: {new Date(stripeStatus.last_sync).toLocaleString()}
                </div>
              )}
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={createStripeAccount} disabled={creating} variant="outline">
                {creating ? 'Updating...' : 'Update payout info'}
              </Button>
              
              <Button onClick={openStripeDashboard} className="bg-blue-600 hover:bg-blue-700">
                Open Stripe Dashboard (Express)
              </Button>
            </div>

            {/* Requirements Notice */}
            {stripeStatus.stripe_requirements_due && stripeStatus.stripe_requirements_due.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Action Required: Complete Verification
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p className="mb-2">
                        <strong>Missing:</strong> {formatRequirements(stripeStatus.stripe_requirements_due)}
                      </p>
                      <p>
                        You need to upload verification documents to receive payouts. 
                        Click "Update payout info" to continue setup in Stripe.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Buyer Orders Page (unchanged from original)
const BuyerOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    document.title = 'My Orders | Rnkem';
  }, []);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders/my`);
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = async (order) => {
    try {
      await axios.post(`${API}/orders`, {
        listing_id: order.listing_id,
        qty: order.qty
      });
      toast.success('Reorder created successfully!');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create reorder');
    }
  };

  const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;

  if (loading) {
    return <div className="min-h-screen bg-slate-50"><Navigation /><div className="flex items-center justify-center py-12">Loading...</div></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-8">My Orders</h1>
        
        <Card>
          <CardContent className="pt-6">
            {orders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                You haven't placed any orders yet.
                <div className="mt-4">
                  <Link to="/browse">
                    <Button className="bg-amber-600 hover:bg-amber-700">
                      Browse Listings
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order ID</TableHead>
                    <TableHead>Listing</TableHead>
                    <TableHead>Seller</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell className="font-mono text-xs">
                        <Link to={`/orders/${order.id}`} className="text-blue-600 hover:underline">
                          {order.id.slice(-8)}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">
                            {order.listing ? order.listing.title : 'N/A'}
                          </p>
                          {order.listing && (
                            <Link to={`/listing/${order.listing.id}`} className="text-xs text-amber-600 hover:underline">
                              View Listing
                            </Link>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{order.rnkee ? order.rnkee.name : 'N/A'}</p>
                          <p className="text-xs text-gray-500">{order.rnkee ? order.rnkee.company : ''}</p>
                        </div>
                      </TableCell>
                      <TableCell>{order.qty}</TableCell>
                      <TableCell className="font-semibold">{formatPrice(order.price_cents_total)}</TableCell>
                      <TableCell>
                        <Badge 
                          className={
                            order.status === 'completed' ? 'bg-green-100 text-green-800' :
                            order.status === 'delivered' ? 'bg-blue-100 text-blue-800' :
                            order.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }
                        >
                          {order.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(order.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Link to={`/orders/${order.id}`}>
                            <Button size="sm" variant="outline">
                              View
                            </Button>
                          </Link>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReorder(order)}
                            disabled={!order.listing || !order.listing.is_active}
                          >
                            Reorder
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Profile Editing Panel - Comprehensive Fiverr-like profile editing for Settings page
const ProfileEditingPanel = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    bio: '',
    categories: [],
    portfolio_urls: [],
    skills: [],
    languages: [{ language: 'English', proficiency: 'Native' }],
    response_time: 'within 24 hours',
    website_url: '',
    linkedin_url: '',
    certifications: [],
    education: [],
    online_courses: []
  });

  useEffect(() => {
    if (user && user.id) {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profiles/${user.id}`);
      const data = response.data;
      setProfile({
        bio: data.bio || '',
        categories: data.categories || [],
        portfolio_urls: data.portfolio_urls || [],
        skills: data.skills || [],
        languages: data.languages || [{ language: 'English', proficiency: 'Native' }],
        response_time: data.response_time || 'within 24 hours',
        website_url: data.website_url || '',
        linkedin_url: data.linkedin_url || '',
        certifications: data.certifications || [],
        education: data.education || [],
        online_courses: data.online_courses || []
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/profiles/me`, profile);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleArrayFieldChange = (field, index, value, subfield = null) => {
    const newArray = [...profile[field]];
    if (subfield) {
      newArray[index] = { ...newArray[index], [subfield]: value };
    } else {
      newArray[index] = value;
    }
    setProfile({ ...profile, [field]: newArray });
  };

  const addArrayItem = (field, defaultValue = '') => {
    setProfile({ ...profile, [field]: [...profile[field], defaultValue] });
  };

  const removeArrayItem = (field, index) => {
    const newArray = profile[field].filter((_, i) => i !== index);
    setProfile({ ...profile, [field]: newArray });
  };

  const availableCategories = Object.keys(getServiceCategories());
  const responseTimeOptions = [
    'within 1 hour', 'within 2 hours', 'within 6 hours', 'within 12 hours',
    'within 24 hours', 'within 2 days', 'within 3 days'
  ];
  const proficiencyLevels = ['Basic', 'Conversational', 'Fluent', 'Native'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p>Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: 'var(--brand-navy)' }}>
            Professional Profile
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--steel-500)' }}>
            Create a compelling profile to attract more customers like Fiverr sellers.
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary font-semibold px-6"
          style={{ backgroundColor: 'var(--accent-orange)' }}
        >
          {saving ? 'Saving...' : 'Save Profile'}
        </Button>
      </div>

      {/* About Me */}
      <Card className="card-modern">
        <CardHeader>
          <CardTitle style={{ color: 'var(--brand-navy)' }}>About Me</CardTitle>
          <CardDescription>
            Write a compelling description showcasing your expertise and what makes you unique.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="I'm a professional [your field] with [X years] of experience specializing in [your areas of expertise]. I help clients achieve [key value proposition]..."
            value={profile.bio}
            onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
            rows={5}
            className="text-sm resize-none"
          />
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs" style={{ color: 'var(--steel-500)' }}>
              {profile.bio.length}/1200 characters
            </p>
            <p className="text-xs" style={{ color: 'var(--steel-500)' }}>
              💡 Tip: Mention experience, specializations, and unique value
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Service Categories */}
      <Card className="card-modern">
        <CardHeader>
          <CardTitle style={{ color: 'var(--brand-navy)' }}>Service Categories</CardTitle>
          <CardDescription>Select categories that best describe your services.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {availableCategories.map(category => (
              <label key={category} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={profile.categories.includes(category)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setProfile({ ...profile, categories: [...profile.categories, category] });
                    } else {
                      setProfile({ ...profile, categories: profile.categories.filter(c => c !== category) });
                    }
                  }}
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium">{formatCategoryName(category)}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Skills */}
      <Card className="card-modern">
        <CardHeader>
          <CardTitle style={{ color: 'var(--brand-navy)' }}>Skills & Expertise</CardTitle>
          <CardDescription>Add specific skills clients search for. Use keywords related to your services.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {profile.skills.map((skill, index) => (
            <div key={index} className="flex gap-2">
              <Input
                placeholder="e.g., Social Media Marketing, Lead Generation, Content Creation..."
                value={skill}
                onChange={(e) => handleArrayFieldChange('skills', index, e.target.value)}
                className="flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => removeArrayItem('skills', index)}
                className="text-red-600 hover:text-red-700 px-3"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            onClick={() => addArrayItem('skills')}
            className="w-full border-dashed"
            style={{ borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)' }}
          >
            + Add Skill
          </Button>
        </CardContent>
      </Card>

      {/* Languages */}
      <Card className="card-modern">
        <CardHeader>
          <CardTitle style={{ color: 'var(--brand-navy)' }}>Languages</CardTitle>
          <CardDescription>List languages you speak and proficiency levels.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {profile.languages.map((langObj, index) => (
            <div key={index} className="flex gap-2">
              <Input
                placeholder="Language (e.g., English, Spanish, French)"
                value={typeof langObj === 'object' ? langObj.language : langObj}
                onChange={(e) => handleArrayFieldChange('languages', index, e.target.value, 'language')}
                className="flex-1"
              />
              <Select
                value={typeof langObj === 'object' ? langObj.proficiency : 'Native'}
                onValueChange={(value) => handleArrayFieldChange('languages', index, value, 'proficiency')}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {proficiencyLevels.map(level => (
                    <SelectItem key={level} value={level}>{level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {profile.languages.length > 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeArrayItem('languages', index)}
                  className="text-red-600 hover:text-red-700 px-3"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          <Button
            variant="outline"
            onClick={() => addArrayItem('languages', { language: '', proficiency: 'Conversational' })}
            className="w-full border-dashed"
            style={{ borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)' }}
          >
            + Add Language
          </Button>
        </CardContent>
      </Card>

      {/* Education */}
      <Card className="card-modern">
        <CardHeader>
          <CardTitle style={{ color: 'var(--brand-navy)' }}>Education</CardTitle>
          <CardDescription>Add educational background to build credibility.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {profile.education.map((edu, index) => (
            <div key={index} className="p-4 border rounded-lg space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Institution name"
                  value={edu.institution || ''}
                  onChange={(e) => handleArrayFieldChange('education', index, e.target.value, 'institution')}
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeArrayItem('education', index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <Input
                placeholder="Degree/Program"
                value={edu.degree || ''}
                onChange={(e) => handleArrayFieldChange('education', index, e.target.value, 'degree')}
              />
              <Input
                placeholder="Year graduated (e.g., 2020)"
                value={edu.year || ''}
                onChange={(e) => handleArrayFieldChange('education', index, e.target.value, 'year')}
              />
            </div>
          ))}
          <Button
            variant="outline"
            onClick={() => addArrayItem('education', { institution: '', degree: '', year: '' })}
            className="w-full border-dashed"
            style={{ borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)' }}
          >
            + Add Education
          </Button>
        </CardContent>
      </Card>

      {/* Portfolio & Other Professional Details */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Portfolio */}
        <Card className="card-modern">
          <CardHeader>
            <CardTitle style={{ color: 'var(--brand-navy)' }}>Portfolio URLs</CardTitle>
            <CardDescription>Showcase your work with links to portfolio or case studies.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {profile.portfolio_urls.map((url, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  placeholder="https://example.com/portfolio"
                  value={url}
                  onChange={(e) => handleArrayFieldChange('portfolio_urls', index, e.target.value)}
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeArrayItem('portfolio_urls', index)}
                  className="text-red-600 hover:text-red-700 px-3"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              onClick={() => addArrayItem('portfolio_urls')}
              className="w-full border-dashed"
              style={{ borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)' }}
            >
              + Add Portfolio URL
            </Button>
          </CardContent>
        </Card>

        {/* Professional Details */}
        <Card className="card-modern">
          <CardHeader>
            <CardTitle style={{ color: 'var(--brand-navy)' }}>Professional Details</CardTitle>
            <CardDescription>Additional information for clients.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-semibold mb-2 block">Response Time</Label>
              <Select
                value={profile.response_time}
                onValueChange={(value) => setProfile({ ...profile, response_time: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {responseTimeOptions.map(option => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Business Website</Label>
              <Input
                placeholder="https://your-website.com"
                value={profile.website_url}
                onChange={(e) => setProfile({ ...profile, website_url: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">LinkedIn Profile</Label>
              <Input
                placeholder="https://linkedin.com/in/your-profile"
                value={profile.linkedin_url}
                onChange={(e) => setProfile({ ...profile, linkedin_url: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Settings Page (unchanged from original)
const SettingsPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    location: '',
    role: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, message: '', color: 'text-gray-400' });
  
  useEffect(() => {
    document.title = 'Settings | Rnkem';
  }, []);

  // Check for force password reset or password tab parameter
  useEffect(() => {
    const tab = searchParams.get('tab');
    const force = searchParams.get('force');
    
    if (tab === 'password' || force === 'true') {
      setShowPasswordSection(true);
      if (force === 'true') {
        // Show additional message for forced password reset
        setTimeout(() => {
          toast.info('Please change your password to continue using the system.');
        }, 500);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        company: user.company || '',
        phone: user.phone || '',
        location: user.location || '',
        role: user.role || ''
      });
    }
  }, [user]);

  // Password strength calculator
  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, message: 'Enter a password', color: 'text-gray-400' };
    
    let score = 0;
    let message = '';
    let color = 'text-red-500';
    
    if (password.length >= 10) score += 1;
    if (password.length >= 20) {
      // Allow long passphrases
      return { score: 4, message: 'Strong passphrase', color: 'text-green-600' };
    }
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[^a-zA-Z0-9]/.test(password)) score += 1;
    
    if (password.length < 10) {
      message = 'Too short (min 10 characters)';
      color = 'text-red-500';
    } else if (score <= 2) {
      message = 'Weak - add uppercase, lowercase, and numbers';
      color = 'text-red-500';
    } else if (score === 3) {
      message = 'Good - meets requirements';
      color = 'text-yellow-500';
    } else {
      message = 'Strong password';
      color = 'text-green-600';
    }
    
    return { score, message, color };
  };

  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(passwordData.new_password));
  }, [passwordData.new_password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/users/me`, formData);
      toast.success('Settings updated successfully!');
      // Refresh user data
      window.location.reload();
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    
    try {
      // Validate passwords match
      if (passwordData.new_password !== passwordData.confirm_password) {
        toast.error('New passwords do not match');
        return;
      }
      
      // Validate password strength
      if (passwordStrength.score < 2 && passwordData.new_password.length < 20) {
        toast.error('Password does not meet requirements');
        return;
      }
      
      await axios.post(`${API}/change-password`, passwordData);
      
      toast.success('Password updated successfully! You\'ve been signed out on other devices.');
      
      // Clear form
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
      // If this was a forced password reset, redirect to appropriate page after success
      const force = searchParams.get('force');
      if (force === 'true') {
        setTimeout(() => {
          const userRole = user?.role;
          const redirectPath = userRole === 'admin' || userRole === 'gm' ? '/admin' : '/browse';
          window.location.href = redirectPath;
        }, 2000);
      }
      
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Too many password change attempts. Please wait a minute and try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to update password');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
          {user.role === 'rnkee' && (
            <Link to="/profile/edit">
              <Button 
                className="btn-primary font-semibold"
                style={{ backgroundColor: 'var(--accent-orange)' }}
              >
                ✏️ Edit Professional Profile
              </Button>
            </Link>
          )}
        </div>
        
        <Tabs value="account" className="space-y-6">
          <TabsList>
            <TabsTrigger value="account">Account Settings</TabsTrigger>
            {user.role === 'rnkee' && (
              <TabsTrigger value="profile">Professional Profile</TabsTrigger>
            )}
          </TabsList>
          
          <TabsContent value="account">
            <Card>
              <CardHeader>
                <CardTitle>Account Information</CardTitle>
                <CardDescription>Update your account details</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="role">Role</Label>
                    <select
                      id="role"
                      value={formData.role}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                    >
                      <option value="rnkee">Service Provider (Seller)</option>
                      <option value="rnker">Contractor (Buyer)</option>
                      <option value="admin">Administrator</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="company">Company</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                    />
                  </div>
                  
                  <Button type="submit" disabled={loading} className="bg-amber-600 hover:bg-amber-700">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </form>
              </CardContent>
            </Card>
            
            {/* Password Section - Collapsible */}
            <div className="mt-6">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Password & Security</CardTitle>
                      <CardDescription className="text-sm text-gray-600">
                        Manage your account password and security settings
                      </CardDescription>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowPasswordSection(!showPasswordSection);
                        // Reset form when closing
                        if (showPasswordSection) {
                          setPasswordData({
                            current_password: '',
                            new_password: '',
                            confirm_password: ''
                          });
                        }
                      }}
                      className="text-sm"
                    >
                      {showPasswordSection ? 'Cancel' : 'Change Password'}
                    </Button>
                  </div>
                </CardHeader>
                
                {!showPasswordSection && (
                  <CardContent className="pt-0">
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>Password last changed: {user?.last_password_change_at ? new Date(user.last_password_change_at).toLocaleDateString() : 'Never'}</span>
                    </div>
                  </CardContent>
                )}

                {showPasswordSection && (
                  <CardContent className="pt-0">
                    <form onSubmit={handlePasswordChange} className="space-y-6">
                      {/* Current Password */}
                      <div>
                        <Label htmlFor="current_password" className="text-sm font-medium">Current Password</Label>
                        <div className="relative mt-2">
                          <Input
                            id="current_password"
                            type={showCurrentPassword ? "text" : "password"}
                            value={passwordData.current_password}
                            onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                            placeholder="Enter your current password"
                            className="pr-10"
                            required
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          >
                            {showCurrentPassword ? "👁️‍🗨️" : "👁️"}
                          </Button>
                        </div>
                      </div>
                      
                      {/* New Password */}
                      <div>
                        <Label htmlFor="new_password" className="text-sm font-medium">New Password</Label>
                        <div className="relative mt-2">
                          <Input
                            id="new_password"
                            type={showNewPassword ? "text" : "password"}
                            value={passwordData.new_password}
                            onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                            placeholder="Enter your new password"
                            className="pr-10"
                            required
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          >
                            {showNewPassword ? "👁️‍🗨️" : "👁️"}
                          </Button>
                        </div>
                        
                        {/* Password Strength Meter */}
                        {passwordData.new_password && (
                          <div className="mt-3 space-y-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div 
                                  className={`h-2 rounded-full transition-all duration-300 ${
                                    passwordStrength.score <= 1 ? 'bg-red-500 w-1/4' :
                                    passwordStrength.score === 2 ? 'bg-yellow-500 w-2/4' :
                                    passwordStrength.score === 3 ? 'bg-blue-500 w-3/4' :
                                    'bg-green-500 w-full'
                                  }`}
                                ></div>
                              </div>
                              <span className={`text-sm font-medium ${passwordStrength.color} min-w-8`}>
                                {passwordStrength.score}/4
                              </span>
                            </div>
                            <p className={`text-sm ${passwordStrength.color}`}>
                              {passwordStrength.message}
                            </p>
                          </div>
                        )}
                        
                        {/* Password Policy Hints */}
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                          <p className="text-sm font-medium text-gray-700 mb-3">Password Requirements:</p>
                          <div className="grid grid-cols-1 gap-2 text-xs">
                            <div className={`flex items-center gap-2 ${passwordData.new_password.length >= 10 ? 'text-green-600' : 'text-gray-500'}`}>
                              <span className={`text-xs ${passwordData.new_password.length >= 10 ? 'text-green-600' : 'text-gray-400'}`}>✓</span>
                              <span>At least 10 characters</span>
                            </div>
                            <div className={`flex items-center gap-2 ${/[a-z]/.test(passwordData.new_password) ? 'text-green-600' : 'text-gray-500'}`}>
                              <span className={`text-xs ${/[a-z]/.test(passwordData.new_password) ? 'text-green-600' : 'text-gray-400'}`}>✓</span>
                              <span>Lowercase letter</span>
                            </div>
                            <div className={`flex items-center gap-2 ${/[A-Z]/.test(passwordData.new_password) ? 'text-green-600' : 'text-gray-500'}`}>
                              <span className={`text-xs ${/[A-Z]/.test(passwordData.new_password) ? 'text-green-600' : 'text-gray-400'}`}>✓</span>
                              <span>Uppercase letter</span>
                            </div>
                            <div className={`flex items-center gap-2 ${/\d/.test(passwordData.new_password) ? 'text-green-600' : 'text-gray-500'}`}>
                              <span className={`text-xs ${/\d/.test(passwordData.new_password) ? 'text-green-600' : 'text-gray-400'}`}>✓</span>
                              <span>Number</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-500 pt-1 border-t border-gray-200">
                              <span className="text-xs text-blue-500">ℹ️</span>
                              <span className="italic">OR use a passphrase with 20+ characters</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Confirm Password */}
                      <div>
                        <Label htmlFor="confirm_password" className="text-sm font-medium">Confirm New Password</Label>
                        <div className="relative mt-2">
                          <Input
                            id="confirm_password"
                            type={showConfirmPassword ? "text" : "password"}
                            value={passwordData.confirm_password}
                            onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                            placeholder="Confirm your new password"
                            className="pr-10"
                            required
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? "👁️‍🗨️" : "👁️"}
                          </Button>
                        </div>
                        {passwordData.confirm_password && passwordData.new_password !== passwordData.confirm_password && (
                          <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                            <span className="text-xs">⚠️</span>
                            Passwords do not match
                          </p>
                        )}
                      </div>
                      
                      <div className="flex gap-3 pt-4 border-t border-gray-200">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowPasswordSection(false);
                            setPasswordData({
                              current_password: '',
                              new_password: '',
                              confirm_password: ''
                            });
                          }}
                          disabled={passwordLoading}
                        >
                          Cancel
                        </Button>
                        <Button 
                          type="submit" 
                          disabled={
                            passwordLoading || 
                            !passwordData.current_password || 
                            !passwordData.new_password || 
                            !passwordData.confirm_password ||
                            passwordData.new_password !== passwordData.confirm_password ||
                            (passwordStrength.score < 2 && passwordData.new_password.length < 20)
                          } 
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          {passwordLoading ? 'Updating Password...' : 'Update Password'}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                )}
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="profile">
            <ProfileEditingPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Seller Profile Page
const SellerProfilePage = () => {
  const { id } = useParams();
  const [seller, setSeller] = useState(null);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState({
    completed_orders_count: 0,
    avg_sla_hours: 0,
    starting_from_price: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Filter states for seller's listings
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState({
    service_category: searchParams.get('service_category') || '',
    service_subcategory: searchParams.get('service_subcategory') || '',
    pricing_model: searchParams.get('pricing_model') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || ''
  });
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'best_match');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);

  useEffect(() => {
    fetchSellerData();
  }, [id]);

  useEffect(() => {
    if (seller) {
      fetchSellerListings();
    }
  }, [seller, searchTerm, filters, sortBy, currentPage]);

  useEffect(() => {
    updateSearchParams();
  }, [searchTerm, filters, sortBy, currentPage]);

  const fetchSellerData = async () => {
    try {
      setLoading(true);
      
      // Fetch seller user data and profile in parallel, but handle errors gracefully
      const [sellerResult, profileResult] = await Promise.allSettled([
        axios.get(`${API}/users/${id}`),
        axios.get(`${API}/profiles/${id}`)
      ]);
      
      if (sellerResult.status === 'fulfilled') {
        setSeller(sellerResult.value.data);
      } else {
        console.error('Failed to fetch seller:', sellerResult.reason);
        return; // Exit if we can't get the basic seller info
      }
      
      if (profileResult.status === 'fulfilled') {
        setSellerProfile(profileResult.value.data);
      } else {
        console.error('Failed to fetch seller profile:', profileResult.reason);
        // Continue without profile data
        setSellerProfile({ avg_rating: 0, ratings_count: 0, points_total: 0, categories: [], bio: '' });
      }
      
      // Fetch stats
      await fetchSellerStats();
      
    } catch (error) {
      console.error('Failed to fetch seller data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSellerStats = async () => {
    try {
      // Get completed orders count - make this optional
      let completedCount = 0;
      try {
        const ordersResponse = await axios.get(`${API}/orders/seller/${id}/stats`);
        completedCount = ordersResponse.data?.completed_count || 0;
      } catch (error) {
        console.error('Failed to fetch order stats:', error);
      }
      
      // Get seller's active listings for avg SLA and starting price
      const listingsResponse = await axios.get(`${API}/listings`, {
        params: { limit: 100 } // Get all listings to filter client-side
      });
      
      // Filter to only this seller's active listings
      const activeListings = listingsResponse.data.filter(
        listing => listing.rnkee_id === id && listing.is_active
      );
      
      let avgSla = 0;
      let startingPrice = Infinity;
      
      if (activeListings.length > 0) {
        // Calculate average SLA
        const totalSla = activeListings.reduce((sum, listing) => sum + listing.delivery_sla_hours, 0);
        avgSla = Math.round(totalSla / activeListings.length);
        
        // Calculate starting price
        activeListings.forEach(listing => {
          let listingPrice = listing.price_cents;
          
          if (listing.pricing_model === 'fixed' || listing.pricing_model === 'retainer') {
            if (listing.packages && listing.packages.length > 0) {
              listingPrice = Math.min(...listing.packages.map(p => p.price_cents));
            }
          }
          
          startingPrice = Math.min(startingPrice, listingPrice);
        });
      }
      
      setStats({
        completed_orders_count: completedCount,
        avg_sla_hours: avgSla,
        starting_from_price: startingPrice === Infinity ? 0 : startingPrice
      });
      
    } catch (error) {
      console.error('Failed to fetch seller stats:', error);
      // Set default stats on error
      setStats({
        completed_orders_count: 0,
        avg_sla_hours: 0,
        starting_from_price: 0
      });
    }
  };

  const fetchSellerListings = async () => {
    try {
      const params = {
        limit: 100 // Get all listings to filter client-side
      };

      // Add search and filters
      if (searchTerm) params.keyword = searchTerm;
      if (filters.service_category) params.service_category = filters.service_category;
      if (filters.service_subcategory) params.service_subcategory = filters.service_subcategory;
      if (filters.pricing_model) params.pricing_model = filters.pricing_model;
      if (filters.min_price) params.min_price = parseInt(filters.min_price);
      if (filters.max_price) params.max_price = parseInt(filters.max_price);

      const response = await axios.get(`${API}/listings`, { params });
      
      // Filter to only this seller's active listings
      let results = response.data.filter(
        listing => listing.rnkee_id === id && listing.is_active
      );

      // Apply sorting
      if (sortBy === 'price_low') {
        results.sort((a, b) => a.price_cents - b.price_cents);
      } else if (sortBy === 'price_high') {
        results.sort((a, b) => b.price_cents - a.price_cents);
      } else if (sortBy === 'sla_fastest') {
        results.sort((a, b) => a.delivery_sla_hours - b.delivery_sla_hours);
      } else if (sortBy === 'newest') {
        results.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      }

      // Apply pagination
      const startIndex = (currentPage - 1) * 25;
      const endIndex = startIndex + 25;
      const paginatedResults = results.slice(startIndex, endIndex);

      setListings(paginatedResults);
    } catch (error) {
      console.error('Failed to fetch seller listings:', error);
      setListings([]);
    }
  };

  const updateSearchParams = () => {
    const newParams = new URLSearchParams();
    if (searchTerm) newParams.set('search', searchTerm);
    if (filters.service_category) newParams.set('service_category', filters.service_category);
    if (filters.service_subcategory) newParams.set('service_subcategory', filters.service_subcategory);
    if (filters.pricing_model) newParams.set('pricing_model', filters.pricing_model);
    if (filters.min_price) newParams.set('min_price', filters.min_price);
    if (filters.max_price) newParams.set('max_price', filters.max_price);
    if (sortBy !== 'best_match') newParams.set('sort', sortBy);
    if (currentPage > 1) newParams.set('page', currentPage.toString());
    
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      service_category: '',
      service_subcategory: '',
      pricing_model: '',
      min_price: '',
      max_price: ''
    });
    setSortBy('best_match');
    setCurrentPage(1);
    setSearchParams(new URLSearchParams());
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchSellerListings();
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Profile URL copied to clipboard!');
  };

  const scrollToServices = () => {
    document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;
  const formatMemberSince = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const renderSellerListingCard = (listing) => (
    <Card key={listing.id} className="card-modern group">
      <CardHeader className="pb-4">
        <div className="flex justify-between items-start mb-3">
          <Link 
            to={`/listing/${listing.id}`}
            className="font-semibold text-lg hover:text-orange-600 transition-colors line-clamp-2 leading-tight flex-1 pr-3"
            style={{ color: 'var(--brand-navy)' }}
          >
            {listing.title}
          </Link>
          {/* Badge */}
          {listing.service_category === 'lead_generation' ? (
            <Badge 
              className={listing.exclusivity === 'exclusive' 
                ? 'badge-exclusive shrink-0 font-medium' 
                : 'badge-shared shrink-0 font-medium'
              }
            >
              {listing.exclusivity}
            </Badge>
          ) : (
            <Badge 
              variant="outline" 
              className="text-sm shrink-0 font-medium border-2"
              style={{ 
                backgroundColor: '#F8FAFC',
                color: 'var(--brand-navy)',
                borderColor: 'var(--border-strong)'
              }}
            >
              {formatCategoryName(listing.service_category)}
            </Badge>
          )}
        </div>
        
        <div className="text-sm mb-2" style={{ color: 'var(--steel-500)' }}>
          {formatCategoryName(listing.service_category)} • {formatPricingModel(listing.pricing_model)}
        </div>
        
        <CardDescription className="text-sm line-clamp-2 leading-relaxed" style={{ color: 'var(--steel-500)' }}>
          {listing.description}
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold" style={{ color: 'var(--accent-orange)' }}>
                {getPriceDisplay(listing)}
              </span>
            </div>
            <div className="flex flex-col items-end gap-1">
              {listing.pricing_model === 'per_lead' && (
                <div className="text-sm" style={{ color: 'var(--steel-500)' }}>
                  Min: {listing.min_qty}
                </div>
              )}
              <ClockIcon hours={listing.delivery_sla_hours} />
            </div>
          </div>
          
          {listing.highlights && listing.highlights.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {listing.highlights.slice(0, 3).map((highlight, index) => (
                <Badge 
                  key={index} 
                  variant="outline" 
                  className="text-xs px-2 py-1 border"
                  style={{ 
                    color: 'var(--steel-500)',
                    borderColor: 'var(--border-strong)',
                    backgroundColor: '#F8FAFC'
                  }}
                >
                  {highlight}
                </Badge>
              ))}
            </div>
          )}
          
          <div className="pt-3 border-t" style={{ borderColor: 'var(--border-strong)' }}>
            <p className="text-xs" style={{ color: 'var(--steel-500)' }}>
              by {seller?.company || seller?.name}
            </p>
          </div>
          
          <Link to={`/listing/${listing.id}`} className="block">
            <Button 
              className="w-full text-sm btn-primary font-semibold"
              style={{ 
                backgroundColor: 'var(--accent-orange)',
                borderColor: 'var(--accent-orange)'
              }}
            >
              View Service
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="flex items-center justify-center py-12">Loading seller profile...</div>
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="flex items-center justify-center py-12">Seller not found</div>
      </div>
    );
  }

  // Gather sample URLs for portfolio
  const portfolioUrls = listings
    .filter(listing => listing.sample_urls && listing.sample_urls.length > 0)
    .flatMap(listing => listing.sample_urls)
    .slice(0, 6);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      {/* SEO Meta Tags */}
      <title>{`${seller.company || seller.name} — Services for Home Services Businesses`}</title>
      <meta 
        name="description" 
        content={sellerProfile?.bio ? sellerProfile.bio.substring(0, 160) : `Professional services from ${seller.company || seller.name}`} 
      />
      
      {/* Seller Approval Status Banner (only show for seller's own profile view) */}
      {sellerProfile && sellerProfile.approval_status === 'pending' && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  Your seller profile is under review. You'll be notified when it's approved.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seller Denied Status Banner (only show for seller's own profile view) */}
      {sellerProfile && sellerProfile.approval_status === 'denied' && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-grow">
                <h3 className="text-sm font-medium text-red-800">
                  Your profile needs changes before it can be approved.
                </h3>
                {sellerProfile.denial_reason && (
                  <div className="mt-2 text-sm text-red-700">
                    <p>{sellerProfile.denial_reason}</p>
                  </div>
                )}
                <div className="mt-3">
                  <Button 
                    onClick={() => window.location.href = '/profile/edit'}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    size="sm"
                  >
                    Update Profile & Resubmit
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Hero Header */}
        <Card className="card-modern mb-8">
          <CardContent className="pt-8 pb-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div 
                  className="w-24 h-24 rounded-full flex items-center justify-center text-2xl font-bold text-white"
                  style={{ backgroundColor: 'var(--brand-navy)' }}
                >
                  {seller.name.charAt(0)}
                </div>
              </div>
              
              {/* Center Info */}
              <div className="flex-1">
                <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--brand-navy)' }}>
                  {seller.name}
                </h1>
                <p className="text-lg mb-2" style={{ color: 'var(--steel-500)' }}>
                  {seller.company}
                </p>
                <div className="text-sm mb-4" style={{ color: 'var(--steel-500)' }}>
                  {seller.location && (
                    <span>{seller.location} • </span>
                  )}
                  Member since {formatMemberSince(seller.created_at)}
                </div>
                
                {/* Rating and Points */}
                <div className="flex items-center gap-4 mb-4">
                  <NailRatingDisplay 
                    rating={sellerProfile?.avg_rating || 0}
                    count={sellerProfile?.ratings_count || 0}
                    showCount={true}
                    size="large"
                  />
                  <PointsBadge 
                    points={sellerProfile?.points_total || 0}
                    size="lg"
                  />
                </div>
                
                {/* Points Breakdown */}
                <div className="mb-4">
                  <PointsBreakdown sellerId={id} initialExpanded={false} />
                </div>
                
                {/* Categories */}
                {sellerProfile?.categories && sellerProfile.categories.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {sellerProfile.categories.map((category, index) => (
                      <Badge 
                        key={index} 
                        variant="outline" 
                        className="font-medium"
                        style={{ 
                          borderColor: 'var(--border-strong)',
                          color: 'var(--steel-700)'
                        }}
                      >
                        {formatCategoryName(category)}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              {/* CTAs */}
              <div className="flex flex-col gap-2 lg:flex-shrink-0">
                <Button 
                  onClick={scrollToServices} 
                  className="btn-primary font-semibold"
                  style={{ backgroundColor: 'var(--accent-orange)' }}
                >
                  View Services
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleShare}
                  className="border-2"
                  style={{ 
                    borderColor: 'var(--border-strong)',
                    color: 'var(--steel-700)'
                  }}
                >
                  Share Profile
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - About Seller */}
          <div className="lg:col-span-1">
            <Card className="card-modern">
              <CardHeader>
                <CardTitle style={{ color: 'var(--brand-navy)' }}>
                  About {seller.company || seller.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Bio */}
                  {sellerProfile?.bio && (
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--steel-500)' }}>
                      {sellerProfile.bio}
                    </p>
                  )}
                  
                  {/* Quick Stats */}
                  <div className="space-y-3">
                    <h4 className="font-semibold" style={{ color: 'var(--brand-navy)' }}>
                      Quick Stats
                    </h4>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-green-600">✅</span>
                        <span style={{ color: 'var(--steel-700)' }}>
                          Completed orders: {stats.completed_orders_count}
                        </span>
                      </div>
                      
                      {stats.avg_sla_hours > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <ClockIcon hours={stats.avg_sla_hours} className="text-xs" />
                          <span style={{ color: 'var(--steel-700)' }}>
                            Avg delivery SLA: {stats.avg_sla_hours}h
                          </span>
                        </div>
                      )}
                      
                      {stats.starting_from_price > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <span style={{ color: 'var(--accent-orange)' }}>💲</span>
                          <span style={{ color: 'var(--steel-700)' }}>
                            Starting from: {formatPrice(stats.starting_from_price)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Portfolio Section */}
            {portfolioUrls.length > 0 && (
              <Card className="card-modern mt-6">
                <CardHeader>
                  <CardTitle style={{ color: 'var(--brand-navy)' }}>
                    Portfolio
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {portfolioUrls.map((url, index) => (
                      <a 
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 border-2 rounded-lg hover:border-orange-500 transition-colors text-center text-sm"
                        style={{ 
                          borderColor: 'var(--border-strong)',
                          color: 'var(--steel-700)'
                        }}
                      >
                        Sample {index + 1}
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Enhanced Profile Sections */}
            
            {/* Skills & Specializations */}
            {sellerProfile?.skills && sellerProfile.skills.length > 0 && (
              <Card className="card-modern mt-6">
                <CardHeader>
                  <CardTitle style={{ color: 'var(--brand-navy)' }}>
                    Skills & Specializations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {sellerProfile.skills.map((skill, index) => (
                      <Badge 
                        key={index} 
                        variant="outline" 
                        className="font-medium"
                        style={{ 
                          borderColor: 'var(--border-strong)',
                          color: 'var(--steel-700)',
                          backgroundColor: '#F8FAFC'
                        }}
                      >
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Certifications */}
            {sellerProfile?.certifications && sellerProfile.certifications.length > 0 && (
              <Card className="card-modern mt-6">
                <CardHeader>
                  <CardTitle style={{ color: 'var(--brand-navy)' }}>
                    Professional Certifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {sellerProfile.certifications.map((cert, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <span className="text-blue-600 mt-1">🎓</span>
                        <div>
                          <div className="font-medium text-sm" style={{ color: 'var(--steel-700)' }}>
                            {typeof cert === 'string' ? cert : (cert.name || 'Unnamed Certification')}
                          </div>
                          {typeof cert === 'object' && cert.issuer && (
                            <div className="text-xs" style={{ color: 'var(--steel-500)' }}>
                              {cert.issuer} {cert.date_obtained && `• ${new Date(cert.date_obtained).getFullYear()}`}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Education */}
            {sellerProfile?.education && sellerProfile.education.length > 0 && (
              <Card className="card-modern mt-6">
                <CardHeader>
                  <CardTitle style={{ color: 'var(--brand-navy)' }}>
                    Education
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {sellerProfile.education.map((edu, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <span className="text-green-600 mt-1">🎓</span>
                        <div>
                          <div className="font-medium text-sm" style={{ color: 'var(--steel-700)' }}>
                            {edu.degree}
                          </div>
                          <div className="text-xs" style={{ color: 'var(--steel-500)' }}>
                            {edu.institution} {edu.year && `• ${edu.year}`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Online Courses */}
            {sellerProfile?.online_courses && sellerProfile.online_courses.length > 0 && (
              <Card className="card-modern mt-6">
                <CardHeader>
                  <CardTitle style={{ color: 'var(--brand-navy)' }}>
                    Online Courses & Training
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {sellerProfile.online_courses.map((course, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <span className="text-purple-600 mt-1">📚</span>
                        <div>
                          <div className="font-medium text-sm" style={{ color: 'var(--steel-700)' }}>
                            {typeof course === 'string' ? course : (course.name || 'Unnamed Course')}
                          </div>
                          {typeof course === 'object' && course.provider && (
                            <div className="text-xs" style={{ color: 'var(--steel-500)' }}>
                              {course.provider} {course.completion_date && `• ${new Date(course.completion_date).getFullYear()}`}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Additional Profile Details */}
            <Card className="card-modern mt-6">
              <CardHeader>
                <CardTitle style={{ color: 'var(--brand-navy)' }}>
                  Additional Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Languages */}
                  {sellerProfile?.languages && sellerProfile.languages.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <span style={{ color: 'var(--steel-500)' }}>🌐</span>
                        <span className="font-medium" style={{ color: 'var(--steel-700)' }}>
                          Languages:
                        </span>
                      </div>
                      <div className="ml-6 space-y-1">
                        {sellerProfile.languages.map((lang, index) => (
                          <div key={index} className="text-sm" style={{ color: 'var(--steel-600)' }}>
                            {typeof lang === 'object' ? `${lang.language} - ${lang.proficiency}` : lang}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Response Time */}
                  {sellerProfile?.response_time && (
                    <div className="flex items-center gap-2 text-sm">
                      <span style={{ color: 'var(--steel-500)' }}>⏱️</span>
                      <span style={{ color: 'var(--steel-700)' }}>
                        Typically responds: {sellerProfile.response_time}
                      </span>
                    </div>
                  )}
                  
                  {/* Website */}
                  {sellerProfile?.website_url && (
                    <div className="flex items-center gap-2 text-sm">
                      <span style={{ color: 'var(--steel-500)' }}>🌍</span>
                      <a 
                        href={sellerProfile.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-orange-600 transition-colors"
                        style={{ color: 'var(--accent-orange)' }}
                      >
                        Visit Website
                      </a>
                    </div>
                  )}
                  
                  {/* LinkedIn */}
                  {sellerProfile?.linkedin_url && (
                    <div className="flex items-center gap-2 text-sm">
                      <span style={{ color: 'var(--steel-500)' }}>💼</span>
                      <a 
                        href={sellerProfile.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-orange-600 transition-colors"
                        style={{ color: 'var(--accent-orange)' }}
                      >
                        LinkedIn Profile
                      </a>
                    </div>
                  )}
                  
                  {/* Portfolio URLs */}
                  {sellerProfile?.portfolio_urls && sellerProfile.portfolio_urls.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="font-semibold text-sm" style={{ color: 'var(--brand-navy)' }}>
                        Portfolio
                      </h5>
                      {sellerProfile.portfolio_urls.map((url, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          <span style={{ color: 'var(--steel-500)' }}>🎨</span>
                          <a 
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-orange-600 transition-colors"
                            style={{ color: 'var(--accent-orange)' }}
                          >
                            Portfolio Item {index + 1}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Right Column - Tabs */}
          <div className="lg:col-span-2" id="services-section">
            <Tabs defaultValue="services" className="space-y-6">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="services">Services ({listings.length})</TabsTrigger>
                <TabsTrigger value="reviews">Reviews</TabsTrigger>
              </TabsList>
              
              <TabsContent value="services">
                <Card className="card-modern">
                  <CardHeader>
                    <CardTitle style={{ color: 'var(--brand-navy)' }}>
                      Services ({listings.length})
                    </CardTitle>
                    
                    {/* Toolbar */}
                    <div className="space-y-4">
                      {/* Search */}
                      <form onSubmit={handleSearch} className="flex gap-2">
                        <Input
                          placeholder="Search services..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="flex-1"
                        />
                        <Button type="submit" variant="outline">
                          Search
                        </Button>
                      </form>
                      
                      {/* Filters and Sort */}
                      <div className="flex flex-wrap gap-2">
                        <Select 
                          value={filters.service_category} 
                          onValueChange={(value) => setFilters({...filters, service_category: value === 'all' ? '' : value, service_subcategory: ''})}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder="All categories" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All categories</SelectItem>
                            {Object.keys(getServiceCategories()).map(category => (
                              <SelectItem key={category} value={category}>
                                {formatCategoryName(category)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <Select value={sortBy} onValueChange={setSortBy}>
                          <SelectTrigger className="w-48">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="best_match">Best Match</SelectItem>
                            <SelectItem value="price_low">Price: Low → High</SelectItem>
                            <SelectItem value="price_high">Price: High → Low</SelectItem>
                            <SelectItem value="sla_fastest">SLA: Fastest</SelectItem>
                            <SelectItem value="newest">Newest</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <Button variant="outline" onClick={clearFilters} className="text-sm">
                          Clear
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
              
              <CardContent>
                {listings.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--steel-500)' }} />
                    <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--brand-navy)' }}>
                      No services are live yet
                    </h3>
                    <p className="mb-4" style={{ color: 'var(--steel-500)' }}>
                      This seller hasn't published any services at the moment.
                    </p>
                    <Link to="/">
                      <Button 
                        className="btn-primary"
                        style={{ backgroundColor: 'var(--accent-orange)' }}
                      >
                        View All Categories
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {listings.map(renderSellerListingCard)}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="reviews">
            <Card className="card-modern">
              <ReviewsSection sellerId={id} />
            </Card>
          </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      
      {/* Mobile Sticky CTAs */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 lg:hidden no-print" style={{ borderColor: 'var(--border-strong)' }}>
        <div className="flex gap-2">
          <Button 
            onClick={scrollToServices} 
            className="flex-1 btn-primary font-semibold"
            style={{ backgroundColor: 'var(--accent-orange)' }}
          >
            View Services
          </Button>
          <Button 
            variant="outline" 
            onClick={handleShare}
            className="border-2"
            style={{ borderColor: 'var(--border-strong)' }}
          >
            Share
          </Button>
        </div>
      </div>
    </div>
  );
};

// Profile Edit Page - Testing minimal version first
// Profile Edit Page - Enhanced with Education, Work Experience, and Certifications
const ProfileEditPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Individual Profile State
  const [profile, setProfile] = useState({ 
    bio: '', 
    skills: [], 
    categories: [],
    education: [],
    work_experience: [],
    certifications: [],
    seller_type: 'individual',
    approval_status: 'pending', // Add approval status field
    
    // Company Profile Fields
    company_brand_name: '',
    company_logo_url: '',
    company_website: '',
    hq_city: '',
    hq_state: '',
    company_bio: '',
    company_social_links: [],
    company_highlights: [],
    company_portfolio_urls: []
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  
  useEffect(() => {
    document.title = 'Edit Profile | Rnkem';
  }, []);

  const loadProfile = async () => {
    if (!user?.id) return;
    try {
      const response = await axios.get(`${API}/profiles/me`);
      const data = response.data;
      setProfile({
        bio: data.bio || '',
        skills: data.skills || [],
        categories: data.categories || [],
        education: data.education || [],
        work_experience: data.work_experience || [],
        certifications: data.certifications || [],
        seller_type: data.seller_type || 'individual',
        approval_status: data.approval_status || 'draft', // Add approval status
        
        // Company fields
        company_brand_name: data.company_brand_name || '',
        company_logo_url: data.company_logo_url || '',
        company_website: data.company_website || '',
        hq_city: data.hq_city || '',
        hq_state: data.hq_state || '',
        company_bio: data.company_bio || '',
        company_social_links: data.company_social_links || [],
        company_highlights: data.company_highlights || [],
        company_portfolio_urls: data.company_portfolio_urls || []
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  // Alias for loadProfile to maintain consistency
  const fetchProfile = loadProfile;

  React.useEffect(() => {
    loadProfile();
  }, [user]);

  const validateCompanyProfile = () => {
    const errors = [];
    
    if (profile.seller_type === 'company') {
      if (!profile.company_brand_name?.trim()) errors.push('Company Brand Name is required');
      if (!profile.company_logo_url?.trim()) errors.push('Company Logo is required');
      if (!profile.company_website?.trim()) errors.push('Company Website is required');
      if (!profile.hq_city?.trim()) errors.push('Headquarters City is required');
      if (!profile.hq_state?.trim()) errors.push('Headquarters State is required');
      if (!profile.company_bio?.trim()) errors.push('Company Biography is required');
      
      if (profile.company_bio && profile.company_bio.trim().length < 140) {
        errors.push('Company bio must be at least 140 characters');
      }
      
      if (profile.company_highlights.length < 3 || profile.company_highlights.length > 6) {
        errors.push('Company highlights must contain 3-6 items');
      }
      
      profile.company_highlights.forEach((highlight, i) => {
        if (highlight.length > 60) {
          errors.push(`Highlight ${i + 1} must be 60 characters or less`);
        }
      });
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    try {
      setLogoUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/profiles/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setProfile(prev => ({ ...prev, company_logo_url: response.data.logo_url }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const saveProfile = async () => {
    if (!validateCompanyProfile()) {
      toast.error('Please fix validation errors before saving');
      return;
    }
    
    // Validate required fields before saving
    const validationErrors = [];
    
    // Validate Education entries
    profile.education.forEach((edu, index) => {
      if (!edu.degree || edu.degree.toString().trim().length === 0) {
        validationErrors.push(`Education #${index + 1}: Degree is required`);
      }
      if (!edu.institution || edu.institution.toString().trim().length === 0) {
        validationErrors.push(`Education #${index + 1}: Institution is required`);
      }
      if (!edu.graduation_year || edu.graduation_year.toString().trim().length === 0) {
        validationErrors.push(`Education #${index + 1}: Graduation Year is required`);
      }
    });
    
    // Validate Work Experience entries
    profile.work_experience.forEach((exp, index) => {
      if (!exp.company || exp.company.toString().trim().length === 0) {
        validationErrors.push(`Work Experience #${index + 1}: Company Name is required`);
      }
      if (!exp.position || exp.position.toString().trim().length === 0) {
        validationErrors.push(`Work Experience #${index + 1}: Job Title is required`);
      }
      if (!exp.start_date || exp.start_date.toString().trim().length === 0) {
        validationErrors.push(`Work Experience #${index + 1}: Start Date is required`);
      }
      if (!exp.is_current && (!exp.end_date || exp.end_date.toString().trim().length === 0)) {
        validationErrors.push(`Work Experience #${index + 1}: End Date is required (or mark as current job)`);
      }
    });
    
    // Validate Certifications entries
    profile.certifications.forEach((cert, index) => {
      if (!cert.name || cert.name.toString().trim().length === 0) {
        validationErrors.push(`Certification #${index + 1}: Certificate Name is required`);
      }
      if (!cert.issuer || cert.issuer.toString().trim().length === 0) {
        validationErrors.push(`Certification #${index + 1}: Issuing Organization is required`);
      }
    });
    
    // Show validation errors if any
    if (validationErrors.length > 0) {
      toast.error(`Please fill out required fields:\n${validationErrors.slice(0, 3).join('\n')}${validationErrors.length > 3 ? `\n...and ${validationErrors.length - 3} more` : ''}`);
      return;
    }
    
    try {
      setSaving(true);
      await axios.put(`${API}/profiles/me`, profile);
      toast.success('Profile saved successfully!');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const submitForApproval = async () => {
    if (!validateCompanyProfile()) {
      toast.error('Please fix validation errors before submitting for approval');
      return;
    }

    // Basic validation - ensure bio is filled out
    if (profile.seller_type === 'individual' && !profile.bio?.trim()) {
      toast.error('Please add a bio before submitting for approval');
      return;
    }
    
    if (profile.seller_type === 'company' && !profile.company_bio?.trim()) {
      toast.error('Please add a company bio before submitting for approval');
      return;
    }

    try {
      setSaving(true);
      
      // First save the profile
      await axios.put(`${API}/profiles/me`, profile);
      
      // Then submit for approval
      await axios.post(`${API}/profiles/me/submit`);
      
      toast.success('Profile submitted for approval! You\'ll be notified when it\'s reviewed.');
      
      // Refresh profile data to update approval status
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit profile for approval');
    } finally {
      setSaving(false);
    }
  };

  const handleResubmitForApproval = async () => {
    try {
      setSaving(true);
      
      // Set approval status back to pending and clear denial reason
      await axios.put(`${API}/profiles/me`, {
        approval_status: 'pending',
        submitted_at: new Date().toISOString(),
        denial_reason: null
      });
      
      // Create audit log entry
      await axios.post(`${API}/profiles/me/resubmit`);
      
      toast.success('Profile resubmitted for review. You\'ll be notified when it\'s approved.');
      
      // Refresh profile data
      fetchProfile();
    } catch (error) {
      toast.error('Failed to resubmit profile. Please try again.');
      console.error('Error resubmitting profile:', error);
    } finally {
      setSaving(false);
    }
  };

  // Calculate profile completion score
  const calculateCompletionScore = () => {
    let score = 0;
    const maxScore = 100;
    
    // Bio (20 points)
    if (profile.bio && profile.bio.trim().length > 50) score += 20;
    
    // Skills (15 points) - need at least 3 non-empty skills
    const validSkills = profile.skills.filter(skill => skill && skill.trim().length > 0);
    if (validSkills.length >= 3) score += 15;
    
    // Categories (10 points)
    if (profile.categories && profile.categories.length >= 1) score += 10;
    
    // Education (20 points) - need at least 1 complete education entry
    const validEducation = profile.education.filter(edu => 
      edu.degree && edu.degree.toString().trim().length > 0 && 
      edu.institution && edu.institution.toString().trim().length > 0 && 
      edu.graduation_year && edu.graduation_year.toString().trim().length > 0
    );
    if (validEducation.length >= 1) score += 20;
    
    // Work Experience (20 points) - need at least 1 complete work experience entry
    const validWorkExperience = profile.work_experience.filter(exp => 
      exp.company && exp.company.toString().trim().length > 0 && 
      exp.position && exp.position.toString().trim().length > 0 && 
      exp.start_date && exp.start_date.toString().trim().length > 0
    );
    if (validWorkExperience.length >= 1) score += 20;
    
    // Certifications (15 points) - need at least 1 complete certification entry
    const validCertifications = profile.certifications.filter(cert => 
      cert.name && cert.name.toString().trim().length > 0 && 
      cert.issuer && cert.issuer.toString().trim().length > 0
    );
    if (validCertifications.length >= 1) score += 15;
    
    return Math.min(score, maxScore);
  };

  const completionScore = calculateCompletionScore();

  // Education handlers
  const addEducation = () => {
    setProfile({
      ...profile,
      education: [...profile.education, { degree: '', institution: '', graduation_year: '' }]
    });
  };

  const updateEducation = (index, field, value) => {
    const updatedEducation = [...profile.education];
    updatedEducation[index][field] = value;
    setProfile({ ...profile, education: updatedEducation });
  };

  const removeEducation = (index) => {
    const updatedEducation = profile.education.filter((_, i) => i !== index);
    setProfile({ ...profile, education: updatedEducation });
  };

  // Work Experience handlers
  const addWorkExperience = () => {
    setProfile({
      ...profile,
      work_experience: [...profile.work_experience, { 
        company: '', 
        position: '', 
        start_date: '', 
        end_date: '', 
        description: '',
        is_current: false
      }]
    });
  };

  const updateWorkExperience = (index, field, value) => {
    const updatedExperience = [...profile.work_experience];
    updatedExperience[index][field] = value;
    if (field === 'is_current' && value) {
      updatedExperience[index]['end_date'] = '';
    }
    setProfile({ ...profile, work_experience: updatedExperience });
  };

  const removeWorkExperience = (index) => {
    const updatedExperience = profile.work_experience.filter((_, i) => i !== index);
    setProfile({ ...profile, work_experience: updatedExperience });
  };

  // Certifications handlers
  const addCertification = () => {
    setProfile({
      ...profile,
      certifications: [...profile.certifications, { 
        name: '', 
        issuer: '', 
        date_obtained: '', 
        expiry_date: '' 
      }]
    });
  };

  const updateCertification = (index, field, value) => {
    const updatedCertifications = [...profile.certifications];
    updatedCertifications[index][field] = value;
    setProfile({ ...profile, certifications: updatedCertifications });
  };

  const removeCertification = (index) => {
    const updatedCertifications = profile.certifications.filter((_, i) => i !== index);
    setProfile({ ...profile, certifications: updatedCertifications });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      {/* Seller Approval Status Banner */}
      {profile && profile.approval_status === 'pending' && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  Your seller profile is under review. You'll be notified when it's approved.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seller Denied Status Banner */}
      {profile && profile.approval_status === 'denied' && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-grow">
                <h3 className="text-sm font-medium text-red-800">
                  Your profile needs changes before it can be approved.
                </h3>
                {profile.denial_reason && (
                  <div className="mt-2 text-sm text-red-700">
                    <p>{profile.denial_reason}</p>
                  </div>
                )}
                <div className="mt-3">
                  <Button 
                    onClick={() => window.location.href = '/profile/edit'}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    size="sm"
                  >
                    Update Profile & Resubmit
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Edit Your Professional Profile</h1>
          <div className="flex gap-4">
            <Button variant="outline" onClick={() => navigate('/sell')}>
              Back to Dashboard
            </Button>
            
            {/* Show different buttons based on approval status */}
            {profile.approval_status === 'draft' ? (
              <>
                <Button onClick={saveProfile} disabled={saving} variant="outline">
                  {saving ? 'Saving...' : 'Save Draft'}
                </Button>
                <Button onClick={submitForApproval} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  {saving ? 'Submitting...' : 'Submit for Approval'}
                </Button>
              </>
            ) : profile.approval_status === 'denied' ? (
              <>
                <Button onClick={saveProfile} disabled={saving} variant="outline">
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button 
                  onClick={handleResubmitForApproval} 
                  disabled={saving} 
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? 'Resubmitting...' : 'Resubmit for Approval'}
                </Button>
              </>
            ) : (
              // For pending, approved, or other statuses
              <Button onClick={saveProfile} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                {saving ? 'Saving...' : 'Save Profile'}
              </Button>
            )}
          </div>
        </div>

        {/* Profile Optimization Score */}
        <Card className="mb-6 border-l-4 border-l-orange-500">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Profile Optimization</CardTitle>
                <CardDescription>Complete your profile to attract more clients</CardDescription>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-orange-600">{completionScore}%</div>
                <div className="text-sm text-gray-500">Complete</div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <div 
                className="bg-orange-500 h-3 rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${completionScore}%` }}
              ></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div className={`flex items-center gap-2 ${profile.bio && profile.bio.trim().length > 50 ? 'text-green-600' : 'text-gray-500'}`}>
                <span>{profile.bio && profile.bio.trim().length > 50 ? '✅' : '⭕'}</span>
                Professional Bio (50+ chars)
              </div>
              <div className={`flex items-center gap-2 ${profile.skills.filter(skill => skill && skill.trim().length > 0).length >= 3 ? 'text-green-600' : 'text-gray-500'}`}>
                <span>{profile.skills.filter(skill => skill && skill.trim().length > 0).length >= 3 ? '✅' : '⭕'}</span>
                Skills (3+ required)
              </div>
              <div className={`flex items-center gap-2 ${profile.categories && profile.categories.length >= 1 ? 'text-green-600' : 'text-gray-500'}`}>
                <span>{profile.categories && profile.categories.length >= 1 ? '✅' : '⭕'}</span>
                Service Categories
              </div>
              <div className={`flex items-center gap-2 ${profile.education.filter(edu => 
                edu.degree && edu.degree.toString().trim().length > 0 && 
                edu.institution && edu.institution.toString().trim().length > 0 && 
                edu.graduation_year && edu.graduation_year.toString().trim().length > 0
              ).length >= 1 ? 'text-green-600' : 'text-gray-500'}`}>
                <span>{profile.education.filter(edu => 
                  edu.degree && edu.degree.toString().trim().length > 0 && 
                  edu.institution && edu.institution.toString().trim().length > 0 && 
                  edu.graduation_year && edu.graduation_year.toString().trim().length > 0
                ).length >= 1 ? '✅' : '⭕'}</span>
                Education (Complete Entry)
              </div>
              <div className={`flex items-center gap-2 ${profile.work_experience.filter(exp => 
                exp.company && exp.company.toString().trim().length > 0 && 
                exp.position && exp.position.toString().trim().length > 0 && 
                exp.start_date && exp.start_date.toString().trim().length > 0
              ).length >= 1 ? 'text-green-600' : 'text-gray-500'}`}>
                <span>{profile.work_experience.filter(exp => 
                  exp.company && exp.company.toString().trim().length > 0 && 
                  exp.position && exp.position.toString().trim().length > 0 && 
                  exp.start_date && exp.start_date.toString().trim().length > 0
                ).length >= 1 ? '✅' : '⭕'}</span>
                Work Experience (Complete Entry)
              </div>
              <div className={`flex items-center gap-2 ${profile.certifications.filter(cert => 
                cert.name && cert.name.toString().trim().length > 0 && 
                cert.issuer && cert.issuer.toString().trim().length > 0
              ).length >= 1 ? 'text-green-600' : 'text-gray-500'}`}>
                <span>{profile.certifications.filter(cert => 
                  cert.name && cert.name.toString().trim().length > 0 && 
                  cert.issuer && cert.issuer.toString().trim().length > 0
                ).length >= 1 ? '✅' : '⭕'}</span>
                Certifications (Complete Entry)
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <>
          {/* Seller Type Toggle */}
          <Card>
            <CardHeader>
              <CardTitle>Seller Type</CardTitle>
              <CardDescription>Choose whether you're representing yourself or a company</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="seller_type"
                    value="individual"
                    checked={profile.seller_type === 'individual'}
                    onChange={(e) => setProfile(prev => ({ ...prev, seller_type: e.target.value }))}
                    className="w-4 h-4 text-orange-600"
                  />
                  <div>
                    <span className="font-medium">Individual</span>
                    <p className="text-sm text-gray-500">Freelancer or sole proprietor</p>
                  </div>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="seller_type"
                    value="company"
                    checked={profile.seller_type === 'company'}
                    onChange={(e) => setProfile(prev => ({ ...prev, seller_type: e.target.value }))}
                    className="w-4 h-4 text-orange-600"
                  />
                  <div>
                    <span className="font-medium">Company</span>
                    <p className="text-sm text-gray-500">Business or organization</p>
                  </div>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Company Profile Section */}
          {profile.seller_type === 'company' && (
            <Card>
              <CardHeader>
                <CardTitle>Company Profile</CardTitle>
                <CardDescription>Complete your company information to build trust with clients</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Company Brand Name */}
                <div>
                  <Label htmlFor="company_brand_name">Company Brand Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="company_brand_name"
                    value={profile.company_brand_name}
                    onChange={(e) => setProfile(prev => ({ ...prev, company_brand_name: e.target.value }))}
                    placeholder="Enter your company name"
                    required
                  />
                </div>

                {/* Company Logo */}
                <div>
                  <Label>Company Logo <span className="text-red-500">*</span></Label>
                  <div className="mt-2">
                    {profile.company_logo_url && (
                      <div className="mb-4">
                        <img 
                          src={profile.company_logo_url} 
                          alt="Company Logo" 
                          className="w-24 h-24 object-cover border rounded-lg"
                        />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                      disabled={logoUploading}
                    />
                    {logoUploading && <p className="text-sm text-gray-500 mt-1">Uploading...</p>}
                    <p className="text-sm text-gray-500 mt-1">Square images work best. Max 5MB.</p>
                  </div>
                </div>

                {/* Company Website */}
                <div>
                  <Label htmlFor="company_website">Company Website <span className="text-red-500">*</span></Label>
                  <Input
                    id="company_website"
                    type="url"
                    value={profile.company_website}
                    onChange={(e) => setProfile(prev => ({ ...prev, company_website: e.target.value }))}
                    placeholder="https://yourcompany.com"
                    required
                  />
                </div>

                {/* Headquarters */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="hq_city">Headquarters City <span className="text-red-500">*</span></Label>
                    <Input
                      id="hq_city"
                      value={profile.hq_city}
                      onChange={(e) => setProfile(prev => ({ ...prev, hq_city: e.target.value }))}
                      placeholder="New York"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="hq_state">Headquarters State <span className="text-red-500">*</span></Label>
                    <Input
                      id="hq_state"
                      value={profile.hq_state}
                      onChange={(e) => setProfile(prev => ({ ...prev, hq_state: e.target.value }))}
                      placeholder="NY"
                      required
                    />
                  </div>
                </div>

                {/* Company Bio */}
                <div>
                  <Label htmlFor="company_bio">Company Biography <span className="text-red-500">*</span></Label>
                  <Textarea
                    id="company_bio"
                    value={profile.company_bio}
                    onChange={(e) => setProfile(prev => ({ ...prev, company_bio: e.target.value }))}
                    placeholder="Tell clients about your company's mission, values, and expertise. This should be at least 140 characters..."
                    rows={4}
                    className="resize-none"
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {profile.company_bio.length}/140 characters minimum
                  </p>
                </div>

                {/* Company Highlights */}
                <div>
                  <Label>Company Highlights (3-6 items)</Label>
                  <p className="text-sm text-gray-500 mb-2">Short bullet points about what makes your company special</p>
                  <div className="space-y-2">
                    {profile.company_highlights.map((highlight, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={highlight}
                          onChange={(e) => setProfile(prev => ({
                            ...prev,
                            company_highlights: prev.company_highlights.map((h, i) => i === index ? e.target.value : h)
                          }))}
                          placeholder="e.g., 10+ years in business"
                          maxLength={60}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setProfile(prev => ({
                            ...prev,
                            company_highlights: prev.company_highlights.filter((_, i) => i !== index)
                          }))}
                          className="text-red-600"
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    {profile.company_highlights.length < 6 && (
                      <Button
                        variant="outline"
                        onClick={() => setProfile(prev => ({
                          ...prev,
                          company_highlights: [...prev.company_highlights, '']
                        }))}
                        className="w-full"
                      >
                        + Add Highlight
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 text-red-500 mt-0.5">⚠️</div>
                  <div>
                    <h4 className="font-medium text-red-800 mb-2">Please fix the following errors:</h4>
                    <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                      {validationErrors.map((error, i) => (
                        <li key={i}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bio Section (for Individual) */}
          {profile.seller_type === 'individual' && (
            <>
            <Card>
            <CardHeader>
              <CardTitle>About Me</CardTitle>
              <CardDescription>Write a compelling bio that showcases your expertise</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={profile.bio}
                onChange={(e) => setProfile({...profile, bio: e.target.value})}
                placeholder="Describe your experience, specializations, and what makes you unique..."
                rows={5}
                className="w-full"
              />
              <p className="text-sm text-gray-500 mt-2">{profile.bio.length}/1000 characters</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Skills & Expertise</CardTitle>
              <CardDescription>Add skills that clients search for</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {profile.skills.map((skill, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={skill}
                    onChange={(e) => {
                      const newSkills = [...profile.skills];
                      newSkills[index] = e.target.value;
                      setProfile({...profile, skills: newSkills});
                    }}
                    placeholder="Enter a skill..."
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      const newSkills = profile.skills.filter((_, i) => i !== index);
                      setProfile({...profile, skills: newSkills});
                    }}
                    className="text-red-600"
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={() => setProfile({...profile, skills: [...profile.skills, '']})}
                className="w-full"
              >
                + Add Skill
              </Button>
            </CardContent>
          </Card>
          </>
          )}

          {/* Categories */}
          <Card>
            <CardHeader>
              <CardTitle>Service Categories</CardTitle>
              <CardDescription>Select the categories that describe your services</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.keys(getServiceCategories()).map(category => (
                  <label key={category} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={profile.categories.includes(category)}
                      onChange={(e) => {
                        let newCategories;
                        if (e.target.checked) {
                          newCategories = [...profile.categories, category];
                        } else {
                          newCategories = profile.categories.filter(c => c !== category);
                        }
                        setProfile({...profile, categories: newCategories});
                      }}
                    />
                    <span className="text-sm">{formatCategoryName(category)}</span>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Individual-only sections: Education, Work Experience, Certifications */}
          {profile.seller_type === 'individual' && (
          <>
          {/* Education Section */}
          <Card>
            <CardHeader>
              <CardTitle>Education</CardTitle>
              <CardDescription>Add your educational background</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {profile.education.map((edu, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium">Education #{index + 1}</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeEducation(index)}
                      className="text-red-600"
                    >
                      Remove
                    </Button>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <div>
                      <Label>Degree/Qualification <span className="text-red-500">*</span></Label>
                      <Input
                        value={edu.degree}
                        onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                        placeholder="Bachelor's in Computer Science"
                        required
                      />
                    </div>
                    <div>
                      <Label>Institution <span className="text-red-500">*</span></Label>
                      <Input
                        value={edu.institution}
                        onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                        placeholder="University of Technology"
                        required
                      />
                    </div>
                    <div>
                      <Label>Graduation Year <span className="text-red-500">*</span></Label>
                      <Input
                        value={edu.graduation_year}
                        onChange={(e) => updateEducation(index, 'graduation_year', e.target.value)}
                        placeholder="2020"
                        type="number"
                        min="1950"
                        max="2030"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={addEducation}
                className="w-full"
              >
                + Add Education
              </Button>
            </CardContent>
          </Card>

          {/* Work Experience Section */}
          <Card>
            <CardHeader>
              <CardTitle>Work Experience</CardTitle>
              <CardDescription>Add your professional work history</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {profile.work_experience.map((exp, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium">Experience #{index + 1}</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeWorkExperience(index)}
                      className="text-red-600"
                    >
                      Remove
                    </Button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label>Company Name <span className="text-red-500">*</span></Label>
                      <Input
                        value={exp.company}
                        onChange={(e) => updateWorkExperience(index, 'company', e.target.value)}
                        placeholder="ABC Construction"
                        required
                      />
                    </div>
                    <div>
                      <Label>Job Title <span className="text-red-500">*</span></Label>
                      <Input
                        value={exp.position}
                        onChange={(e) => updateWorkExperience(index, 'position', e.target.value)}
                        placeholder="Senior Project Manager"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label>Start Date <span className="text-red-500">*</span></Label>
                      <Input
                        value={exp.start_date}
                        onChange={(e) => updateWorkExperience(index, 'start_date', e.target.value)}
                        placeholder="2020-01"
                        type="month"
                        required
                      />
                    </div>
                    <div>
                      <Label>End Date {!exp.is_current && <span className="text-red-500">*</span>}</Label>
                      <Input
                        value={exp.end_date}
                        onChange={(e) => updateWorkExperience(index, 'end_date', e.target.value)}
                        placeholder="2023-12"
                        type="month"
                        disabled={exp.is_current}
                        required={!exp.is_current}
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`current-${index}`}
                      checked={exp.is_current}
                      onChange={(e) => updateWorkExperience(index, 'is_current', e.target.checked)}
                    />
                    <Label htmlFor={`current-${index}`}>I currently work here</Label>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={exp.description}
                      onChange={(e) => updateWorkExperience(index, 'description', e.target.value)}
                      placeholder="Describe your responsibilities and achievements..."
                      rows={3}
                    />
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={addWorkExperience}
                className="w-full"
              >
                + Add Work Experience
              </Button>
            </CardContent>
          </Card>

          {/* Certifications Section */}
          <Card>
            <CardHeader>
              <CardTitle>Certifications</CardTitle>
              <CardDescription>Add your professional certifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {profile.certifications.map((cert, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium">Certification #{index + 1}</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeCertification(index)}
                      className="text-red-600"
                    >
                      Remove
                    </Button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label>Certificate Name <span className="text-red-500">*</span></Label>
                      <Input
                        value={cert.name}
                        onChange={(e) => updateCertification(index, 'name', e.target.value)}
                        placeholder="Project Management Professional (PMP)"
                        required
                      />
                    </div>
                    <div>
                      <Label>Issuing Organization <span className="text-red-500">*</span></Label>
                      <Input
                        value={cert.issuer}
                        onChange={(e) => updateCertification(index, 'issuer', e.target.value)}
                        placeholder="Project Management Institute"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label>Date Obtained</Label>
                      <Input
                        value={cert.date_obtained}
                        onChange={(e) => updateCertification(index, 'date_obtained', e.target.value)}
                        placeholder="2022-06"
                        type="month"
                      />
                    </div>
                    <div>
                      <Label>Expiry Date (Optional)</Label>
                      <Input
                        value={cert.expiry_date}
                        onChange={(e) => updateCertification(index, 'expiry_date', e.target.value)}
                        placeholder="2025-06"
                        type="month"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={addCertification}
                className="w-full"
              >
                + Add Certification
              </Button>
            </CardContent>
          </Card>
          </>
          )}

          {/* Save Button */}
          <div className="flex justify-end pt-6">
            <Button 
              onClick={saveProfile} 
              disabled={saving}
              className="bg-orange-600 hover:bg-orange-700 px-8 py-3"
              size="lg"
            >
              {saving ? 'Saving Profile...' : 'Save Profile'}
            </Button>
          </div>
          </>
        </div>
      </div>
    </div>
  );
};

// Contact Seller Button Component
const ContactSellerButton = ({ sellerId, listingId, className = '' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showContactModal, setShowContactModal] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleContactSeller = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      setSending(true);
      const response = await axios.post(`${API}/conversations`, {
        seller_id: sellerId,
        listing_id: listingId,
        initial_message: message.trim()
      });
      
      setShowContactModal(false);
      setMessage('');
      navigate(`/inbox/${response.data.id}`);
      toast.success('Message sent! Redirecting to conversation...');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setShowContactModal(true)}
        className={`text-sm border-2 ${className}`}
        style={{ 
          borderColor: 'var(--border-strong)',
          color: 'var(--steel-700)'
        }}
      >
        Contact
      </Button>

      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Contact Seller</DialogTitle>
            <DialogDescription>
              Send a message to discuss this listing. Keep it professional and on-topic.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Hi, I'm interested in your services. Could you provide more details about..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="resize-none"
              />
              <p className="text-xs text-slate-500 mt-1">
                Text only - no links or contact information allowed
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handleContactSeller}
                disabled={sending || !message.trim()}
                className="flex-1"
              >
                {sending ? 'Sending...' : 'Send Message'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowContactModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Thread View Component for Right Pane
const ThreadView = ({ thread, details, loading, onAction, onBack, isMobile, onThreadUpdate }) => {
  const { user } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // Scroll to bottom of messages
  const messagesEndRef = useRef(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [details?.messages]);

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    try {
      setSending(true);
      
      if (thread.thread_type === 'conversation') {
        await axios.post(`${API}/conversations/${thread.thread_id}/messages`, {
          body: newMessage.trim()
        });
      } else {
        await axios.post(`${API}/orders/${thread.thread_id}/messages`, {
          body: newMessage.trim()
        });
      }
      
      setNewMessage('');
      
      // Refresh thread details immediately
      const response = await axios.get(`${API}/inbox/${thread.thread_type}/${thread.thread_id}`);
      onThreadUpdate(response.data); // Update the thread details in parent component
      
      toast.success('Message sent');
      
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatMessageDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach(message => {
      const date = formatMessageDate(message.created_at);
      if (!groups[date]) groups[date] = [];
      groups[date].push(message);
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  const messageGroups = groupMessagesByDate(details.messages || []);

  return (
    <div className="flex flex-col h-full">
      {/* Thread Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="flex items-center gap-3">
          {isMobile && (
            <Button variant="ghost" size="sm" onClick={onBack} className="p-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Button>
          )}
          
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium">
            {details.other_party?.name?.charAt(0) || 'U'}
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-900">
              {details.other_party?.name || 'Unknown User'}
            </h3>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              {details.other_party?.company && (
                <span>{details.other_party.company}</span>
              )}
              {thread.labels.map((label, index) => (
                <span 
                  key={index}
                  className={`px-2 py-1 rounded-full text-xs ${
                    label.startsWith('Order #') 
                      ? 'bg-green-100 text-green-700'
                      : 'bg-orange-100 text-orange-700'
                  }`}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
        
        {/* Thread Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onAction(thread, 'starred', !thread.starred)}
            className="p-2"
            title={thread.starred ? 'Remove from starred' : 'Add to starred'}
          >
            <svg className={`w-5 h-5 ${thread.starred ? 'text-yellow-500 fill-current' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAction(thread, 'archived', !thread.archived)}
            className="px-3 py-1 text-xs"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8l6 6 6-6" />
            </svg>
            {thread.archived ? 'Unarchive' : 'Archive'}
          </Button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {Object.entries(messageGroups).map(([date, messages]) => (
          <div key={date}>
            {/* Date Separator */}
            <div className="flex items-center justify-center my-4">
              <div className="px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-500">
                {date}
              </div>
            </div>
            
            {/* Messages for this date */}
            {messages.map((message, index) => {
              const isCurrentUser = message.sender_id === user.id;
              const showAvatar = index === 0 || messages[index - 1].sender_id !== message.sender_id;
              
              return (
                <div
                  key={message.id}
                  className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} ${
                    showAvatar ? 'mb-4' : 'mb-1'
                  }`}
                >
                  {!isCurrentUser && showAvatar && (
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-medium mr-2 flex-shrink-0">
                      {details.other_party?.name?.charAt(0) || 'U'}
                    </div>
                  )}
                  
                  {!isCurrentUser && !showAvatar && <div className="w-10 mr-2" />}
                  
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                      isCurrentUser
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="text-sm">{message.body}</p>
                    {showAvatar && (
                      <p className={`text-xs mt-1 ${isCurrentUser ? 'text-blue-100' : 'text-gray-500'}`}>
                        {formatMessageTime(message.created_at)}
                      </p>
                    )}
                  </div>
                  
                  {isCurrentUser && showAvatar && (
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-pink-600 rounded-full flex items-center justify-center text-white text-sm font-medium ml-2 flex-shrink-0">
                      {user.name?.charAt(0) || 'U'}
                    </div>
                  )}
                  
                  {isCurrentUser && !showAvatar && <div className="w-10 ml-2" />}
                </div>
              );
            })}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Composer */}
      <div className="border-t border-gray-200 p-4 bg-white">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                thread.thread_type === 'conversation' 
                  ? "Keep communication in Rnkem. Email addresses and phone numbers aren't allowed."
                  : "Type your message..."
              }
              className="resize-none border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              rows={2}
              disabled={sending}
            />
            {thread.thread_type === 'conversation' && (
              <p className="text-xs text-gray-500 mt-1">
                Keep communication in Rnkem. Email addresses and phone numbers aren't allowed.
              </p>
            )}
          </div>
          
          <div className="flex flex-col gap-2 items-center justify-center">
            {thread.thread_type === 'order' && (
              <Button variant="outline" size="sm" disabled>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                Attach
              </Button>
            )}
            
            <Button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sending}
              className="bg-blue-500 hover:bg-blue-600 text-white p-3"
              size="sm"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Side Card for Context - Desktop Only */}
      {!isMobile && (
        <div className="w-80 border-l border-gray-200 bg-gray-50 p-4">
          {thread.thread_type === 'order' && details.order && (
            <div className="bg-white rounded-lg p-4 border">
              <h4 className="font-semibold mb-2">Order Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className="capitalize">{details.order.status}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total:</span>
                  <span className="font-semibold">${(details.order.total_price_cents / 100).toFixed(2)}</span>
                </div>
                <Button size="sm" className="w-full mt-3" variant="outline">
                  View Full Order
                </Button>
              </div>
            </div>
          )}
          
          {thread.thread_type === 'conversation' && details.listing && (
            <div className="bg-white rounded-lg p-4 border">
              <h4 className="font-semibold mb-2">{details.listing.title}</h4>
              <p className="text-sm text-gray-600 mb-3 line-clamp-3">{details.listing.description}</p>
              <div className="text-sm mb-3">
                <span className="text-orange-600 font-semibold">
                  {getPriceDisplay(details.listing)}
                </span>
              </div>
              <Button 
                size="sm" 
                className="w-full bg-orange-600 hover:bg-orange-700"
                onClick={() => window.location.href = `/listing/${details.listing.id}`}
              >
                Buy Now
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
const InboxPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State management
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadDetails, setThreadDetails] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  
  // Filters and search
  const [activeFilter, setActiveFilter] = useState(searchParams.get('filter') || 'all');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [showSearch, setShowSearch] = useState(false);
  
  // Mobile responsive
  const [showSidebar, setShowSidebar] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    document.title = 'Inbox | Rnkem';
  }, []);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeFilter !== 'all') params.set('filter', activeFilter);
    if (searchQuery) params.set('search', searchQuery);
    setSearchParams(params);
  }, [activeFilter, searchQuery, setSearchParams]);

  // Handle window resize for mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setShowSidebar(true);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch unified inbox threads
  const fetchThreads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (activeFilter !== 'all') params.append('filter_type', activeFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API}/inbox?${params}`);
      setThreads(response.data);
    } catch (error) {
      console.error('Failed to fetch threads:', error);
      toast.error('Failed to load inbox');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThreads();
  }, [activeFilter, searchQuery]);

  // Handle thread selection
  const handleThreadSelect = async (thread) => {
    setSelectedThread(thread);
    setLoadingThread(true);
    
    // Auto-hide sidebar on mobile when thread is selected
    if (isMobile) {
      setShowSidebar(false);
    }
    
    try {
      const response = await axios.get(`${API}/inbox/${thread.thread_type}/${thread.thread_id}`);
      setThreadDetails(response.data);
      
      // Mark as read if it has unread messages
      if (thread.unread_count > 0) {
        await axios.post(`${API}/inbox/${thread.thread_type}/${thread.thread_id}/mark-read`);
        // Update local thread state
        setThreads(prev => 
          prev.map(t => 
            t.thread_id === thread.thread_id && t.thread_type === thread.thread_type
              ? { ...t, unread_count: 0 }
              : t
          )
        );
      }
    } catch (error) {
      console.error('Failed to load thread details:', error);
      toast.error('Failed to load conversation');
    } finally {
      setLoadingThread(false);
    }
  };

  // Handle thread details update (for real-time message updates)
  const handleThreadUpdate = (updatedDetails) => {
    setThreadDetails(updatedDetails);
  };

  // Handle thread actions (star, archive, etc.)
  const handleThreadAction = async (thread, action, value) => {
    try {
      const updateData = {};
      updateData[action] = value;
      
      await axios.post(`${API}/inbox/${thread.thread_type}/${thread.thread_id}/update-state`, updateData);
      
      // Update local state
      setThreads(prev => 
        prev.map(t => 
          t.thread_id === thread.thread_id && t.thread_type === thread.thread_type
            ? { ...t, [action]: value }
            : t
        )
      );
      
      // If thread is archived and we're not in archived view, remove it from list
      if (action === 'archived' && value && activeFilter !== 'archived') {
        setThreads(prev => 
          prev.filter(t => !(t.thread_id === thread.thread_id && t.thread_type === thread.thread_type))
        );
        if (selectedThread?.thread_id === thread.thread_id) {
          setSelectedThread(null);
          setThreadDetails(null);
        }
      }
      
      toast.success(
        action === 'starred' 
          ? (value ? 'Conversation starred' : 'Conversation unstarred')
          : action === 'archived'
          ? (value ? 'Conversation archived' : 'Conversation unarchived')
          : 'Updated'
      );
    } catch (error) {
      console.error('Failed to update thread:', error);
      toast.error('Failed to update conversation');
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d`;
    return `${Math.floor(diffInMinutes / 10080)}w`;
  };

  const filters = [
    { key: 'all', label: 'All messages', count: threads.length },
    { key: 'unread', label: 'Unread', count: threads.filter(t => t.unread_count > 0).length },
    { key: 'starred', label: 'Starred', count: threads.filter(t => t.starred).length },
    { key: 'orders', label: 'Orders', count: threads.filter(t => t.thread_type === 'order').length },
    { key: 'pre-purchase', label: 'Pre-purchase', count: threads.filter(t => t.thread_type === 'conversation').length },
    { key: 'archived', label: 'Archived' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Inbox</h1>
            <p className="text-gray-600 mt-1">Manage your conversations with clients and sellers</p>
          </div>
          
          {/* Mobile sidebar toggle */}
          {isMobile && (
            <Button
              variant="outline"
              onClick={() => setShowSidebar(!showSidebar)}
              className="md:hidden"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </Button>
          )}
        </div>

        {/* Two-pane layout */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex h-[600px]">
            
            {/* Left Rail - Conversations Sidebar */}
            <div className={`${
              isMobile ? (showSidebar ? 'w-full' : 'hidden') : 'w-80'
            } border-r border-gray-200 flex flex-col bg-gray-50`}>
              
              {/* Filter Dropdown & Controls */}
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="space-y-3">
                  {/* Filter Dropdown */}
                  <Select value={activeFilter} onValueChange={setActiveFilter}>
                    <SelectTrigger className="w-full">
                      <SelectValue>
                        {filters.find(f => f.key === activeFilter)?.label || 'All messages'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {filters.map(filter => (
                        <SelectItem key={filter.key} value={filter.key}>
                          {filter.label}
                          {filter.count !== undefined && ` (${filter.count})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Search Toggle & Input */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowSearch(!showSearch)}
                      className="p-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </Button>
                    
                    {showSearch && (
                      <Input
                        placeholder="Search conversations..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1"
                        autoFocus
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Conversations List */}
              <div className="flex-1 overflow-y-auto">
                {threads.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center p-6">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.013 8.013 0 01-7-4L5 21l4.5-4.5c1.393.877 3.057 1.5 4.5 1.5 4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8c0 1.016.195 1.996.548 2.89" />
                      </svg>
                    </div>
                    <h3 className="font-medium text-gray-900 mb-2">No conversations yet</h3>
                    <p className="text-sm text-gray-500">Start browsing services to connect with sellers</p>
                    <Button 
                      onClick={() => navigate('/browse')}
                      className="mt-4 bg-orange-600 hover:bg-orange-700"
                    >
                      Browse Services
                    </Button>
                  </div>
                ) : (
                  threads.map((thread) => (
                    <div
                      key={`${thread.thread_type}-${thread.thread_id}`}
                      className={`flex items-start gap-3 p-4 hover:bg-white cursor-pointer border-b border-gray-100 transition-all ${
                        selectedThread?.thread_id === thread.thread_id && selectedThread?.thread_type === thread.thread_type
                          ? 'bg-blue-50 border-l-4 border-l-blue-500'
                          : thread.unread_count > 0
                          ? 'bg-blue-25'
                          : ''
                      }`}
                      onClick={() => handleThreadSelect(thread)}
                    >
                      <div className="relative">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                          {thread.other_party?.name?.charAt(0) || 'U'}
                        </div>
                        {thread.unread_count > 0 && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 border-2 border-white rounded-full"></div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className={`text-sm truncate ${thread.unread_count > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                            {thread.other_party?.name || 'Unknown User'}
                          </h4>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-xs text-gray-500">
                              {formatRelativeTime(thread.last_message_at)}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1 h-6 w-6 hover:bg-gray-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleThreadAction(thread, 'starred', !thread.starred);
                              }}
                            >
                              <svg className={`w-3 h-3 ${thread.starred ? 'text-yellow-500 fill-current' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                              </svg>
                            </Button>
                          </div>
                        </div>
                        
                        {/* Labels */}
                        {thread.labels.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-1">
                            {thread.labels.map((label, index) => (
                              <span 
                                key={index}
                                className={`text-xs px-2 py-0.5 rounded-full ${
                                  label.startsWith('Order #') 
                                    ? 'bg-green-100 text-green-700'
                                    : label === 'Sponsored'
                                    ? 'bg-orange-100 text-orange-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        <p className={`text-xs truncate ${thread.unread_count > 0 ? 'text-gray-700' : 'text-gray-500'}`}>
                          {thread.last_snippet || 'No messages yet'}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Right Pane - Thread View */}
            <div className={`${
              isMobile ? (showSidebar ? 'hidden' : 'w-full') : 'flex-1'
            } flex flex-col`}>
              {selectedThread && threadDetails ? (
                <ThreadView
                  thread={selectedThread}
                  details={threadDetails}
                  loading={loadingThread}
                  onAction={handleThreadAction}
                  onBack={() => {
                    if (isMobile) setShowSidebar(true);
                    setSelectedThread(null);
                    setThreadDetails(null);
                  }}
                  isMobile={isMobile}
                  onThreadUpdate={handleThreadUpdate}
                />
              ) : (
                <div className="flex items-center justify-center h-full bg-gray-50">
                  <div className="text-center p-8">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h8z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Pick up where you left off</h3>
                    <p className="text-gray-500">Select a conversation and chat away</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Conversation Detail Page
const ConversationPage = () => {
  const { id: conversationId } = useParams();
  const { user } = useAuth();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  
  useEffect(() => {
    document.title = 'Conversation | Rnkem';
  }, []);

  useEffect(() => {
    if (conversationId) {
      fetchConversationData();
    }
  }, [conversationId]);

  const fetchConversationData = async () => {
    try {
      const [convRes, messagesRes] = await Promise.all([
        axios.get(`${API}/conversations/${conversationId}`),
        axios.get(`${API}/conversations/${conversationId}/messages`)
      ]);
      
      setConversation(convRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    try {
      setSending(true);
      await axios.post(`${API}/conversations/${conversationId}/messages`, {
        body: newMessage.trim()
      });
      
      setNewMessage('');
      fetchConversationData(); // Refresh to get new message
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const toggleConversationStatus = async () => {
    const newStatus = conversation.status === 'open' ? 'closed' : 'open';
    try {
      await axios.patch(`${API}/conversations/${conversationId}`, { status: newStatus });
      setConversation({ ...conversation, status: newStatus });
      toast.success(`Conversation ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update conversation status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="text-center py-20">Conversation not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/inbox" className="text-blue-600 hover:underline text-sm">
              ← Back to Inbox
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 mt-1">
              {conversation.counterparty?.name}
            </h1>
            {conversation.counterparty?.company && (
              <p className="text-slate-600">{conversation.counterparty.company}</p>
            )}
            {conversation.listing && (
              <p className="text-sm text-slate-500">Re: {conversation.listing.title}</p>
            )}
          </div>
          <Button
            onClick={toggleConversationStatus}
            variant="outline"
            size="sm"
          >
            {conversation.status === 'open' ? 'Close' : 'Reopen'}
          </Button>
        </div>

        <Card>
          <CardContent className="p-6">
            {/* Messages */}
            <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
              {messages.map((message) => (
                <div key={message.id} className="flex gap-3">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    {message.sender?.name?.charAt(0) || 'U'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">
                        {message.sender?.name || 'Unknown'}
                        {message.sender_id === user?.id && (
                          <span className="text-slate-500 font-normal"> (You)</span>
                        )}
                      </span>
                      <span className="text-xs text-slate-500">
                        {new Date(message.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-slate-700 bg-slate-50 rounded-lg p-3">{message.body}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Message Composer */}
            {conversation.status === 'open' && (
              <div className="border-t pt-4">
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Type your message... (text only, no links or contact info)"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    rows={2}
                    className="flex-1"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={sending || !newMessage.trim()}
                    className="self-end"
                  >
                    Send
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Pre-purchase messages are text-only. Links and contact info are not allowed.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Enhanced Order Detail Page with Messaging and Deliveries
// Attachments Section Component
const AttachmentsSection = ({ orderId, user }) => {
  const {
    files,
    isLoading,
    error,
    hasProcessingFiles,
    uploadFile,
    deleteFile,
    downloadFileBlob,
    fetchPreviewBlob,
    refresh,
  } = useAttachments(orderId, {
    kindFilter: null, // Show all files
    autoRefresh: true,
    refreshInterval: 10000,
  });

  return (
    <div className="mt-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            📎 Order Attachments
            {hasProcessingFiles && (
              <span className="text-sm font-normal text-yellow-600">(Processing files...)</span>
            )}
          </CardTitle>
          <p className="text-sm text-gray-600">
            Upload and share files related to this order
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* File Uploader */}
            <AttachmentsUploader
              orderId={orderId}
              kind="deliverable"
              uploadFile={uploadFile}
              onUploaded={(file) => {
                console.log('File uploaded:', file);
                // Optionally show a toast notification
              }}
            />

            {/* Files List */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Uploaded Files {files.length > 0 && `(${files.length})`}
              </h3>
              <AttachmentsList
                files={files}
                isLoading={isLoading}
                error={error}
                currentUserId={user?.id}
                currentUserRole={user?.role}
                hasProcessingFiles={hasProcessingFiles}
                downloadFileBlob={downloadFileBlob}
                deleteFile={deleteFile}
                fetchPreviewBlob={fetchPreviewBlob}
                onRefresh={refresh}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const OrderDetailPage = () => {
  const { id: orderId } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryType, setDeliveryType] = useState('');
  const [deliveryData, setDeliveryData] = useState({
    title: '',
    note: '',
    text_content: '',
    link_urls: [''],
    file_urls: [],
    is_final_delivery: false
  });
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionReason, setRevisionReason] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  
  useEffect(() => {
    if (order) {
      document.title = `Order #${orderId.slice(0, 8)} | Rnkem`;
    } else {
      document.title = 'Order Details | Rnkem';
    }
  }, [order, orderId]);

  useEffect(() => {
    if (orderId) {
      fetchOrderData();
    }
  }, [orderId]);

  const fetchOrderData = async () => {
    try {
      const [orderRes, messagesRes, deliveriesRes] = await Promise.all([
        axios.get(`${API}/orders/${orderId}`),
        axios.get(`${API}/orders/${orderId}/messages`),
        axios.get(`${API}/orders/${orderId}/deliveries`)
      ]);
      
      setOrder(orderRes.data);
      setMessages(messagesRes.data);
      setDeliveries(deliveriesRes.data);
    } catch (error) {
      console.error('Failed to load order data:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    try {
      setSendingMessage(true);
      await axios.post(`${API}/orders/${orderId}/messages`, {
        body: newMessage.trim(),
        attachments: []
      });
      
      setNewMessage('');
      fetchOrderData(); // Refresh to get new message
      toast.success('Message sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const createDelivery = async () => {
    try {
      const payload = {
        kind: deliveryType,
        title: deliveryData.title,
        note: deliveryData.note,
        is_final_delivery: deliveryData.is_final_delivery
      };

      if (deliveryType === 'text') {
        payload.text_content = deliveryData.text_content;
      } else if (deliveryType === 'links') {
        payload.link_urls = deliveryData.link_urls.filter(url => url.trim());
      } else if (deliveryType === 'files') {
        payload.file_urls = deliveryData.file_urls;
      }

      await axios.post(`${API}/orders/${orderId}/deliveries`, payload);
      
      setShowDeliveryModal(false);
      fetchOrderData();
      toast.success('Delivery created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create delivery');
    }
  };

  const handleDeliveryAction = async (action, reason = '') => {
    try {
      await axios.post(`${API}/orders/${orderId}/accept`, {
        action,
        revision_reason: reason
      });
      
      fetchOrderData();
      
      if (action === 'accept') {
        // Show review modal after successful acceptance
        setShowReviewModal(true);
        toast.success('Delivery accepted');
      } else {
        toast.success('Revision requested');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process delivery action');
    }
  };

  const isSeller = order && order.rnkee_id === user?.id;
  const isBuyer = order && order.rnker_id === user?.id;
  const canDeliver = isSeller && order && ['created', 'in_progress'].includes(order.status);
  const canAccept = isBuyer && order && order.status === 'delivered';
  const latestDelivery = deliveries.length > 0 ? deliveries[0] : null;
  
  // Revision count logic
  const revisionCount = order?.revision_count || 0;
  const packageRevisions = order?.package_revisions;
  const canRequestRevision = canAccept && (packageRevisions === null || revisionCount < packageRevisions);
  const revisionsRemaining = packageRevisions !== null ? Math.max(0, packageRevisions - revisionCount) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="text-center py-20">Order not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Order Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Order #{order.id.slice(-8)}</h1>
              <p className="text-lg text-slate-700">{order.listing?.title}</p>
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                <span>Seller: {order.rnkee?.company || order.rnkee?.name}</span>
                <span>Buyer: {order.rnker?.company || order.rnker?.name}</span>
                <span>${(order.price_cents_total / 100).toFixed(2)}</span>
              </div>
            </div>
            <div className="text-right">
              <Badge 
                className={
                  order.status === 'completed' ? 'bg-green-100 text-green-800' :
                  order.status === 'delivered' ? 'bg-blue-100 text-blue-800' :
                  order.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }
              >
                {order.status.replace('_', ' ').toUpperCase()}
              </Badge>
              <div className="text-sm text-slate-500 mt-2">
                Created: {new Date(order.created_at).toLocaleDateString()}
              </div>
              {order.delivered_at && (
                <div className="text-sm text-slate-500">
                  Delivered: {new Date(order.delivered_at).toLocaleDateString()}
                </div>
              )}
              {packageRevisions !== null && (
                <div className="text-sm text-slate-500">
                  Revisions: {revisionCount}/{packageRevisions} used
                  {revisionsRemaining !== null && revisionsRemaining > 0 && (
                    <span className="text-blue-600 ml-1">({revisionsRemaining} remaining)</span>
                  )}
                  {revisionsRemaining === 0 && (
                    <span className="text-red-600 ml-1">(none remaining)</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content - Messages */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Order Communication</CardTitle>
                {canDeliver && (
                  <Button
                    onClick={() => setShowDeliveryModal(true)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Deliver Work
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {/* Messages Thread */}
                <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                  {messages.map((message) => (
                    <div key={message.id} className="flex gap-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                        {message.sender_id === 'system' ? 'S' : 
                         message.sender?.name?.charAt(0) || 'U'}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">
                            {message.sender_id === 'system' ? 'System' : 
                             message.sender?.name || 'Unknown'}
                          </span>
                          <span className="text-xs text-slate-500">
                            {new Date(message.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-slate-700 bg-slate-50 rounded-lg p-3">{message.body}</p>
                        {message.attachments && message.attachments.length > 0 && (
                          <div className="mt-2">
                            {message.attachments.map((url, idx) => (
                              <a key={idx} href={url} target="_blank" rel="noopener noreferrer" 
                                 className="text-blue-600 hover:underline text-sm block">
                                Attachment {idx + 1}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Message Composer */}
                {order.status !== 'created' && (
                  <div className="border-t pt-4">
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        rows={2}
                        className="flex-1"
                      />
                      <Button
                        onClick={sendMessage}
                        disabled={sendingMessage || !newMessage.trim()}
                        className="self-end"
                      >
                        Send
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Deliveries */}
          <div>
            {/* Latest Delivery */}
            {latestDelivery && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Latest Delivery</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold">{latestDelivery.title}</h4>
                      <p className="text-sm text-slate-500 capitalize">{latestDelivery.kind.replace('_', ' ')}</p>
                    </div>
                    
                    {latestDelivery.note && (
                      <p className="text-sm text-slate-600">{latestDelivery.note}</p>
                    )}
                    
                    {latestDelivery.text_content && (
                      <div className="bg-slate-50 p-3 rounded text-sm">
                        {latestDelivery.text_content.slice(0, 200)}...
                      </div>
                    )}
                    
                    {latestDelivery.link_urls && latestDelivery.link_urls.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-1">Links:</p>
                        {latestDelivery.link_urls.map((url, idx) => (
                          <a key={idx} href={url} target="_blank" rel="noopener noreferrer"
                             className="text-blue-600 hover:underline text-sm block">
                            {url}
                          </a>
                        ))}
                      </div>
                    )}
                    
                    {latestDelivery.file_urls && latestDelivery.file_urls.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-1">Files:</p>
                        {latestDelivery.file_urls.map((url, idx) => (
                          <a key={idx} href={url} target="_blank" rel="noopener noreferrer"
                             className="text-blue-600 hover:underline text-sm block">
                            Download File {idx + 1}
                          </a>
                        ))}
                      </div>
                    )}

                    {canAccept && (
                      <div className="flex gap-2 pt-3 border-t">
                        <Button
                          onClick={() => handleDeliveryAction('accept')}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          Accept
                        </Button>
                        <Button
                          onClick={() => setShowRevisionModal(true)}
                          variant="outline"
                          className="flex-1"
                          disabled={!canRequestRevision}
                        >
                          {packageRevisions !== null && !canRequestRevision 
                            ? 'No Revisions Left' 
                            : 'Request Revision'
                          }
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* All Deliveries */}
            {deliveries.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>All Deliveries ({deliveries.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {deliveries.map((delivery, idx) => (
                      <div key={delivery.id} className="border-l-2 border-slate-200 pl-3">
                        <h5 className="font-medium text-sm">{delivery.title}</h5>
                        <p className="text-xs text-slate-500">
                          {new Date(delivery.created_at).toLocaleDateString()} • 
                          <span className="capitalize"> {delivery.kind.replace('_', ' ')}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* File Attachments Section */}
        <AttachmentsSection orderId={orderId} user={user} />

        {/* Delivery Modal */}
        {showDeliveryModal && (
          <Dialog open={showDeliveryModal} onOpenChange={setShowDeliveryModal}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Deliver Work</DialogTitle>
                <DialogDescription>
                  Choose the type of delivery and provide the necessary details.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Delivery Type Selection */}
                <div>
                  <Label className="text-sm font-medium">Delivery Type</Label>
                  <Select value={deliveryType} onValueChange={setDeliveryType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select delivery type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="csv_leads">Leads (CSV)</SelectItem>
                      <SelectItem value="files">Files</SelectItem>
                      <SelectItem value="links">Links</SelectItem>
                      <SelectItem value="text">Text/Content</SelectItem>
                      <SelectItem value="milestone">Milestone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Common Fields */}
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={deliveryData.title}
                    onChange={(e) => setDeliveryData({...deliveryData, title: e.target.value})}
                    placeholder="Enter delivery title"
                  />
                </div>

                <div>
                  <Label htmlFor="note">Note (Optional)</Label>
                  <Textarea
                    id="note"
                    value={deliveryData.note}
                    onChange={(e) => setDeliveryData({...deliveryData, note: e.target.value})}
                    placeholder="Add any additional notes"
                    rows={2}
                  />
                </div>

                {/* Type-specific Fields */}
                {deliveryType === 'text' && (
                  <div>
                    <Label htmlFor="content">Content</Label>
                    <Textarea
                      id="content"
                      value={deliveryData.text_content}
                      onChange={(e) => setDeliveryData({...deliveryData, text_content: e.target.value})}
                      placeholder="Enter your content here..."
                      rows={6}
                    />
                  </div>
                )}

                {deliveryType === 'links' && (
                  <div>
                    <Label>Links</Label>
                    {deliveryData.link_urls.map((url, idx) => (
                      <div key={idx} className="flex gap-2 mt-2">
                        <Input
                          value={url}
                          onChange={(e) => {
                            const newUrls = [...deliveryData.link_urls];
                            newUrls[idx] = e.target.value;
                            setDeliveryData({...deliveryData, link_urls: newUrls});
                          }}
                          placeholder="https://example.com"
                        />
                        <Button
                          variant="outline"
                          onClick={() => {
                            const newUrls = deliveryData.link_urls.filter((_, i) => i !== idx);
                            setDeliveryData({...deliveryData, link_urls: newUrls});
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      onClick={() => setDeliveryData({...deliveryData, link_urls: [...deliveryData.link_urls, '']})}
                      className="mt-2"
                    >
                      Add Link
                    </Button>
                  </div>
                )}

                {deliveryType === 'files' && (
                  <div>
                    <Label>Files</Label>
                    <p className="text-sm text-slate-500">File upload functionality would be implemented here</p>
                  </div>
                )}

                {deliveryType === 'csv_leads' && (
                  <div>
                    <Label>Lead CSV File</Label>
                    <p className="text-sm text-slate-500">CSV upload with validation would be implemented here</p>
                  </div>
                )}

                {/* Final Delivery Checkbox */}
                {deliveryType === 'milestone' && (
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="final"
                      checked={deliveryData.is_final_delivery}
                      onChange={(e) => setDeliveryData({...deliveryData, is_final_delivery: e.target.checked})}
                    />
                    <Label htmlFor="final">This is the final delivery</Label>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button onClick={createDelivery} disabled={!deliveryType || !deliveryData.title}>
                    Create Delivery
                  </Button>
                  <Button variant="outline" onClick={() => setShowDeliveryModal(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Revision Request Modal */}
        {showRevisionModal && (
          <Dialog open={showRevisionModal} onOpenChange={setShowRevisionModal}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Request Revision</DialogTitle>
                <DialogDescription>
                  Please explain what needs to be changed or improved.
                  {packageRevisions !== null && (
                    <div className="mt-2 text-sm">
                      <Badge variant="outline" className="mr-2">
                        {revisionsRemaining} revision{revisionsRemaining !== 1 ? 's' : ''} remaining
                      </Badge>
                    </div>
                  )}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="revision-reason">Revision Reason *</Label>
                  <Textarea
                    id="revision-reason"
                    value={revisionReason}
                    onChange={(e) => setRevisionReason(e.target.value)}
                    placeholder="Please describe what needs to be revised..."
                    rows={4}
                    className="mt-1"
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button 
                    onClick={() => {
                      if (revisionReason.trim()) {
                        handleDeliveryAction('revision', revisionReason.trim());
                        setShowRevisionModal(false);
                        setRevisionReason('');
                      }
                    }}
                    disabled={!revisionReason.trim()}
                    className="flex-1"
                  >
                    Request Revision
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowRevisionModal(false);
                      setRevisionReason('');
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Review Modal */}
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          orderId={orderId}
          onReviewSubmitted={() => {
            setShowReviewModal(false);
            fetchOrderData(); // Refresh order data
          }}
        />
      </div>
    </div>
  );
};

// Drill-Down Modal Component
const DrillDownModal = ({ data, type, isOpen, onClose }) => {
  if (!data) return null;

  const renderContent = () => {
    switch (type) {
      case 'seller':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-600">Name</Label>
                <p className="text-lg font-semibold">{data.name}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-600">Email</Label>
                <p>{data.email}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-600">Company</Label>
                <p>{data.company || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-600">Phone</Label>
                <p>{data.phone || 'N/A'}</p>
              </div>
            </div>
            
            {data.listings && data.listings.length > 0 && (
              <div>
                <Label className="text-sm font-medium text-gray-600 mb-2 block">Recent Listings</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {data.listings.map((listing, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium">{listing.title}</p>
                      <p className="text-sm text-gray-600">{listing.service_category}</p>
                      <p className="text-sm text-green-600">${(listing.price_cents / 100).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      
      case 'buyer':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-600">Name</Label>
                <p className="text-lg font-semibold">{data.name}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-600">Email</Label>
                <p>{data.email}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-600">Company</Label>
                <p>{data.company || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-600">Phone</Label>
                <p>{data.phone || 'N/A'}</p>
              </div>
            </div>
            
            {data.orders && data.orders.length > 0 && (
              <div>
                <Label className="text-sm font-medium text-gray-600 mb-2 block">Recent Orders</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {data.orders.map((order, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium">{order.listing_title}</p>
                      <p className="text-sm text-gray-600">Status: {order.status}</p>
                      <p className="text-sm text-green-600">${(order.total_cents / 100).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      
      case 'category':
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-600">Category</Label>
              <p className="text-lg font-semibold">{data.name}</p>
            </div>
            
            {data.listings && data.listings.length > 0 && (
              <div>
                <Label className="text-sm font-medium text-gray-600 mb-2 block">Listings in Category</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {data.listings.map((listing, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium">{listing.title}</p>
                      <p className="text-sm text-gray-600">by {listing.seller_name}</p>
                      <p className="text-sm text-green-600">${(listing.price_cents / 100).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {data.stats && (
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{data.stats.total_listings || 0}</p>
                  <p className="text-sm text-gray-600">Total Listings</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">${((data.stats.total_revenue || 0) / 100).toFixed(2)}</p>
                  <p className="text-sm text-gray-600">Total Revenue</p>
                </div>
              </div>
            )}
          </div>
        );
      
      default:
        return <p>No data available</p>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {type === 'seller' && 'Seller Details'}
            {type === 'buyer' && 'Buyer Details'}
            {type === 'category' && 'Category Details'}
          </DialogTitle>
          <DialogDescription>
            Detailed information and related data
          </DialogDescription>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

// Simple Cache Utility for Dashboard API Responses
class DashboardCache {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes in milliseconds
  }

  generateKey(url, params = {}) {
    const sortedParams = Object.keys(params).sort().reduce((result, key) => {
      result[key] = params[key];
      return result;
    }, {});
    return `${url}?${new URLSearchParams(sortedParams).toString()}`;
  }

  get(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    if (Date.now() > cached.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  set(key, data, ttl = this.defaultTTL) {
    this.cache.set(key, {
      data,
      expiry: Date.now() + ttl,
      timestamp: Date.now()
    });
  }

  clear() {
    this.cache.clear();
  }

  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Global cache instance
const dashboardCache = new DashboardCache();

// Cached Axios wrapper for dashboard APIs
const cachedDashboardGet = async (url, params = {}, options = {}) => {
  const cacheKey = dashboardCache.generateKey(url, params);
  const cachedData = dashboardCache.get(cacheKey);
  
  if (cachedData && !options.skipCache) {
    console.log(`Cache hit for: ${cacheKey}`);
    return { data: cachedData };
  }
  
  console.log(`Cache miss for: ${cacheKey}`);
  const fullUrl = params ? `${url}?${new URLSearchParams(params).toString()}` : url;
  const response = await axios.get(fullUrl);
  
  // Cache the response data
  dashboardCache.set(cacheKey, response.data, options.ttl);
  
  return response;
};

// Admin Dashboard Component - Phase 3 with Commission Management
const AdminDashboard = () => {
  const [filters, setFilters] = useState({
    date_range: 'last_90',
    group_by: 'month',
    status_filter: 'completed',
    categories: []
  });
  const [kpiData, setKpiData] = useState(null);
  const [topSellers, setTopSellers] = useState([]);
  const [topBuyers, setTopBuyers] = useState([]);
  const [chartData, setChartData] = useState({});
  const [buyerCohorts, setBuyerCohorts] = useState([]);
  const [repeatCustomers, setRepeatCustomers] = useState([]);
  const [deliverySla, setDeliverySla] = useState([]);
  const [pricingModelMix, setPricingModelMix] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drillDownData, setDrillDownData] = useState(null);
  const [drillDownType, setDrillDownType] = useState(null);
  const [isDrillDownOpen, setIsDrillDownOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    document.title = 'Admin Dashboard | Rnkem';
  }, []);

  useEffect(() => {
    fetchAllDashboardData();
  }, [filters]);

  // Cache control functions
  const refreshCache = async () => {
    try {
      setLoading(true);
      dashboardCache.clear();
      await fetchAllDashboardData();
      toast.success('Dashboard data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh dashboard data');
    }
  };

  const getCacheStats = () => {
    return dashboardCache.getStats();
  };

  const fetchAllDashboardData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        date_range: filters.date_range,
        group_by: filters.group_by,
        status_filter: filters.status_filter
      });
      
      if (filters.categories?.length > 0) {
        filters.categories.forEach(cat => params.append('categories', cat));
      }

      // Fetch all data in parallel with caching
      const paramsObj = Object.fromEntries(params.entries());
      const [kpiResponse, sellersResponse, buyersResponse, cohortsResponse, repeatResponse, slaResponse, pricingResponse] = await Promise.all([
        cachedDashboardGet(`${API}/admin/dashboard/kpis`, paramsObj),
        cachedDashboardGet(`${API}/admin/dashboard/top-sellers`, paramsObj),
        cachedDashboardGet(`${API}/admin/dashboard/top-buyers`, paramsObj),
        cachedDashboardGet(`${API}/admin/dashboard/buyer-cohorts`, paramsObj),
        cachedDashboardGet(`${API}/admin/dashboard/repeat-customers`, paramsObj),
        cachedDashboardGet(`${API}/admin/dashboard/delivery-sla`, paramsObj),
        cachedDashboardGet(`${API}/admin/dashboard/pricing-model-mix`, paramsObj)
      ]);

      setKpiData(kpiResponse.data);
      setTopSellers(sellersResponse.data);
      setTopBuyers(buyersResponse.data);
      setBuyerCohorts(cohortsResponse.data);
      setRepeatCustomers(repeatResponse.data);
      setDeliverySla(slaResponse.data);
      setPricingModelMix(pricingResponse.data);

      // Fetch chart data with caching
      const chartTypes = ['users_by_month', 'orders_by_month', 'gmv_by_month', 'gmv_by_category'];
      const chartPromises = chartTypes.map(type => 
        cachedDashboardGet(`${API}/admin/dashboard/chart-data/${type}`, paramsObj)
      );

      const chartResponses = await Promise.all(chartPromises);
      const newChartData = {};
      chartTypes.forEach((type, index) => {
        newChartData[type] = chartResponses[index].data;
      });
      setChartData(newChartData);

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatPricingModel = (model) => {
    switch (model) {
      case 'per_lead':
        return 'Per Lead';
      case 'hourly':
        return 'Hourly Rate';
      case 'project':
        return 'Project-based';
      case 'fixed':
        return 'Fixed Price';
      case 'package':
        return 'Package Deal';
      default:
        return model ? model.charAt(0).toUpperCase() + model.slice(1) : 'Fixed';
    }
  };

  const exportToCSV = (data, filename, customHeaders = null) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Use custom headers if provided, otherwise use object keys
    const headers = customHeaders || Object.keys(data[0]);
    
    const csvContent = [
      headers.join(','), // Headers
      ...data.map(row => headers.map(header => {
        let val = row[header];
        
        // Handle null/undefined
        if (val === null || val === undefined) {
          return '';
        }
        
        // Handle objects and arrays - convert to JSON string
        if (typeof val === 'object') {
          if (Array.isArray(val)) {
            val = val.join('; '); // Join arrays with semicolon
          } else {
            val = JSON.stringify(val); // Convert objects to JSON
          }
        }
        
        // Convert numbers to strings with proper formatting
        if (typeof val === 'number') {
          // Only convert specific cent fields to dollars - be very specific
          if (header.toLowerCase().includes('price_cents') || header.toLowerCase().includes('aov_cents') || 
              header.toLowerCase().includes('gmv_cents') || header.toLowerCase() === 'cents') {
            val = (val / 100).toFixed(2); // Convert cents to dollars
          } else if (header.toLowerCase().includes('percentage') || header.toLowerCase().includes('rate')) {
            val = val.toFixed(2); // Don't add % here, let it be a pure number
          } else {
            val = val.toString(); // Keep all other numbers as-is
          }
        }
        
        // Handle boolean values
        if (typeof val === 'boolean') {
          val = val ? 'Yes' : 'No';
        }
        
        // Handle dates
        if (header.toLowerCase().includes('date') || header.toLowerCase().includes('_at') || header.toLowerCase().includes('time')) {
          if (val && typeof val === 'string') {
            try {
              const date = new Date(val);
              if (!isNaN(date.getTime())) {
                val = date.toLocaleDateString();
              }
            } catch (e) {
              // Keep original value if date parsing fails
            }
          }
        }
        
        // Convert to string and escape CSV special characters
        val = String(val);
        if (val.includes(',') || val.includes('"') || val.includes('\n')) {
          val = `"${val.replace(/"/g, '""')}"`;
        }
        
        return val;
      }).join(','))
    ].join('\n');

    // Add metadata header
    const now = new Date().toISOString().split('T')[0];
    const timestamp = new Date().toLocaleString();
    const metadataHeader = `# Rnkem Analytics Export - ${filename}\n# Generated: ${timestamp}\n# Date Range: ${filters.date_range}\n# Total Records: ${data.length}\n\n`;
    
    const fullContent = metadataHeader + csvContent;

    const blob = new Blob([fullContent], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rnkem_${filename}_${filters.date_range}_${now}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success(`${filename} exported successfully (${data.length} records)`);
  };

  // Drill-down functionality
  const handleSellerDrillDown = async (seller) => {
    try {
      setLoading(true);
      const response = await cachedDashboardGet(`${API}/admin/dashboard/seller-details/${seller.user_id}`, {}, { ttl: 2 * 60 * 1000 }); // 2-minute cache
      setDrillDownData({
        ...response.data,
        name: seller.name,
        type: 'seller'
      });
      setDrillDownType('seller');
      setIsDrillDownOpen(true);
    } catch (error) {
      console.error('Failed to fetch seller details:', error);
      toast.error('Failed to load seller details');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyerDrillDown = async (buyer) => {
    try {
      setLoading(true);
      const response = await cachedDashboardGet(`${API}/admin/dashboard/buyer-details/${buyer.user_id}`, {}, { ttl: 2 * 60 * 1000 }); // 2-minute cache
      setDrillDownData({
        ...response.data,
        name: buyer.name,
        type: 'buyer'
      });
      setDrillDownType('buyer');
      setIsDrillDownOpen(true);
    } catch (error) {
      console.error('Failed to fetch buyer details:', error);
      toast.error('Failed to load buyer details');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryDrillDown = async (categoryName) => {
    try {
      setLoading(true);
      const response = await cachedDashboardGet(`${API}/admin/dashboard/category-details/${encodeURIComponent(categoryName)}`, {}, { ttl: 2 * 60 * 1000 }); // 2-minute cache
      setDrillDownData({
        ...response.data,
        name: categoryName,
        type: 'category'
      });
      setDrillDownType('category');
      setIsDrillDownOpen(true);
    } catch (error) {
      console.error('Failed to fetch category details:', error);
      toast.error('Failed to load category details');
    } finally {
      setLoading(false);
    }
  };

  const closeDrillDown = () => {
    setIsDrillDownOpen(false);
    setDrillDownData(null);
    setDrillDownType(null);
  };

  // Enhanced export function for full dataset (bypasses pagination)
  const exportFullDataset = async (endpoint, filename, customHeaders = null) => {
    try {
      const params = new URLSearchParams({
        ...filters,
        date_range: filters.date_range || 'last_90'
      });
      
      const response = await axios.get(`${API}${endpoint}?${params.toString()}`);
      console.log(`Export Debug - ${filename}:`, response.data); // Debug log
      
      let exportData;
      if (endpoint.includes('kpis')) {
        // For KPIs, create a structured export - first log the actual data structure
        const kpiData = response.data;
        console.log('KPI Export Debug - Raw Data:', kpiData);
        
        exportData = [
          { 
            metric: 'Active Users', 
            current_value: kpiData.active_users?.current_value ?? kpiData.active_users ?? 0, 
            previous_value: kpiData.active_users?.previous_value ?? 0,
            delta: kpiData.active_users?.delta ?? 0,
            date_range: filters.date_range 
          },
          { 
            metric: 'New Users', 
            current_value: kpiData.new_users?.current_value ?? kpiData.new_users ?? 0,
            previous_value: kpiData.new_users?.previous_value ?? 0, 
            delta: kpiData.new_users?.delta ?? 0,
            date_range: filters.date_range 
          },
          { 
            metric: 'Orders Placed', 
            current_value: kpiData.orders_placed?.current_value ?? kpiData.orders_placed ?? 0,
            previous_value: kpiData.orders_placed?.previous_value ?? 0,
            delta: kpiData.orders_placed?.delta ?? 0, 
            date_range: filters.date_range 
          },
          { 
            metric: 'GMV (USD)', 
            current_value: kpiData.gmv?.current_value ?? kpiData.gmv ?? 0,
            previous_value: kpiData.gmv?.previous_value ?? 0,
            delta: kpiData.gmv?.delta ?? 0,
            gmv_cents: kpiData.gmv?.current_value ?? kpiData.gmv ?? 0,
            gmv_dollars: ((kpiData.gmv?.current_value ?? kpiData.gmv ?? 0) / 100).toFixed(2),
            date_range: filters.date_range 
          },
          { 
            metric: 'AOV (USD)', 
            current_value: kpiData.aov?.current_value ?? kpiData.aov ?? 0,
            previous_value: kpiData.aov?.previous_value ?? 0,
            delta: kpiData.aov?.delta ?? 0,
            aov_cents: kpiData.aov?.current_value ?? kpiData.aov ?? 0,
            aov_dollars: ((kpiData.aov?.current_value ?? kpiData.aov ?? 0) / 100).toFixed(2),
            date_range: filters.date_range 
          },
          { 
            metric: 'Unique Buyers', 
            current_value: kpiData.unique_buyers?.current_value ?? kpiData.unique_buyers ?? 0,
            previous_value: kpiData.unique_buyers?.previous_value ?? 0,
            delta: kpiData.unique_buyers?.delta ?? 0,
            date_range: filters.date_range 
          }
        ];
        
        console.log('KPI Export Debug - Formatted Data:', exportData);
      } else {
        // For other endpoints, use the data directly
        exportData = Array.isArray(response.data) ? response.data : [response.data];
        
        // If data is empty, show a message
        if (!exportData || exportData.length === 0) {
          toast.error(`No data available for ${filename}`);
          return;
        }
      }
      
      exportToCSV(exportData, filename, customHeaders);
    } catch (error) {
      console.error('Export failed:', error);
      if (error.response?.status === 404) {
        toast.error(`Export endpoint not found: ${endpoint}`);
      } else {
        toast.error(`Failed to export ${filename}. Please try again.`);
      }
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return Math.round(num).toString();
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
    return `$${amount.toFixed(0)}`;
  };

  const renderKPICard = (title, data, format = 'number') => {
    if (!data) return null;

    const value = format === 'currency' ? formatCurrency(data.current_value) : formatNumber(data.current_value);
    const deltaText = data.delta_percentage > 0 ? `+${data.delta_percentage.toFixed(1)}%` : `${data.delta_percentage.toFixed(1)}%`;
    const trendColor = data.trend === 'up' ? 'text-green-700' : data.trend === 'down' ? 'text-red-700' : 'text-gray-700';
    const trendBgColor = data.trend === 'up' ? 'bg-green-50 border-green-200' : data.trend === 'down' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200';
    const arrow = data.trend === 'up' ? '↗️' : data.trend === 'down' ? '↘️' : '➡️';

    return (
      <Card key={title} className="hover:shadow-md transition-shadow duration-200 border-l-4 border-l-blue-500">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">{title}</p>
              <p className="text-3xl lg:text-4xl font-bold text-gray-900 mb-1">{value}</p>
              <p className="text-xs text-gray-500">Current period</p>
            </div>
            <div className={`text-right ${trendBgColor} px-3 py-2 rounded-lg border`}>
              <div className="flex items-center gap-1 mb-1">
                <span role="img" aria-label={`Trend ${data.trend}`}>{arrow}</span>
                <span className={`text-sm font-bold ${trendColor}`}>{deltaText}</span>
              </div>
              <p className="text-xs text-gray-600 font-medium">vs previous</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50" role="main" aria-label="Loading Admin Dashboard">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="mb-8">
            <div className="h-8 bg-gray-200 rounded w-64 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-100 rounded w-32 animate-pulse"></div>
          </div>
          
          {/* Loading Skeletons */}
          <div className="space-y-8">
            {/* KPI Skeletons */}
            <section aria-label="Loading KPI cards">
              <div className="h-6 bg-gray-200 rounded w-48 mb-4 animate-pulse"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            </section>
            
            {/* Chart Skeletons */}
            <section aria-label="Loading charts">
              <div className="h-6 bg-gray-200 rounded w-40 mb-4 animate-pulse"></div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <SkeletonChart key={i} />
                ))}
              </div>
            </section>
            
            {/* Table Skeletons */}
            <section aria-label="Loading tables">
              <div className="h-6 bg-gray-200 rounded w-48 mb-4 animate-pulse"></div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <SkeletonTable rows={10} columns={4} />
                <SkeletonTable rows={10} columns={4} />
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
        <Badge variant="outline" className="bg-blue-100 text-blue-800">
          {user?.role?.toUpperCase()}
        </Badge>
      </div>

        {/* Enhanced Filters Bar with Better Accessibility */}
        <Card className="mb-8 shadow-sm border-t-4 border-t-blue-500">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-gray-900">Dashboard Filters</CardTitle>
                <CardDescription className="text-sm text-gray-600 mt-1">
                  Customize your analytics view and data range
                </CardDescription>
              </div>
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 font-medium">
                Live Data
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 items-end">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-800">Date Range</Label>
                <Select
                  value={filters.date_range}
                  onValueChange={(value) => setFilters({...filters, date_range: value})}
                >
                  <SelectTrigger className="w-full bg-white border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="last_30">Last 30 Days</SelectItem>
                    <SelectItem value="last_90">Last 90 Days</SelectItem>
                    <SelectItem value="mtd">Month to Date</SelectItem>
                    <SelectItem value="qtd">Quarter to Date</SelectItem>
                    <SelectItem value="ytd">Year to Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-800">Group By</Label>
                <Select
                  value={filters.group_by}
                  onValueChange={(value) => setFilters({...filters, group_by: value})}
                >
                  <SelectTrigger className="w-full bg-white border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="week">Week</SelectItem>
                    <SelectItem value="month">Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 sm:col-span-2 lg:col-span-1">
                <Label className="text-sm font-semibold text-gray-800">Status</Label>
                <Select
                  value={filters.status_filter}
                  onValueChange={(value) => setFilters({...filters, status_filter: value})}
                >
                  <SelectTrigger className="w-full bg-white border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">Completed Only</SelectItem>
                    <SelectItem value="completed_delivered">Completed + Delivered</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Enhanced Cache Control Section */}
              <div className="sm:col-span-2 lg:col-span-2 xl:col-span-2 space-y-2">
                <Label className="text-sm font-semibold text-gray-800">Cache Control</Label>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 text-xs text-gray-700 font-medium">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" title="Cache active"></div>
                    <span>{getCacheStats().size} cached items</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={refreshCache}
                    disabled={loading}
                    className="flex items-center gap-2 text-xs font-medium bg-white hover:bg-gray-50 border-gray-300 focus:ring-2 focus:ring-blue-200"
                    title="Clear cache and refresh all dashboard data"
                  >
                    {loading ? (
                      <div className="animate-spin h-3 w-3 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    ) : (
                      <span role="img" aria-hidden="true">🔄</span>
                    )}
                    <span className="hidden sm:inline">Refresh Data</span>
                    <span className="sm:hidden">Refresh</span>
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards */}
        <section className="mb-8" aria-labelledby="kpi-heading">
          <div className="flex justify-between items-center mb-4">
            <h2 id="kpi-heading" className="text-xl font-semibold text-gray-900">Key Performance Indicators</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportFullDataset('/admin/dashboard/kpis', 'kpi_summary')}
              disabled={!kpiData}
              aria-label="Export KPI data to CSV"
            >
              Export KPIs
            </Button>
          </div>
          {kpiData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {renderKPICard('Active Users', kpiData.active_users)}
              {renderKPICard('New Users', kpiData.new_users)}
              {renderKPICard('Orders Placed', kpiData.orders_placed)}
              {renderKPICard('GMV', kpiData.gmv, 'currency')}
              {renderKPICard('AOV', kpiData.aov, 'currency')}
              {renderKPICard('Unique Buyers', kpiData.unique_buyers)}
            </div>
          ) : (
            <EmptyStateCard
              title="KPI Data Unavailable"
              description="Key performance indicators will appear here once data is available for the selected date range."
              icon="📊"
              action={
                <Button 
                  onClick={() => window.location.reload()} 
                  variant="outline"
                  aria-label="Refresh dashboard data"
                >
                  Refresh Data
                </Button>
              }
            />
          )}
        </section>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <ChartCard
            title="Users by Month"
            chartData={chartData.users_by_month}
            onExport={() => {
              if (chartData.users_by_month) {
                const chartExportData = chartData.users_by_month.labels?.map((label, index) => ({
                  month: label,
                  users: chartData.users_by_month.datasets?.[0]?.data?.[index] || 0
                })) || [];
                exportToCSV(chartExportData, 'users_by_month');
              }
            }}
          />
          <ChartCard
            title="Orders by Month"
            chartData={chartData.orders_by_month}
            onExport={() => {
              if (chartData.orders_by_month) {
                const chartExportData = chartData.orders_by_month.labels?.map((label, index) => ({
                  month: label,
                  orders: chartData.orders_by_month.datasets?.[0]?.data?.[index] || 0
                })) || [];
                exportToCSV(chartExportData, 'orders_by_month');
              }
            }}
          />
          <ChartCard
            title="GMV by Month"
            chartData={chartData.gmv_by_month}
            onExport={() => {
              if (chartData.gmv_by_month) {
                console.log('GMV by Month Chart Data:', chartData.gmv_by_month);
                const chartExportData = chartData.gmv_by_month.labels?.map((label, index) => ({
                  month: label,
                  gmv_raw_value: chartData.gmv_by_month.datasets?.[0]?.data?.[index] || 0,
                  gmv_dollars: chartData.gmv_by_month.datasets?.[0]?.data?.[index] || 0
                })) || [];
                console.log('GMV by Month Export Data:', chartExportData);
                exportToCSV(chartExportData, 'gmv_by_month');
              }
            }}
          />
          <ChartCard
            title="GMV by Category"
            chartData={chartData.gmv_by_category}
            chartType="doughnut"
            onExport={() => {
              if (chartData.gmv_by_category) {
                console.log('GMV by Category Chart Data:', chartData.gmv_by_category);
                const chartExportData = chartData.gmv_by_category.labels?.map((label, index) => ({
                  category: label,
                  gmv_raw_value: chartData.gmv_by_category.datasets?.[0]?.data?.[index] || 0,
                  gmv_dollars: chartData.gmv_by_category.datasets?.[0]?.data?.[index] || 0
                })) || [];
                console.log('GMV by Category Export Data:', chartExportData);
                exportToCSV(chartExportData, 'gmv_by_category');
              }
            }}
            onChartClick={(categoryName) => handleCategoryDrillDown(categoryName)}
          />
        </div>

        {/* Tables Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Sellers */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Top 10 Sellers</CardTitle>
                  <CardDescription>By revenue in selected period</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (topSellers.length > 0) {
                      exportToCSV(topSellers, 'top_sellers');
                    } else {
                      exportFullDataset('/admin/dashboard/top-sellers', 'top_sellers');
                    }
                  }}
                  disabled={loading}
                >
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {topSellers.length === 0 ? (
                <EmptyTable 
                  title="No Top Sellers Data"
                  description="Seller performance data will appear here once there are completed orders in the selected date range."
                  icon="🏆"
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Seller</TableHead>
                      <TableHead>Orders</TableHead>
                      <TableHead>GMV</TableHead>
                      <TableHead>% Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topSellers.map((seller, index) => (
                      <TableRow 
                        key={seller.user_id}
                        className="cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => handleSellerDrillDown(seller)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div>
                              <p className="font-medium">{seller.name}</p>
                              <p className="text-sm text-gray-500">{seller.company}</p>
                              <p className="text-xs text-gray-400">Click for details</p>
                            </div>
                            {seller.is_vip && (
                              <Badge className="bg-yellow-100 text-yellow-800 text-xs">VIP</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{seller.orders}</TableCell>
                        <TableCell>{formatCurrency(seller.gmv)}</TableCell>
                        <TableCell>{seller.percentage_of_total.toFixed(1)}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Top Buyers */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Top 10 Buyers</CardTitle>
                  <CardDescription>By total spend in selected period</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (topBuyers.length > 0) {
                      exportToCSV(topBuyers, 'top_buyers');
                    } else {
                      exportFullDataset('/admin/dashboard/top-buyers', 'top_buyers');
                    }
                  }}
                  disabled={loading}
                >
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {topBuyers.length === 0 ? (
                <EmptyTable 
                  title="No Top Buyers Data"
                  description="Buyer performance data will appear here once there are completed purchases in the selected date range."
                  icon="🛒"
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Buyer</TableHead>
                      <TableHead>Orders</TableHead>
                      <TableHead>Total Spend</TableHead>
                      <TableHead>Last Order</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topBuyers.map((buyer, index) => (
                      <TableRow 
                        key={buyer.user_id}
                        className="cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => handleBuyerDrillDown(buyer)}
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium">{buyer.name}</p>
                            <p className="text-sm text-gray-500">{buyer.company}</p>
                            <p className="text-xs text-gray-400">Click for details</p>
                          </div>
                        </TableCell>
                        <TableCell>{buyer.orders}</TableCell>
                        <TableCell>{formatCurrency(buyer.gmv)}</TableCell>
                        <TableCell>
                          {new Date(buyer.last_order_date).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Additional Analytics Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Buyer Cohorts */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Buyer Cohorts</CardTitle>
                  <CardDescription>Customer retention by registration month</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (buyerCohorts.length > 0) {
                      exportToCSV(buyerCohorts, 'buyer_cohorts');
                    } else {
                      exportFullDataset('/admin/dashboard/buyer-cohorts', 'buyer_cohorts');
                    }
                  }}
                  disabled={loading}
                >
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {buyerCohorts.length === 0 ? (
                <EmptyTable 
                  title="No Cohort Data"
                  description="Buyer cohort analysis will appear here once there are multiple months of user registration data."
                  icon="👥"
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cohort Month</TableHead>
                      <TableHead>Users</TableHead>
                      <TableHead>Active</TableHead>
                      <TableHead>Retention %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {buyerCohorts.map((cohort, index) => (
                      <TableRow key={index}>
                        <TableCell>{cohort.month}</TableCell>
                        <TableCell>{cohort.total_users}</TableCell>
                        <TableCell>{cohort.active_users}</TableCell>
                        <TableCell>
                          <span className={`font-medium ${cohort.retention_rate > 50 ? 'text-green-600' : cohort.retention_rate > 25 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {cohort.retention_rate.toFixed(1)}%
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Repeat Customers */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Repeat Customers</CardTitle>
                  <CardDescription>Customer loyalty and repeat purchase behavior</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (repeatCustomers.length > 0) {
                      exportToCSV(repeatCustomers, 'repeat_customers');
                    } else {
                      exportFullDataset('/admin/dashboard/repeat-customers', 'repeat_customers');
                    }
                  }}
                  disabled={loading}
                >
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {repeatCustomers.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No repeat customer data available</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Total Orders</TableHead>
                      <TableHead>Lifetime Value</TableHead>
                      <TableHead>Avg Order Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {repeatCustomers.map((customer, index) => (
                      <TableRow key={customer.user_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{customer.name}</p>
                            <p className="text-sm text-gray-500">{customer.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{customer.total_orders}</span>
                        </TableCell>
                        <TableCell>{formatCurrency(customer.lifetime_value)}</TableCell>
                        <TableCell>{formatCurrency(customer.avg_order_value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Performance Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Delivery SLA */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Delivery SLA Performance</CardTitle>
                  <CardDescription>Service delivery time analysis</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (deliverySla.length > 0) {
                      exportToCSV(deliverySla, 'delivery_sla');
                    } else {
                      exportFullDataset('/admin/dashboard/delivery-sla', 'delivery_sla');
                    }
                  }}
                  disabled={loading}
                >
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {deliverySla.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No delivery data available</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service Category</TableHead>
                      <TableHead>Avg Delivery Time</TableHead>
                      <TableHead>On-Time %</TableHead>
                      <TableHead>Late Orders</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deliverySla.map((sla, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{sla.category}</TableCell>
                        <TableCell>{sla.avg_delivery_hours}h</TableCell>
                        <TableCell>
                          <span className={`font-medium ${sla.on_time_percentage > 90 ? 'text-green-600' : sla.on_time_percentage > 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {sla.on_time_percentage.toFixed(1)}%
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-red-600">{sla.late_orders}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Pricing Model Mix */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Pricing Model Distribution</CardTitle>
                  <CardDescription>Breakdown of listing pricing models</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (pricingModelMix.length > 0) {
                      exportToCSV(pricingModelMix, 'pricing_model_mix');
                    } else {
                      exportFullDataset('/admin/dashboard/pricing-model-mix', 'pricing_model_mix');
                    }
                  }}
                  disabled={loading}
                >
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {pricingModelMix.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No pricing model data available</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pricing Model</TableHead>
                      <TableHead>Listings</TableHead>
                      <TableHead>Percentage</TableHead>
                      <TableHead>Avg Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pricingModelMix.map((model, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{formatPricingModel(model.pricing_model)}</TableCell>
                        <TableCell>{model.listing_count}</TableCell>
                        <TableCell>
                          <span className="font-medium">{model.percentage.toFixed(1)}%</span>
                        </TableCell>
                        <TableCell>{formatCurrency(model.avg_price)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
    </>
  );
};

// Landing Page Settings Admin Component
const AdminLandingSettings = () => {
  const [settings, setSettings] = useState({
    landing_rotation_enabled: 'true',
    landing_rotate_interval_ms: '2500',
    landing_rotating_phrases: '["bad leads again","bad facebook ads again","bad google ads again","bad nextdoor ads again","bad websites again","bad seo again"]'
  });
  const [phrases, setPhrases] = useState([]);
  const [newPhrase, setNewPhrase] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  
  const { user } = useAuth();

  useEffect(() => {
    document.title = 'Landing Page Settings | Admin Portal';
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/landing-settings`);
      setSettings(response.data);
      
      // Parse phrases from JSON
      const phrasesArray = JSON.parse(response.data.landing_rotating_phrases || '[]');
      setPhrases(phrasesArray);
    } catch (error) {
      console.error('Failed to fetch landing settings:', error);
      toast.error('Failed to load landing settings');
    } finally {
      setLoading(false);
    }
  };

  const validatePhrase = (phrase) => {
    if (!phrase || phrase.trim().length === 0) {
      return 'Phrase cannot be empty';
    }
    if (phrase.length > 50) {
      return 'Phrase must be 50 characters or less';
    }
    if (/<[^>]*>/.test(phrase)) {
      return 'HTML tags are not allowed';
    }
    if (phrases.some(p => p.toLowerCase() === phrase.toLowerCase())) {
      return 'Duplicate phrases are not allowed';
    }
    return null;
  };

  const addPhrase = () => {
    const trimmedPhrase = newPhrase.trim();
    const error = validatePhrase(trimmedPhrase);
    
    if (error) {
      toast.error(error);
      return;
    }
    
    setPhrases([...phrases, trimmedPhrase]);
    setNewPhrase('');
  };

  const deletePhrase = (index) => {
    if (phrases.length <= 1 && settings.landing_rotation_enabled === 'true') {
      toast.error('At least one phrase is required when rotation is enabled');
      return;
    }
    setPhrases(phrases.filter((_, i) => i !== index));
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    setEditingValue(phrases[index]);
  };

  const saveEdit = () => {
    const trimmedValue = editingValue.trim();
    const error = validatePhrase(trimmedValue);
    
    if (error) {
      toast.error(error);
      return;
    }
    
    const newPhrases = [...phrases];
    newPhrases[editingIndex] = trimmedValue;
    setPhrases(newPhrases);
    setEditingIndex(null);
    setEditingValue('');
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingValue('');
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null) return;
    
    const newPhrases = [...phrases];
    const draggedItem = newPhrases[draggedIndex];
    
    newPhrases.splice(draggedIndex, 1);
    newPhrases.splice(dropIndex, 0, draggedItem);
    
    setPhrases(newPhrases);
    setDraggedIndex(null);
  };

  const resetToRecommended = () => {
    const recommended = [
      "bad leads again",
      "bad facebook ads again", 
      "bad google ads again",
      "bad nextdoor ads again",
      "bad websites again",
      "bad seo again"
    ];
    setPhrases(recommended);
    toast.success('Phrases reset to recommended defaults');
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      if (settings.landing_rotation_enabled === 'true' && phrases.length === 0) {
        toast.error('At least one phrase is required when rotation is enabled');
        return;
      }
      
      const updatedSettings = {
        ...settings,
        landing_rotating_phrases: JSON.stringify(phrases)
      };
      
      await axios.post(`${API}/admin/landing-settings`, updatedSettings);
      
      setSettings(updatedSettings);
      setLastUpdated(new Date().toLocaleString());
      toast.success('Landing page settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading landing page settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Landing Page Settings</h2>
        <p className="text-gray-600 mt-1">Manage rotating hero phrases and display settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Settings Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Rotating Phrase Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                🔄 Rotating Phrase Settings
              </CardTitle>
              <CardDescription>
                Configure how phrases rotate in the hero section
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Enable Toggle */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="rotation-enabled"
                  checked={settings.landing_rotation_enabled === 'true'}
                  onChange={(e) => setSettings({
                    ...settings,
                    landing_rotation_enabled: e.target.checked ? 'true' : 'false'
                  })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label htmlFor="rotation-enabled" className="text-sm font-medium">
                  Enable phrase rotation
                </Label>
              </div>

              {/* Interval Input */}
              <div>
                <Label htmlFor="interval" className="text-sm font-medium">
                  Rotation Interval (milliseconds)
                </Label>
                <Input
                  id="interval"
                  type="number"
                  min="1000"
                  max="10000"
                  step="100"
                  value={settings.landing_rotate_interval_ms}
                  onChange={(e) => setSettings({
                    ...settings,
                    landing_rotate_interval_ms: e.target.value
                  })}
                  className="mt-1"
                  placeholder="2500"
                />
                <p className="text-xs text-gray-500 mt-1">Recommended: 2500ms (2.5 seconds)</p>
              </div>
            </CardContent>
          </Card>

          {/* Phrases Editor */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    ✏️ Phrases Editor
                  </CardTitle>
                  <CardDescription>
                    Add, edit, reorder, and manage rotating phrases (max 50 chars each)
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetToRecommended}
                  className="text-xs"
                >
                  Reset to Recommended
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add New Phrase */}
              <div className="flex gap-2">
                <Input
                  value={newPhrase}
                  onChange={(e) => setNewPhrase(e.target.value)}
                  placeholder="Enter new phrase (e.g., 'bad leads again')"
                  maxLength={50}
                  onKeyPress={(e) => e.key === 'Enter' && addPhrase()}
                  className="flex-1"
                />
                <Button
                  onClick={addPhrase}
                  disabled={!newPhrase.trim()}
                  size="sm"
                >
                  Add
                </Button>
              </div>

              {/* Phrases List */}
              <div className="space-y-2">
                {phrases.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No phrases added yet</p>
                ) : (
                  phrases.map((phrase, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border hover:bg-gray-100 cursor-move"
                    >
                      <span className="text-gray-400">⋮⋮</span>
                      {editingIndex === index ? (
                        <>
                          <Input
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            maxLength={50}
                            className="flex-1"
                            autoFocus
                          />
                          <Button size="sm" variant="outline" onClick={saveEdit}>✓</Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit}>✕</Button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 font-mono text-sm">{phrase}</span>
                          <Button size="sm" variant="ghost" onClick={() => startEdit(index)}>
                            ✏️
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => deletePhrase(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            🗑️
                          </Button>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Publishing & Audit */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                🚀 Publishing & Audit
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {lastUpdated && (
                <div className="text-sm text-gray-600">
                  <strong>Last updated:</strong> {lastUpdated} by {user?.name}
                </div>
              )}
              
              <div className="flex gap-2">
                <Button
                  onClick={saveSettings}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Live Preview Panel */}
        <div className="lg:col-span-1">
          <Card className="sticky top-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                👁️ Live Preview
              </CardTitle>
              <CardDescription>
                Preview how the hero will look with current settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-900 text-white p-6 rounded-lg">
                <h1 className="text-2xl font-bold mb-4">
                  Never waste money on{' '}
                  <span className="text-orange-400">
                    {settings.landing_rotation_enabled === 'true' && phrases.length > 0 ? (
                      <RotatingPhrasePreview 
                        phrases={phrases}
                        intervalMs={parseInt(settings.landing_rotate_interval_ms) || 2500}
                      />
                    ) : (
                      'bad services again'
                    )}
                  </span>
                </h1>
                <p className="text-gray-300 text-sm">
                  This is how your hero section will appear to visitors
                </p>
              </div>
              
              <div className="mt-4 text-sm text-gray-600 space-y-1">
                <div><strong>Status:</strong> {settings.landing_rotation_enabled === 'true' ? 'Enabled' : 'Disabled'}</div>
                <div><strong>Phrases:</strong> {phrases.length}</div>
                <div><strong>Interval:</strong> {settings.landing_rotate_interval_ms}ms</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Admin Stripe Settings Component
const AdminStripeSettings = () => {
  const [settings, setSettings] = useState({
    stripe_mode: 'test',
    stripe_secret_key: null,
    stripe_publishable_key: null,
    stripe_webhook_secret: null,
    stripe_connect_client_id: null,
    webhook_url: ''
  });
  const [webhookHealth, setWebhookHealth] = useState({
    last_delivery_timestamp: null,
    last_delivery_status: null,
    webhook_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [revealedKeys, setRevealedKeys] = useState(new Set());
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [revealKeyName, setRevealKeyName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  const { user } = useAuth();

  useEffect(() => {
    document.title = 'Stripe Settings | Admin Portal';
    fetchSettings();
    fetchWebhookHealth();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/stripe`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      } else {
        console.error('Failed to fetch stripe settings');
      }
    } catch (error) {
      console.error('Error fetching stripe settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWebhookHealth = async () => {
    try {
      const response = await fetch(`${API}/admin/stripe/webhook-health`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWebhookHealth(data);
      }
    } catch (error) {
      console.error('Error fetching webhook health:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {};
      
      // Only include non-null values
      Object.keys(settings).forEach(key => {
        if (key !== 'webhook_url' && settings[key] !== null && settings[key] !== '') {
          updateData[key] = settings[key];
        }
      });

      const response = await fetch(`${API}/admin/stripe`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        toast.success('Stripe settings saved successfully');
        // Re-fetch to get updated masked values
        fetchSettings();
        setRevealedKeys(new Set()); // Clear revealed keys after save
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRevealKey = async (keyName) => {
    if (!password) {
      setPasswordError('Password is required');
      return;
    }

    try {
      const response = await fetch(`${API}/admin/stripe/reveal-key?key_name=${keyName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ password_confirmation: password })
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          [keyName]: data.value
        }));
        setRevealedKeys(prev => new Set([...prev, keyName]));
        setShowPasswordDialog(false);
        setPassword('');
        setPasswordError('');
        toast.success('Key revealed successfully');
      } else {
        let errorMessage = 'Invalid password';
        try {
          const errorData = await response.json();
          
          // Handle different error response formats
          if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else if (errorData.detail) {
            if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            } else if (Array.isArray(errorData.detail)) {
              // Handle Pydantic validation errors
              errorMessage = errorData.detail.map(err => err.msg || err.type || 'Validation error').join(', ');
            } else {
              errorMessage = 'Invalid password';
            }
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
          errorMessage = `Server error (${response.status})`;
        }
        
        setPasswordError(errorMessage);
      }
    } catch (error) {
      console.error('Error revealing key:', error);
      setPasswordError('Failed to reveal key');
    }
  };

  const openRevealDialog = (keyName) => {
    setRevealKeyName(keyName);
    setShowPasswordDialog(true);
    setPassword('');
    setPasswordError('');
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(settings.webhook_url);
    toast.success('Webhook URL copied to clipboard');
  };

  const getModeColor = (mode) => {
    return mode === 'live' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stripe Settings</h1>
          <p className="text-gray-600">Configure Stripe payment processing settings</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* API Keys & Mode Card */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">API Keys & Mode</h2>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getModeColor(settings.stripe_mode)}`}>
              {settings.stripe_mode?.toUpperCase()} MODE
            </span>
          </div>

          <div className="space-y-4">
            {/* Mode Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stripe Mode
              </label>
              <select
                value={settings.stripe_mode}
                onChange={(e) => setSettings(prev => ({ ...prev, stripe_mode: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="test">Test</option>
                <option value="live">Live</option>
              </select>
            </div>

            {/* API Keys */}
            {[
              { key: 'stripe_publishable_key', label: 'Publishable Key', placeholder: 'pk_test_...' },
              { key: 'stripe_secret_key', label: 'Secret Key', placeholder: 'sk_test_...' },
              { key: 'stripe_webhook_secret', label: 'Webhook Secret', placeholder: 'whsec_...' },
              { key: 'stripe_connect_client_id', label: 'Connect Client ID', placeholder: 'ca_...' }
            ].map(({ key, label, placeholder }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {label}
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={settings[key] || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {settings[key] && settings[key].startsWith('•') && !revealedKeys.has(key) && (
                    <Button
                      onClick={() => openRevealDialog(key)}
                      variant="outline"
                      size="sm"
                      className="rounded-l-none border-l-0"
                    >
                      Reveal
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </Card>

        {/* Webhook Health Card */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Webhook Health</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Webhook URL
              </label>
              <div className="flex">
                <input
                  type="text"
                  value={settings.webhook_url}
                  readOnly
                  className="flex-1 border border-gray-300 rounded-l-md px-3 py-2 bg-gray-50 text-gray-600"
                />
                <Button
                  onClick={copyWebhookUrl}
                  variant="outline"
                  size="sm"
                  className="rounded-l-none border-l-0"
                >
                  Copy
                </Button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Configure this URL in your Stripe webhook settings
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Delivery
                </label>
                <p className="text-sm text-gray-600">
                  {webhookHealth.last_delivery_timestamp || 'No deliveries yet'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                  webhookHealth.last_delivery_status === 'success' 
                    ? 'bg-green-100 text-green-800'
                    : webhookHealth.last_delivery_status === 'failed'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {webhookHealth.last_delivery_status || 'Unknown'}
                </span>
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-md">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Setup Instructions:</h4>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Copy the webhook URL above</li>
                <li>Go to your Stripe Dashboard → Webhooks</li>
                <li>Add endpoint with the copied URL</li>
                <li>Select events you want to receive</li>
                <li>Copy the signing secret to Webhook Secret field</li>
              </ol>
            </div>
          </div>
        </Card>
      </div>

      {/* Password Reveal Dialog */}
      {showPasswordDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md">
            <h3 className="text-lg font-semibold mb-4">Confirm Password</h3>
            <p className="text-gray-600 mb-4">
              Enter your password to reveal the {revealKeyName.replace('stripe_', '').replace('_', ' ')}:
            </p>
            
            <div className="mb-4">
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError('');
                }}
                placeholder="Enter your password"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onKeyPress={(e) => e.key === 'Enter' && handleRevealKey(revealKeyName)}
              />
              {passwordError && (
                <p className="text-red-600 text-sm mt-1">{passwordError}</p>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                onClick={() => {
                  setShowPasswordDialog(false);
                  setPassword('');
                  setPasswordError('');
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button onClick={() => handleRevealKey(revealKeyName)}>
                Reveal Key
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Rotating Phrase Component with Smooth Cross-Fade + Slide Animation
const RotatingPhrase = ({ phrases = [], intervalMs = 2500, className = "" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isTabHidden, setIsTabHidden] = useState(false);
  const [longestPhraseWidth, setLongestPhraseWidth] = useState('auto');
  const measureRef = useRef();
  const animationTimeouts = useRef([]);

  // Animation configuration (configurable via settings)
  const animationConfig = {
    outDuration: 380,
    inDuration: 420, 
    overlap: 120,
    easing: 'cubic-bezier(.22,.61,.36,1)'
  };

  // Respect user's motion preferences
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setIsPaused(mediaQuery.matches);
    
    const handleChange = (e) => setIsPaused(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Handle tab visibility for performance
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabHidden(document.hidden);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Measure longest phrase width to prevent layout shift
  useEffect(() => {
    if (phrases.length > 0 && measureRef.current) {
      let maxWidth = 0;
      
      phrases.forEach(phrase => {
        measureRef.current.textContent = phrase;
        const width = measureRef.current.offsetWidth;
        if (width > maxWidth) {
          maxWidth = width;
        }
      });
      
      setLongestPhraseWidth(`${maxWidth + 4}px`); // Add small buffer
    }
  }, [phrases, className]);

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      animationTimeouts.current.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  // Smooth animation rotation logic
  useEffect(() => {
    if (phrases.length <= 1 || isHovered || isPaused || isTabHidden || isAnimating) return;
    
    const interval = setInterval(() => {
      const nextIdx = (currentIndex + 1) % phrases.length;
      setNextIndex(nextIdx);
      setIsAnimating(true);
      
      // Clear any existing timeouts
      animationTimeouts.current.forEach(timeout => clearTimeout(timeout));
      animationTimeouts.current = [];
      
      // Start fade-in animation after overlap delay
      const overlapTimeout = setTimeout(() => {
        setCurrentIndex(nextIdx);
        
        // Complete animation cycle
        const completeTimeout = setTimeout(() => {
          setIsAnimating(false);
        }, animationConfig.inDuration);
        
        animationTimeouts.current.push(completeTimeout);
      }, animationConfig.overlap);
      
      animationTimeouts.current.push(overlapTimeout);
      
    }, intervalMs);

    return () => clearInterval(interval);
  }, [phrases.length, intervalMs, isHovered, isPaused, isTabHidden, isAnimating, currentIndex]);

  if (phrases.length === 0) return <span className={className}>bad services again</span>;

  const currentPhrase = phrases[currentIndex] || phrases[0];
  const nextPhrase = phrases[nextIndex] || phrases[0];

  // Base styles for the wrapper
  const wrapperStyle = {
    position: 'relative',
    display: 'inline-block',
    minWidth: longestPhraseWidth,
    minHeight: '1.2em'
  };

  // Animation keyframes as CSS strings - Left to Right Fade
  const fadeSlideInKeyframes = `
    @keyframes rp-fade-slide-in {
      from { 
        opacity: 0; 
        transform: translateX(-20px); 
        filter: blur(1px);
      }
      to { 
        opacity: 1; 
        transform: translateX(0); 
        filter: blur(0);
      }
    }
  `;

  const fadeSlideOutKeyframes = `
    @keyframes rp-fade-slide-out {
      from { 
        opacity: 1; 
        transform: translateX(0); 
        filter: blur(0);
      }
      to { 
        opacity: 0; 
        transform: translateX(10px); 
        filter: blur(1px);
      }
    }
  `;

  const reducedMotionCSS = `
    @media (prefers-reduced-motion: reduce) {
      .rp-animate-in, .rp-animate-out { 
        animation: none !important;
        opacity: 1 !important;
        transform: none !important;
        filter: none !important;
      }
    }
  `;

  // Layer base styles
  const layerBaseStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    willChange: 'opacity, transform, filter',
    whiteSpace: 'nowrap'
  };

  // Current layer animation style
  const currentLayerStyle = {
    ...layerBaseStyle,
    zIndex: 2,
    animation: (isAnimating && !isPaused) ? 
      `rp-fade-slide-out ${animationConfig.outDuration}ms ${animationConfig.easing} both` : 
      'none'
  };

  // Next layer animation style
  const nextLayerStyle = {
    ...layerBaseStyle,
    zIndex: 1,
    animation: `rp-fade-slide-in ${animationConfig.inDuration}ms ${animationConfig.easing} both`
  };

  return (
    <>
      {/* Inject animation keyframes */}
      <style dangerouslySetInnerHTML={{
        __html: fadeSlideInKeyframes + fadeSlideOutKeyframes + reducedMotionCSS
      }} />

      <span 
        style={wrapperStyle}
        className={className}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        tabIndex={-1}
      >
        {/* Current phrase layer */}
        <span
          style={currentLayerStyle}
          className={isAnimating && !isPaused ? 'rp-animate-out' : ''}
          aria-hidden="true"
        >
          {currentPhrase}
        </span>
        
        {/* Next phrase layer - only render during animation */}
        {isAnimating && !isPaused && (
          <span
            style={nextLayerStyle}
            className="rp-animate-in"
            aria-hidden="true"
          >
            {nextPhrase}
          </span>
        )}
      </span>
      
      {/* Hidden measurement element */}
      <span
        ref={measureRef}
        className={`absolute opacity-0 pointer-events-none whitespace-nowrap -z-10 ${className}`}
        style={{ left: '-9999px' }}
        aria-hidden="true"
      />
      
      {/* Screen reader accessible static text */}
      <span className="sr-only">
        Never waste money on bad services again
      </span>
    </>
  );
  };

// Rotating Phrase Preview Component for Admin
const RotatingPhrasePreview = ({ phrases, intervalMs }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (phrases.length <= 1 || isHovered) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % phrases.length);
    }, intervalMs);

    return () => clearInterval(interval);
  }, [phrases.length, intervalMs, isHovered]);

  if (phrases.length === 0) return 'bad services again';

  return (
    <span
      className="inline-block transition-all duration-500 ease-in-out"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {phrases[currentIndex]}
    </span>
  );
};



// Admin User Management Component (Enhanced)
const AdminUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0
  });
  const [userFilters, setUserFilters] = useState({
    search: '',
    role_filter: 'all',
    status_filter: 'all'
  });
  const [searchInput, setSearchInput] = useState(''); // Separate state for search input
  const [loading, setLoading] = useState(true);
  
  const { user } = useAuth();

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setUserFilters(prev => ({
        ...prev,
        search: searchInput
      }));
      setUsersPagination(prev => ({
        ...prev,
        page: 1 // Reset to first page on search
      }));
    }, 800); // Wait 800ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    document.title = 'User Management | Admin Portal';
    fetchUsers();
  }, [userFilters, usersPagination.page]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: usersPagination.page.toString(),
        per_page: usersPagination.per_page.toString(),
        search: userFilters.search,
        role_filter: userFilters.role_filter,
        status_filter: userFilters.status_filter
      });
      
      const response = await axios.get(`${API}/admin/users?${params.toString()}`);
      setUsers(response.data.users);
      setUsersPagination(prev => ({
        ...prev,
        total: response.data.total,
        total_pages: response.data.total_pages
      }));
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, {
        role: newRole
      });
      toast.success(`User role updated to ${newRole.toUpperCase()} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const updateVipStatus = async (userId, isVip, note = '', userRole = 'rnkee') => {
    try {
      if (userRole === 'rnkee') {
        await axios.put(`${API}/admin/sellers/${userId}/vip`, {
          is_vip_no_commission: isVip,
          vip_note: note
        });
      } else if (userRole === 'rnker') {
        await axios.put(`${API}/admin/buyers/${userId}/vip`, {
          is_vip_no_commission: isVip,
          vip_note: note
        });
      }
      
      const userType = userRole === 'rnkee' ? 'seller' : 'buyer';
      toast.success(`${userType.charAt(0).toUpperCase() + userType.slice(1)} VIP status ${isVip ? 'granted' : 'removed'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update VIP status');
    }
  };

  const updateUserStatus = async (userId, isDisabled) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, {
        is_disabled: isDisabled
      });
      toast.success(`User account ${isDisabled ? 'disabled' : 'enabled'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const deleteUser = async (userId) => {
    try {
      const response = await axios.delete(`${API}/admin/users/${userId}`);
      
      // Show success message with details
      const deletedData = response.data.deleted_data;
      let message = `User ${deletedData.user} (${deletedData.email}) deleted successfully.`;
      if (deletedData.listings_deleted > 0) {
        message += ` ${deletedData.listings_deleted} listings were also deleted.`;
      }
      
      toast.success(message);
      
      // Refresh the user list
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete user';
      toast.error(errorMessage);
      throw error; // Re-throw so the UserManagementRow can handle loading state
    }
  };

  const handleUserSearch = (searchTerm) => {
    setSearchInput(searchTerm); // Update the input state immediately for UI responsiveness
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
        <p className="text-gray-600 mt-1">Manage platform users, admin roles, and account status</p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>User Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Search</Label>
              <div className="relative">
                <Input
                  placeholder="Search users..."
                  value={searchInput}
                  onChange={(e) => handleUserSearch(e.target.value)}
                />
                {searchInput !== userFilters.search && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-amber-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={userFilters.role_filter}
                onValueChange={(value) => setUserFilters(prev => ({ ...prev, role_filter: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                  <SelectItem value="gm">GMs</SelectItem>
                  <SelectItem value="rnkee">Sellers</SelectItem>
                  <SelectItem value="rnker">Buyers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select
                value={userFilters.status_filter}
                onValueChange={(value) => setUserFilters(prev => ({ ...prev, status_filter: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Platform Users</CardTitle>
          <CardDescription>
            {users.length} users found • Page {usersPagination.page} of {usersPagination.total_pages}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Current Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>VIP Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <UserManagementRow 
                  key={user.id} 
                  user={user} 
                  onUpdateRole={updateUserRole}
                  onUpdateVip={updateVipStatus}
                  onUpdateStatus={updateUserStatus}
                  onDeleteUser={deleteUser}
                />
              ))}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm text-gray-500">
              Showing {((usersPagination.page - 1) * usersPagination.per_page) + 1} to{' '}
              {Math.min(usersPagination.page * usersPagination.per_page, usersPagination.total)} of{' '}
              {usersPagination.total} users
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={usersPagination.page <= 1}
                onClick={() => setUsersPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={usersPagination.page >= usersPagination.total_pages}
                onClick={() => setUsersPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Chart Card Component
const ChartCard = ({ title, chartData, chartType = 'bar', onExport }) => {
  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
  );

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
    },
    scales: chartType !== 'doughnut' ? {
      y: {
        beginAtZero: true,
      },
    } : undefined,
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>{title}</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={onExport}
            disabled={!chartData}
          >
            Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!chartData ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            No data available
          </div>
        ) : chartType === 'doughnut' ? (
          <div className="h-64">
            <Doughnut data={chartData} options={options} />
          </div>
        ) : (
          <div className="h-64">
            <Bar data={chartData} options={options} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Admin Page Wrapper - Uses regular navigation with admin content area
const AdminPageWrapper = ({ children }) => {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {children}
      </div>
    </div>
  );
};

// Main App Component
// Impersonation Banner Component
const ImpersonationBanner = () => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [adminInfo, setAdminInfo] = useState(null);
  
  useEffect(() => {
    const impersonatedBy = localStorage.getItem('impersonated_by');
    if (impersonatedBy) {
      setIsImpersonating(true);
      setAdminInfo(JSON.parse(impersonatedBy));
    }
  }, []);
  
  if (!isImpersonating) return null;
  
  const exitImpersonation = () => {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      localStorage.setItem('token', adminToken);
      localStorage.removeItem('admin_token');
      localStorage.removeItem('impersonated_by');
      // Redirect back to admin user management
      setTimeout(() => {
        window.location.href = '/admin/users';
      }, 100);
    } else {
      // Fallback: just clear impersonation and go to homepage
      localStorage.removeItem('impersonated_by');
      window.location.href = '/';
    }
  };
  
  return (
    <div className="bg-purple-600 text-white px-4 py-3 flex items-center justify-between shadow-md fixed top-0 left-0 right-0 z-50">
      <div className="flex items-center gap-3">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 animate-pulse" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        <span className="font-semibold">
          You are viewing as another user
        </span>
        {adminInfo && (
          <span className="text-purple-200 text-sm">
            (Admin: {adminInfo.email})
          </span>
        )}
      </div>
      <button
        onClick={exitImpersonation}
        className="bg-white text-purple-600 px-4 py-1.5 rounded hover:bg-purple-50 font-medium text-sm transition-colors shadow-sm"
      >
        Exit Impersonation
      </button>
    </div>
  );
};

// Wrapper to add padding when impersonating
const AppContent = ({ children }) => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  
  useEffect(() => {
    setIsImpersonating(!!localStorage.getItem('impersonated_by'));
  }, []);
  
  return (
    <div style={{ paddingTop: isImpersonating ? '48px' : '0' }}>
      {children}
    </div>
  );
};

function App() {
  return (
    <QueryProvider>
      <AuthProvider>
        <div className="App">
          <ImpersonationBanner />
          <BrowserRouter>
            <AppContent>
              <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/browse" element={<BrowsePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/listing/:id" element={<ListingDetailPage />} />
              <Route path="/seller/:id" element={<SellerProfilePage />} />
              
              <Route path="/sell" element={
                <ProtectedRoute requiredRole="rnkee">
                  <SellerDashboard />
                </ProtectedRoute>
              } />
              
              <Route path="/sell/analytics" element={
                <ProtectedRoute requiredRole="rnkee">
                  <SellerAnalyticsPage />
                </ProtectedRoute>
              } />
              
              <Route path="/sell/payouts" element={
                <ProtectedRoute requiredRole="rnkee">
                  <SellerPayoutsPage />
                </ProtectedRoute>
              } />
            
            <Route path="/buy/orders" element={
              <ProtectedRoute requiredRole="rnker">
                <BuyerOrdersPage />
              </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/orders/:id" element={
              <ProtectedRoute>
                <OrderDetailPage />
              </ProtectedRoute>
            } />
            
            <Route path="/inbox" element={
              <ProtectedRoute>
                <InboxPage />
              </ProtectedRoute>
            } />
            
            <Route path="/inbox/conversation/:id" element={
              <ProtectedRoute>
                <ConversationPage />
              </ProtectedRoute>
            } />
            
            <Route path="/inbox/:id" element={
              <ProtectedRoute>
                <ConversationPage />
              </ProtectedRoute>
            } />
            
            <Route path="/profile/edit" element={
              <ProtectedRoute requiredRole="rnkee">
                <ProfileEditPage />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes - Now using regular navigation */}
            <Route path="/admin" element={
              <ProtectedRoute requiredRoles={["admin", "gm"]}>
                <AdminPageWrapper>
                  <AdminDashboard />
                </AdminPageWrapper>
              </ProtectedRoute>
            } />
            <Route path="/admin/dashboard" element={
              <ProtectedRoute requiredRoles={["admin", "gm"]}>
                <AdminPageWrapper>
                  <AdminDashboard />
                </AdminPageWrapper>
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute requiredRoles={["admin", "gm"]}>
                <AdminPageWrapper>
                  <AdminUserManagement />
                </AdminPageWrapper>
              </ProtectedRoute>
            } />
            <Route path="/admin/disputes" element={
              <ProtectedRoute requiredRoles={["admin", "gm"]}>
                <AdminPageWrapper>
                  <AdminDisputesPage />
                </AdminPageWrapper>
              </ProtectedRoute>
            } />
            <Route path="/admin/points" element={
              <ProtectedRoute requiredRoles={["admin", "gm"]}>
                <AdminPageWrapper>
                  <AdminPointsPage />
                </AdminPageWrapper>
              </ProtectedRoute>
            } />
            <Route path="/admin/categories" element={
              <ProtectedRoute requiredRoles={["admin", "gm"]}>
                <AdminPageWrapper>
                  <CategoryManagement />
                </AdminPageWrapper>
              </ProtectedRoute>
            } />
            <Route path="/admin/commission" element={
              <ProtectedRoute requiredRoles={["admin", "gm"]}>
                <AdminPageWrapper>
                  <AdminCommissionManagement />
                </AdminPageWrapper>
              </ProtectedRoute>
            } />
            <Route path="/admin/listings" element={
              <ProtectedRoute requiredRoles={["admin", "gm"]}>
                <AdminPageWrapper>
                  <AdminListingManagement />
                </AdminPageWrapper>
              </ProtectedRoute>
            } />
            <Route path="/admin/profiles" element={
              <ProtectedRoute requiredRoles={["admin", "gm"]}>
                <AdminPageWrapper>
                  <AdminProfileManagement />
                </AdminPageWrapper>
              </ProtectedRoute>
            } />
            <Route path="/admin/seller-approvals" element={
              <ProtectedRoute requiredRoles={["admin", "gm"]}>
                <AdminPageWrapper>
                  <AdminSellerApprovalsPage />
                </AdminPageWrapper>
              </ProtectedRoute>
            } />
            <Route path="/admin/landing" element={
              <ProtectedRoute requiredRoles={["admin", "gm"]}>
                <AdminPageWrapper>
                  <AdminLandingSettings />
                </AdminPageWrapper>
              </ProtectedRoute>
            } />
            <Route path="/admin/stripe" element={
              <ProtectedRoute requiredRoles={["admin", "gm"]}>
                <AdminPageWrapper>
                  <AdminStripeSettings />
                </AdminPageWrapper>
              </ProtectedRoute>
            } />
            <Route path="/admin/reviews" element={
              <ProtectedRoute requiredRoles={["admin", "gm"]}>
                <AdminPageWrapper>
                  <AdminReviewsPage />
                </AdminPageWrapper>
              </ProtectedRoute>
            } />
            
            {/* Legacy routes removed - admin routes now use regular navigation */}
              </Routes>
            </AppContent>
          </BrowserRouter>
          <Toaster />
        </div>
      </AuthProvider>
    </QueryProvider>
  );
};

// Admin Commission Management Component  
const AdminCommissionManagement = () => {
  const [globalCommission, setGlobalCommission] = useState({
    seller: 12,
    buyer: 8
  });
  const [commissionData, setCommissionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCommission, setSavingCommission] = useState(false);

  useEffect(() => {
    document.title = 'Commission Management | Admin Portal';
    fetchCommissionData();
  }, []);

  const fetchCommissionData = async () => {
    try {
      setLoading(true);
      const [commissionResponse, sellerSettingResponse, buyerSettingResponse] = await Promise.all([
        axios.get(`${API}/admin/dashboard/commission-preview`),
        axios.get(`${API}/admin/platform-settings/seller_commission_percent`),
        axios.get(`${API}/admin/platform-settings/buyer_commission_percent`)
      ]);
      
      setCommissionData(commissionResponse.data);
      setGlobalCommission({
        seller: parseFloat(sellerSettingResponse.data.value),
        buyer: parseFloat(buyerSettingResponse.data.value)
      });
    } catch (error) {
      console.error('Failed to fetch commission data:', error);
      setGlobalCommission({ seller: 12, buyer: 8 });
    } finally {
      setLoading(false);
    }
  };

  const updateGlobalCommission = async (commissionType, value) => {
    try {
      setSavingCommission(true);
      const settingKey = commissionType === 'seller' ? 'seller_commission_percent' : 'buyer_commission_percent';
      
      await axios.put(`${API}/admin/platform-settings/${settingKey}`, {
        value: value.toString()
      });
      
      const userType = commissionType === 'seller' ? 'seller' : 'buyer';
      toast.success(`${userType.charAt(0).toUpperCase() + userType.slice(1)} commission rate updated successfully`);
      fetchCommissionData();
    } catch (error) {
      toast.error('Failed to update commission rate');
    } finally {
      setSavingCommission(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading commission data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Commission Management</h2>
        <p className="text-gray-600 mt-1">Configure platform commission rates and manage VIP exemptions</p>
      </div>

      {/* Global Commission Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Global Commission Rates</CardTitle>
          <CardDescription>Set platform-wide commission rates for sellers and buyers</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Seller Commission */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="seller-commission" className="text-sm font-medium">
                  Seller Commission Percentage
                </Label>
                <p className="text-xs text-gray-500 mt-1">Commission charged to sellers on each transaction</p>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="seller-commission"
                    type="number"
                    min="0"
                    max="30"
                    step="0.1"
                    value={globalCommission.seller}
                    onChange={(e) => setGlobalCommission(prev => ({
                      ...prev,
                      seller: parseFloat(e.target.value) || 0
                    }))}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">%</span>
                  <Button
                    onClick={() => updateGlobalCommission('seller', globalCommission.seller)}
                    disabled={savingCommission}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {savingCommission ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Buyer Commission */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="buyer-commission" className="text-sm font-medium">
                  Buyer Commission Percentage
                </Label>
                <p className="text-xs text-gray-500 mt-1">Commission charged to buyers on each transaction</p>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    id="buyer-commission"
                    type="number"
                    min="0"
                    max="30"
                    step="0.1"
                    value={globalCommission.buyer}
                    onChange={(e) => setGlobalCommission(prev => ({
                      ...prev,
                      buyer: parseFloat(e.target.value) || 0
                    }))}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">%</span>
                  <Button
                    onClick={() => updateGlobalCommission('buyer', globalCommission.buyer)}
                    disabled={savingCommission}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {savingCommission ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium mb-2">Commission Structure:</h4>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Regular Seller + Regular Buyer = {globalCommission.seller}% seller + {globalCommission.buyer}% buyer commission</li>
              <li>• VIP Seller + Regular Buyer = 0% seller + {globalCommission.buyer}% buyer commission</li>
              <li>• Regular Seller + VIP Buyer = {globalCommission.seller}% seller + 0% buyer commission</li>
              <li>• VIP Seller + VIP Buyer = 0% commission for both</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Commission Preview */}
      {commissionData && (
        <Card>
          <CardHeader>
            <CardTitle>Commission Preview</CardTitle>
            <CardDescription>Current commission calculations and VIP seller overview</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  ${(commissionData.total_commission_due / 100).toFixed(2)}
                </div>
                <div className="text-sm text-gray-600">Total Commission Due</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {globalCommission.seller}%
                </div>
                <div className="text-sm text-gray-600">Seller Commission Rate</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">
                  {globalCommission.buyer}%
                </div>
                <div className="text-sm text-gray-600">Buyer Commission Rate</div>
              </div>
            </div>

            {commissionData.sellers && commissionData.sellers.length > 0 && (
              <div>
                <h4 className="font-medium mb-3">Seller Commission Breakdown</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Seller</TableHead>
                      <TableHead>Commission Rate</TableHead>
                      <TableHead>Amount Due</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commissionData.sellers.map((seller, index) => (
                      <TableRow key={index}>
                        <TableCell>{seller.name}</TableCell>
                        <TableCell>{seller.commission_rate}%</TableCell>
                        <TableCell>${(seller.commission_due / 100).toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant={seller.commission_rate === 0 ? 'secondary' : 'default'}>
                            {seller.commission_rate === 0 ? 'VIP' : 'Regular'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// User Management Row Component
const UserManagementRow = ({ user, onUpdateVip, onUpdateStatus, onUpdateRole, onDeleteUser }) => {
  const [isEditingVip, setIsEditingVip] = useState(false);
  const [isEditingRole, setIsEditingRole] = useState(false);
  const [vipNote, setVipNote] = useState(user.vip_note || '');
  const [selectedRole, setSelectedRole] = useState(user.role);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showPasswordMenu, setShowPasswordMenu] = useState(false);
  const [showTempPasswordModal, setShowTempPasswordModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tempPasswordData, setTempPasswordData] = useState({
    temp_password: '',
    confirm_password: ''
  });
  const [tempPasswordResult, setTempPasswordResult] = useState(null);

  // Password Actions
  const handleSendResetLink = async () => {
    try {
      await axios.post(`${API}/admin/users/${user.id}/send-reset-link`);
      toast.success('Reset link sent successfully.');
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Too many reset links sent for this user. Please wait an hour.');
      } else {
        toast.error('Failed to send reset link.');
      }
    }
    setShowPasswordMenu(false);
  };

  const handleToggleForceReset = async () => {
    try {
      const response = await axios.post(`${API}/admin/users/${user.id}/toggle-force-reset`);
      const status = response.data.force_password_reset ? 'enabled' : 'disabled';
      toast.success(`Force password reset ${status} for user.`);
      // Refresh user data
      window.location.reload();
    } catch (error) {
      toast.error('Failed to toggle force reset.');
    }
    setShowPasswordMenu(false);
  };

  const handleSetTempPassword = async () => {
    try {
      if (tempPasswordData.temp_password !== tempPasswordData.confirm_password) {
        toast.error('Passwords do not match');
        return;
      }

      const response = await axios.post(`${API}/admin/users/${user.id}/set-temp-password`, {
        user_id: user.id,
        temp_password: tempPasswordData.temp_password,
        confirm_password: tempPasswordData.confirm_password
      });
      
      setTempPasswordResult(response.data);
      toast.success('Temporary password set successfully.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set temporary password.');
    }
  };

  const handleDeleteUser = async () => {
    try {
      setIsUpdating(true);
      await onDeleteUser(user.id);
      setShowDeleteConfirm(false);
    } catch (error) {
      // Error handling done in parent component
    } finally {
      setIsUpdating(false);
    }
  };

  const copyTempPassword = () => {
    if (tempPasswordResult?.temp_password) {
      navigator.clipboard.writeText(tempPasswordResult.temp_password);
      toast.success('Temporary password copied to clipboard!');
    }
  };

  const handleToggleVip = async () => {
    if (user.role !== 'rnkee' && user.role !== 'rnker') return;
    
    setIsUpdating(true);
    try {
      await onUpdateVip(user.id, !user.is_vip, vipNote, user.role);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleStatus = async () => {
    if (user.role === 'admin') return;
    
    setIsUpdating(true);
    try {
      await onUpdateStatus(user.id, !user.is_disabled);
    } catch (error) {
      // Error handling is done in parent component  
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveVipNote = async () => {
    setIsUpdating(true);
    try {
      await onUpdateVip(user.id, user.is_vip, vipNote, user.role);
      setIsEditingVip(false);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateRole = async () => {
    if (selectedRole === user.role) {
      setIsEditingRole(false);
      return;
    }
    
    setIsUpdating(true);
    try {
      await onUpdateRole(user.id, selectedRole);
      setIsEditingRole(false);
    } catch (error) {
      setSelectedRole(user.role); // Reset on error
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <TableRow className={user.is_disabled ? 'opacity-60 bg-gray-50' : ''}>
      <TableCell>
        <div className="flex items-center gap-2">
          <div>
            <p className="font-medium">{user.name}</p>
            <p className="text-sm text-gray-500">{user.company}</p>
            <p className="text-xs text-gray-400">{user.email}</p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        {isEditingRole ? (
          <div className="flex items-center gap-2">
            <Select
              value={selectedRole}
              onValueChange={setSelectedRole}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rnkee">Seller</SelectItem>
                <SelectItem value="rnker">Buyer</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="gm">GM</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={handleUpdateRole} disabled={isUpdating}>
              Save
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => {
                setIsEditingRole(false);
                setSelectedRole(user.role);
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-xs ${
              user.role === 'admin' ? 'bg-red-100 text-red-800' :
              user.role === 'gm' ? 'bg-purple-100 text-purple-800' :
              user.role === 'rnkee' ? 'bg-blue-100 text-blue-800' :
              'bg-green-100 text-green-800'
            }`}>
              {user.role === 'rnkee' ? 'Seller' : 
               user.role === 'rnker' ? 'Buyer' :
               user.role === 'admin' ? 'Admin' : 'GM'}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsEditingRole(true)}
              disabled={isUpdating}
            >
              Change Role
            </Button>
          </div>
        )}
      </TableCell>
      <TableCell>
        <span className={`px-2 py-1 rounded-full text-xs ${
          user.is_disabled ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
        }`}>
          {user.is_disabled ? 'Disabled' : 'Active'}
        </span>
      </TableCell>
      <TableCell>
        {(user.role === 'rnkee' || user.role === 'rnker') ? (
          <div>
            {isEditingVip ? (
              <div className="flex flex-col gap-2">
                <Input
                  placeholder="VIP note (optional)"
                  value={vipNote}
                  onChange={(e) => setVipNote(e.target.value)}
                  className="w-48"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSaveVipNote} disabled={isUpdating}>
                    Save
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      setIsEditingVip(false);
                      setVipNote(user.vip_note || '');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm">
                  {user.is_vip ? `VIP (No Commission)` : 'Regular'}
                </span>
                {user.vip_note && (
                  <span className="text-xs text-gray-500">• {user.vip_note}</span>
                )}
              </div>
            )}
          </div>
        ) : (
          <span className="text-sm text-gray-400">N/A</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex gap-2 flex-wrap">
          {(user.role === 'rnkee' || user.role === 'rnker') && (
            <>
              <Button
                size="sm"
                variant={user.is_vip ? "destructive" : "default"}
                onClick={handleToggleVip}
                disabled={isUpdating}
              >
                {user.is_vip ? 'Remove VIP' : 'Grant VIP'}
              </Button>
              {!isEditingVip && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsEditingVip(true)}
                >
                  Edit Note
                </Button>
              )}
            </>
          )}
          {user.role !== 'admin' && (
            <Button
              size="sm"
              variant={user.is_disabled ? "default" : "destructive"}
              onClick={handleToggleStatus}
              disabled={isUpdating}
            >
              {user.is_disabled ? 'Enable' : 'Disable'}
            </Button>
          )}
          
          {/* Delete User Button */}
          {user.role !== 'admin' && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isUpdating}
            >
              Delete User
            </Button>
          )}
          
          {/* Login as User Button */}
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              try {
                const response = await axios.post(`${API}/admin/users/${user.id}/impersonate`);
                const { access_token, user: impersonatedUser, impersonated_by } = response.data;
                
                // Store the original admin token
                const currentToken = localStorage.getItem('token');
                if (currentToken) {
                  localStorage.setItem('admin_token', currentToken);
                  localStorage.setItem('impersonated_by', JSON.stringify(impersonated_by));
                }
                
                // Set the new token
                localStorage.setItem('token', access_token);
                
                // Show success message
                toast.success(`Now logged in as ${impersonatedUser.name}`);
                
                // Redirect to appropriate dashboard
                if (impersonatedUser.role === 'rnkee') {
                  window.location.href = '/sell';
                } else if (impersonatedUser.role === 'rnker') {
                  window.location.href = '/browse';
                } else {
                  window.location.href = '/';
                }
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Failed to impersonate user');
              }
            }}
            disabled={isUpdating}
            className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
            </svg>
            Login as User
          </Button>
          
          {/* Password Actions Dropdown */}
          <DropdownMenu open={showPasswordMenu} onOpenChange={setShowPasswordMenu}>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline">
                <MoreVertical className="h-4 w-4 mr-1" />
                Password Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onClick={handleSendResetLink}
                className="cursor-pointer"
              >
                Send Password Reset Link
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleToggleForceReset}
                className="cursor-pointer"
              >
                {user.force_password_reset ? 'Disable' : 'Enable'} Force Reset on Login
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setShowPasswordMenu(false);
                  setShowTempPasswordModal(true);
                }}
                className="cursor-pointer"
              >
                Set Temporary Password
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Temporary Password Modal */}
        {showTempPasswordModal && (
          <Dialog open={showTempPasswordModal} onOpenChange={setShowTempPasswordModal}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Set Temporary Password for {user.name}</DialogTitle>
                <DialogDescription>
                  Set a temporary password that the user will be required to change on next login.
                </DialogDescription>
              </DialogHeader>
              
              {tempPasswordResult ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-800 mb-2">Temporary Password Created</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Input
                          value={tempPasswordResult.temp_password}
                          readOnly
                          className="font-mono text-sm"
                        />
                        <Button
                          size="sm"
                          onClick={copyTempPassword}
                          variant="outline"
                        >
                          Copy
                        </Button>
                      </div>
                      <p className="text-sm text-green-700">
                        Password expires: {new Date(tempPasswordResult.expires_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      onClick={() => {
                        setShowTempPasswordModal(false);
                        setTempPasswordResult(null);
                        setTempPasswordData({ temp_password: '', confirm_password: '' });
                      }}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Temporary Password</Label>
                    <Input
                      type="password"
                      value={tempPasswordData.temp_password}
                      onChange={(e) => setTempPasswordData(prev => ({
                        ...prev,
                        temp_password: e.target.value
                      }))}
                      placeholder="Enter temporary password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirm Password</Label>
                    <Input
                      type="password"
                      value={tempPasswordData.confirm_password}
                      onChange={(e) => setTempPasswordData(prev => ({
                        ...prev,
                        confirm_password: e.target.value
                      }))}
                      placeholder="Confirm temporary password"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowTempPasswordModal(false);
                        setTempPasswordData({ temp_password: '', confirm_password: '' });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSetTempPassword}
                      disabled={!tempPasswordData.temp_password || !tempPasswordData.confirm_password}
                    >
                      Set Password
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}

        {/* Delete User Confirmation Dialog */}
        {showDeleteConfirm && (
          <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-red-600">Delete User: {user.name}</DialogTitle>
                <DialogDescription>
                  This action cannot be undone. This will permanently delete the user account and all associated data.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-medium text-red-800 mb-2">⚠️ Warning: This will delete:</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    <li>• User account and profile</li>
                    {user.role === 'rnkee' && <li>• All listings created by this seller</li>}
                    <li>• All conversations and messages</li>
                    <li>• All orders (as buyer or seller)</li>
                    <li>• All associated data</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">
                    <strong>User:</strong> {user.name} ({user.email})<br/>
                    <strong>Role:</strong> {user.role === 'rnkee' ? 'Seller' : user.role === 'rnker' ? 'Buyer' : user.role}<br/>
                    <strong>Company:</strong> {user.company || 'N/A'}
                  </p>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isUpdating}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDeleteUser}
                    disabled={isUpdating}
                  >
                    {isUpdating ? 'Deleting...' : 'Delete User'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </TableCell>
    </TableRow>
  );
};

// VIP Seller Row Component (kept for backwards compatibility but unused)
const VipSellerRow = ({ seller, onUpdateVip }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [vipNote, setVipNote] = useState(seller.vip_note || '');
  const [isUpdating, setIsUpdating] = useState(false);

  const handleToggleVip = async () => {
    setIsUpdating(true);
    try {
      await onUpdateVip(seller.user_id, !seller.is_vip_no_commission, vipNote);
      setIsEditing(false);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveNote = async () => {
    setIsUpdating(true);
    try {
      await onUpdateVip(seller.user_id, seller.is_vip_no_commission, vipNote);
      setIsEditing(false);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <div>
            <p className="font-medium">{seller.name}</p>
            <p className="text-sm text-gray-500">{seller.company}</p>
            <p className="text-xs text-gray-400">{seller.email}</p>
          </div>
          {seller.is_vip_no_commission && (
            <Badge className="bg-yellow-100 text-yellow-800 text-xs">VIP</Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={seller.is_vip_no_commission}
            onChange={handleToggleVip}
            disabled={isUpdating}
            className="rounded border-gray-300"
          />
          <span className="text-sm">
            {seller.is_vip_no_commission ? 'VIP (No Commission)' : 'Regular'}
          </span>
        </div>
      </TableCell>
      <TableCell>
        {isEditing ? (
          <div className="flex gap-2">
            <Input
              value={vipNote}
              onChange={(e) => setVipNote(e.target.value)}
              placeholder="Add VIP note..."
              className="text-sm"
            />
            <Button
              size="sm"
              onClick={handleSaveNote}
              disabled={isUpdating}
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setIsEditing(false);
                setVipNote(seller.vip_note || '');
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {seller.vip_note || 'No note'}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          </div>
        )}
      </TableCell>
      <TableCell>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={seller.is_vip_no_commission ? "destructive" : "default"}
            onClick={handleToggleVip}
            disabled={isUpdating}
          >
            {isUpdating ? 'Updating...' : seller.is_vip_no_commission ? 'Remove VIP' : 'Grant VIP'}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

// Category Management Component
const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [activeTab, setActiveTab] = useState('categories'); // categories, aliases
  
  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);

  const { user } = useAuth();

  useEffect(() => {
    document.title = 'Category Management | Rnkem Admin';
    fetchCategories();
    fetchAliases();
  }, [showInactive]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        include_inactive: showInactive.toString()
      });
      
      const response = await axios.get(`${API}/admin/categories?${params.toString()}`);
      setCategories(response.data);
      
      // Select first category if none selected
      if (!selectedCategory && response.data.length > 0) {
        setSelectedCategory(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchAliases = async () => {
    try {
      const response = await axios.get(`${API}/admin/category-aliases`);
      setAliases(response.data);
    } catch (error) {
      console.error('Failed to fetch aliases:', error);
    }
  };

  const handleCreateCategory = async (categoryData) => {
    try {
      await axios.post(`${API}/admin/categories`, categoryData);
      toast.success('Category created successfully');
      fetchCategories();
      setShowCategoryModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleUpdateCategory = async (categoryId, updateData) => {
    try {
      await axios.put(`${API}/admin/categories/${categoryId}`, updateData);
      toast.success('Category updated successfully');
      fetchCategories();
      setEditingCategory(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category?')) return;
    
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`);
      toast.success('Category deleted successfully');
      fetchCategories();
      if (selectedCategory?.id === categoryId) {
        setSelectedCategory(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const handleToggleCategoryActive = async (categoryId, isActive) => {
    try {
      await axios.put(`${API}/admin/categories/${categoryId}`, {
        is_active: !isActive
      });
      toast.success(`Category ${!isActive ? 'activated' : 'deactivated'} successfully`);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update category status');
    }
  };

  const handleCreateSubcategory = async (subcategoryData) => {
    try {
      await axios.post(`${API}/admin/categories/${selectedCategory.id}/subcategories`, subcategoryData);
      toast.success('Subcategory created successfully');
      fetchCategories();
      setShowSubcategoryModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create subcategory');
    }
  };

  const handleUpdateSubcategory = async (subcategoryId, updateData) => {
    try {
      await axios.put(`${API}/admin/subcategories/${subcategoryId}`, updateData);
      toast.success('Subcategory updated successfully');
      fetchCategories();
      setEditingSubcategory(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subcategory');
    }
  };

  const handleDeleteSubcategory = async (subcategoryId) => {
    if (!confirm('Are you sure you want to delete this subcategory?')) return;
    
    try {
      await axios.delete(`${API}/admin/subcategories/${subcategoryId}`);
      toast.success('Subcategory deleted successfully');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete subcategory');
    }
  };

  const handleCreateAlias = async (aliasData) => {
    try {
      await axios.post(`${API}/admin/category-aliases`, aliasData);
      toast.success('Alias created successfully');
      fetchAliases();
      setShowAliasModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create alias');
    }
  };

  const handleDeleteAlias = async (aliasId) => {
    if (!confirm('Are you sure you want to delete this alias?')) return;
    
    try {
      await axios.delete(`${API}/admin/category-aliases/${aliasId}`);
      toast.success('Alias deleted successfully');
      fetchAliases();
    } catch (error) {
      toast.error('Failed to delete alias');
    }
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading categories...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Category Management</h1>
            <p className="text-gray-600 mt-1">Manage service categories and subcategories</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setActiveTab('categories')}
              variant={activeTab === 'categories' ? 'default' : 'outline'}
            >
              Categories
            </Button>
            <Button
              onClick={() => setActiveTab('aliases')}
              variant={activeTab === 'aliases' ? 'default' : 'outline'}
            >
              Aliases
            </Button>
          </div>
        </div>
      </div>

      {activeTab === 'categories' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Category List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Categories</CardTitle>
                  <Button
                    size="sm"
                    onClick={() => setShowCategoryModal(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Add Category
                  </Button>
                </div>
                <div className="space-y-2">
                  <Input
                    placeholder="Search categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="show-inactive"
                      checked={showInactive}
                      onCheckedChange={setShowInactive}
                    />
                    <Label htmlFor="show-inactive" className="text-sm">
                      Show inactive
                    </Label>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredCategories.map((category) => (
                    <div
                      key={category.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedCategory?.id === category.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${!category.is_active ? 'opacity-60' : ''}`}
                      onClick={() => setSelectedCategory(category)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{category.name}</div>
                          <div className="text-sm text-gray-500">{category.slug}</div>
                          <div className="text-xs text-gray-400">
                            {category.subcategories.length} subcategories • {category.listing_count} listings
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!category.is_active && (
                            <Badge variant="secondary" className="text-xs">
                              Inactive
                            </Badge>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleCategoryActive(category.id, category.is_active);
                            }}
                          >
                            {category.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Category Details */}
          <div className="lg:col-span-2">
            {selectedCategory ? (
              <div className="space-y-6">
                {/* Category Edit Form */}
                <Card>
                  <CardHeader>
                    <CardTitle>Category Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CategoryEditForm
                      category={selectedCategory}
                      onUpdate={handleUpdateCategory}
                      onDelete={handleDeleteCategory}
                    />
                  </CardContent>
                </Card>

                {/* Subcategories */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Subcategories</CardTitle>
                      <Button
                        size="sm"
                        onClick={() => setShowSubcategoryModal(true)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Add Subcategory
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <SubcategoryTable
                      subcategories={selectedCategory.subcategories}
                      onEdit={setEditingSubcategory}
                      onDelete={handleDeleteSubcategory}
                    />
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <p className="text-gray-500">Select a category to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      ) : (
        // Aliases Tab
        <AliasManagement
          aliases={aliases}
          onCreate={handleCreateAlias}
          onDelete={handleDeleteAlias}
        />
      )}

      {/* Modals */}
      {showCategoryModal && (
        <CategoryModal
          isOpen={showCategoryModal}
          onClose={() => setShowCategoryModal(false)}
          onSubmit={handleCreateCategory}
        />
      )}

      {showSubcategoryModal && (
        <SubcategoryModal
          isOpen={showSubcategoryModal}
          onClose={() => setShowSubcategoryModal(false)}
          onSubmit={handleCreateSubcategory}
          categoryId={selectedCategory?.id}
        />
      )}

      {showAliasModal && (
        <AliasModal
          isOpen={showAliasModal}
          onClose={() => setShowAliasModal(false)}
          onSubmit={handleCreateAlias}
          categories={categories}
        />
      )}

      {editingCategory && (
        <CategoryModal
          isOpen={true}
          onClose={() => setEditingCategory(null)}
          onSubmit={(data) => handleUpdateCategory(editingCategory.id, data)}
          initialData={editingCategory}
          isEditing={true}
        />
      )}

      {editingSubcategory && (
        <SubcategoryModal
          isOpen={true}
          onClose={() => setEditingSubcategory(null)}
          onSubmit={(data) => handleUpdateSubcategory(editingSubcategory.id, data)}
          initialData={editingSubcategory}
          isEditing={true}
        />
      )}
    </div>
  );
};

// Category Edit Form Component
const CategoryEditForm = ({ category, onUpdate, onDelete }) => {
  const [formData, setFormData] = useState({
    name: category.name,
    description: category.description || '',
    icon_name: category.icon_name || '',
    is_active: category.is_active
  });

  // Update form data when category changes
  useEffect(() => {
    setFormData({
      name: category.name,
      description: category.description || '',
      icon_name: category.icon_name || '',
      is_active: category.is_active
    });
  }, [category]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(category.id, formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            required
          />
        </div>
        <div>
          <Label htmlFor="slug">Slug</Label>
          <Input
            id="slug"
            value={category.slug}
            disabled
            className="bg-gray-50"
          />
          <p className="text-xs text-gray-500 mt-1">Slug cannot be changed</p>
        </div>
      </div>
      
      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
        />
      </div>
      
      <div>
        <Label htmlFor="icon_name">Icon Name</Label>
        <Input
          id="icon_name"
          value={formData.icon_name}
          onChange={(e) => setFormData(prev => ({ ...prev, icon_name: e.target.value }))}
          placeholder="e.g., briefcase, home, code"
        />
      </div>
      
      <div className="flex items-center space-x-2">
        <Checkbox
          id="is_active"
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
        />
        <Label htmlFor="is_active">Active</Label>
      </div>
      
      <div className="flex justify-between pt-4">
        <Button
          type="button"
          variant="destructive"
          onClick={() => onDelete(category.id)}
        >
          Delete Category
        </Button>
        <Button type="submit">Save Changes</Button>
      </div>
    </form>
  );
};

// Subcategory Table Component
const SubcategoryTable = ({ subcategories, onEdit, onDelete }) => {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Sort Order</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {subcategories.map((subcategory) => (
            <TableRow key={subcategory.id}>
              <TableCell className="font-medium">{subcategory.name}</TableCell>
              <TableCell className="font-mono text-sm">{subcategory.slug}</TableCell>
              <TableCell>
                <Badge variant={subcategory.is_active ? 'default' : 'secondary'}>
                  {subcategory.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </TableCell>
              <TableCell>{subcategory.sort_order}</TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onEdit(subcategory)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => onDelete(subcategory.id)}
                  >
                    Delete
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {subcategories.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No subcategories found
        </div>
      )}
    </div>
  );
};

// Alias Management Component
const AliasManagement = ({ aliases, onCreate, onDelete }) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Category Aliases</CardTitle>
          <Button
            size="sm"
            onClick={() => onCreate()}
            className="bg-purple-600 hover:bg-purple-700"
          >
            Add Alias
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Alias Slug</TableHead>
              <TableHead>Target Slug</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {aliases.map((alias) => (
              <TableRow key={alias.id}>
                <TableCell>
                  <Badge variant={alias.kind === 'category' ? 'default' : 'secondary'}>
                    {alias.kind}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-sm">{alias.alias_slug}</TableCell>
                <TableCell className="font-mono text-sm">{alias.target_slug}</TableCell>
                <TableCell>
                  {new Date(alias.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => onDelete(alias.id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        {aliases.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No aliases found
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Category Modal Component
const CategoryModal = ({ isOpen, onClose, onSubmit, initialData, isEditing }) => {
  const [formData, setFormData] = useState(
    initialData || {
      slug: '',
      name: '',
      description: '',
      icon_name: '',
      is_active: true,
      sort_order: 100
    }
  );

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (name) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: isEditing ? prev.slug : generateSlug(name)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">
              {isEditing ? 'Edit Category' : 'Create Category'}
            </h3>
          </div>
          
          <div className="px-6 py-4 space-y-4">
            <div>
              <Label htmlFor="modal-name">Name</Label>
              <Input
                id="modal-name"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="modal-slug">Slug</Label>
              <Input
                id="modal-slug"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                disabled={isEditing}
                className={isEditing ? 'bg-gray-50' : ''}
                required
              />
              {isEditing && (
                <p className="text-xs text-gray-500 mt-1">Slug cannot be changed</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="modal-description">Description</Label>
              <Textarea
                id="modal-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="modal-icon">Icon Name</Label>
              <Input
                id="modal-icon"
                value={formData.icon_name}
                onChange={(e) => setFormData(prev => ({ ...prev, icon_name: e.target.value }))}
                placeholder="e.g., briefcase, home, code"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="modal-active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="modal-active">Active</Label>
            </div>
          </div>
          
          <div className="px-6 py-4 border-t flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Subcategory Modal Component
const SubcategoryModal = ({ isOpen, onClose, onSubmit, categoryId, initialData, isEditing }) => {
  const [formData, setFormData] = useState(
    initialData || {
      slug: '',
      name: '',
      description: '',
      is_active: true,
      sort_order: 100
    }
  );

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (name) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: isEditing ? prev.slug : generateSlug(name)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">
              {isEditing ? 'Edit Subcategory' : 'Create Subcategory'}
            </h3>
          </div>
          
          <div className="px-6 py-4 space-y-4">
            <div>
              <Label htmlFor="subcat-name">Name</Label>
              <Input
                id="subcat-name"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="subcat-slug">Slug</Label>
              <Input
                id="subcat-slug"
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                disabled={isEditing}
                className={isEditing ? 'bg-gray-50' : ''}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="subcat-description">Description</Label>
              <Textarea
                id="subcat-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="subcat-active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="subcat-active">Active</Label>
            </div>
          </div>
          
          <div className="px-6 py-4 border-t flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Alias Modal Component (placeholder)
const AliasModal = ({ isOpen, onClose, onSubmit, categories }) => {
  const [formData, setFormData] = useState({
    kind: 'category',
    alias_slug: '',
    target_slug: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">Create Alias</h3>
          </div>
          
          <div className="px-6 py-4 space-y-4">
            <div>
              <Label>Type</Label>
              <Select
                value={formData.kind}
                onValueChange={(value) => setFormData(prev => ({ ...prev, kind: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="category">Category</SelectItem>
                  <SelectItem value="subcategory">Subcategory</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="alias-slug">Alias Slug</Label>
              <Input
                id="alias-slug"
                value={formData.alias_slug}
                onChange={(e) => setFormData(prev => ({ ...prev, alias_slug: e.target.value }))}
                placeholder="old-slug"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="target-slug">Target Slug</Label>
              <Input
                id="target-slug"
                value={formData.target_slug}
                onChange={(e) => setFormData(prev => ({ ...prev, target_slug: e.target.value }))}
                placeholder="new-slug"
                required
              />
            </div>
          </div>
          
          <div className="px-6 py-4 border-t flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Create Alias</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Admin Listing Management Component
const AdminListingManagement = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: 'all',
    sort_by: 'created_at',
    sort_order: 'desc'
  });
  const [editingListing, setEditingListing] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    fetchListings();
  }, [pagination.page, filters]);

  const fetchListings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        per_page: pagination.per_page.toString(),
        ...filters
      });

      const response = await axios.get(`${API}/admin/listings?${params.toString()}`);
      setListings(response.data.listings);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        total_pages: response.data.pagination?.total_pages || 0
      }));
    } catch (error) {
      console.error('Failed to fetch listings:', error);
      toast.error('Failed to fetch listings');
    } finally {
      setLoading(false);
    }
  };

  const handleEditListing = (listing) => {
    setEditingListing(listing);
    setIsEditModalOpen(true);
  };

  const handleUpdateListing = async (updatedData) => {
    try {
      await axios.put(`${API}/admin/listings/${editingListing.id}`, updatedData);
      toast.success('Listing updated successfully');
      setIsEditModalOpen(false);
      setEditingListing(null);
      fetchListings();
    } catch (error) {
      toast.error('Failed to update listing');
    }
  };

  const handleDeleteListing = async (listing) => {
    if (window.confirm(`Are you sure you want to delete "${listing.title}"? This action cannot be undone.`)) {
      try {
        await axios.delete(`${API}/admin/listings/${listing.id}`);
        toast.success('Listing deleted successfully');
        fetchListings();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete listing');
      }
    }
  };

  const handleToggleStatus = async (listing) => {
    try {
      await axios.put(`${API}/admin/listings/${listing.id}`, {
        is_active: !listing.is_active
      });
      toast.success(`Listing ${listing.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchListings();
    } catch (error) {
      toast.error('Failed to update listing status');
    }
  };

  const handleSearch = (searchTerm) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleExportListings = async () => {
    try {
      const params = new URLSearchParams({
        ...filters,
        page: 1,
        per_page: 10000 // Get all listings
      });

      const response = await axios.get(`${API}/admin/listings?${params.toString()}`);
      const exportData = (response.data.listings || []).map(listing => ({
        title: listing.title,
        seller_name: listing.seller.name,
        seller_email: listing.seller.email,
        category: listing.service_category,
        subcategory: listing.service_subcategory,
        price_usd: (listing.price_cents / 100).toFixed(2),
        pricing_model: listing.pricing_model,
        tags: listing.tags ? listing.tags.join('; ') : '',
        status: listing.is_active ? 'Active' : 'Inactive',
        created_date: new Date(listing.created_at).toLocaleDateString(),
        updated_date: new Date(listing.updated_at).toLocaleDateString()
      }));

      const csvContent = [
        'Title,Seller Name,Seller Email,Category,Subcategory,Price (USD),Pricing Model,Tags,Status,Created Date,Updated Date',
        ...exportData.map(row => Object.values(row).map(val => 
          typeof val === 'string' && val.includes(',') ? `"${val}"` : val
        ).join(','))
      ].join('\n');

      const now = new Date().toISOString().split('T')[0];
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rnkem_listings_export_${now}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${exportData.length} listings exported successfully`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export listings');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Listing Management</h2>
          <p className="text-gray-600 mt-1">Edit and manage all platform listings</p>
        </div>
        <Button
          variant="outline"
          onClick={handleExportListings}
          disabled={loading}
        >
          Export All Listings
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Search Listings</Label>
              <Input
                placeholder="Search by title, description..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
            
            <div>
              <Label>Status</Label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Sort By</Label>
              <Select
                value={filters.sort_by}
                onValueChange={(value) => setFilters(prev => ({ ...prev, sort_by: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Date Created</SelectItem>
                  <SelectItem value="title">Title</SelectItem>
                  <SelectItem value="price_cents">Price</SelectItem>
                  <SelectItem value="updated_at">Last Updated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Order</Label>
              <Select
                value={filters.sort_order}
                onValueChange={(value) => setFilters(prev => ({ ...prev, sort_order: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Descending</SelectItem>
                  <SelectItem value="asc">Ascending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Listings Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>All Listings ({pagination.total})</CardTitle>
            <div className="text-sm text-gray-500">
              Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} listings
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading listings...</p>
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No listings found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Seller</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Tags</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listings.map((listing) => (
                    <TableRow key={listing.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{listing.title}</p>
                          <p className="text-sm text-gray-500 truncate max-w-xs">
                            {listing.description}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{listing.seller.name}</p>
                          <p className="text-sm text-gray-500">{listing.seller.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{listing.service_category}</p>
                          {listing.service_subcategory && (
                            <p className="text-xs text-gray-500">{listing.service_subcategory}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {listing.tags && listing.tags.length > 0 ? (
                            listing.tags.slice(0, 3).map((tag, index) => (
                              <span
                                key={index}
                                className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs"
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">No tags</span>
                          )}
                          {listing.tags && listing.tags.length > 3 && (
                            <span className="text-xs text-gray-500">+{listing.tags.length - 3} more</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{formatCurrency(listing.price_cents)}</p>
                          <p className="text-xs text-gray-500 capitalize">{listing.pricing_model}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          listing.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {listing.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(listing.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditListing(listing)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleStatus(listing)}
                            className={listing.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                          >
                            {listing.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteListing(listing)}
                            className="text-red-600 hover:text-red-700"
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex justify-between items-center mt-4">
                <div className="text-sm text-gray-500">
                  Page {pagination.page} of {pagination.total_pages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page <= 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page >= pagination.total_pages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Listing Modal */}
      {isEditModalOpen && editingListing && (
        <AdminListingEditModal
          listing={editingListing}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingListing(null);
          }}
          onUpdate={handleUpdateListing}
        />
      )}
    </div>
  );
};

// Admin Listing Edit Modal Component
const AdminListingEditModal = ({ listing, isOpen, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    title: listing.title || '',
    description: listing.description || '',
    service_category: listing.service_category || '',
    service_subcategory: listing.service_subcategory || '',
    price_cents: listing.price_cents || 0,
    pricing_model: listing.pricing_model || 'fixed',
    tags: listing.tags || [],
    is_active: listing.is_active || false
  });
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.service_category) {
      fetchSubcategories(formData.service_category);
    } else {
      setSubcategories([]);
      setFormData(prev => ({ ...prev, service_subcategory: '' }));
    }
  }, [formData.service_category]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await axios.get(`${API}/admin/categories/${categoryId}/subcategories`);
      setSubcategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch subcategories:', error);
      setSubcategories([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onUpdate(formData);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Edit Listing</h3>
          <Button variant="outline" onClick={onClose}>✕</Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
              />
            </div>

            <div>
              <Label>Description</Label>
              <textarea
                className="w-full p-2 border rounded-md"
                rows="4"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                required
              />
            </div>
          </div>

          {/* Category Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <Select
                value={formData.service_category}
                onValueChange={(value) => handleChange('service_category', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.slug}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Subcategory</Label>
              <Select
                value={formData.service_subcategory}
                onValueChange={(value) => handleChange('service_subcategory', value)}
                disabled={!formData.service_category}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select subcategory" />
                </SelectTrigger>
                <SelectContent>
                  {subcategories.map((subcategory) => (
                    <SelectItem key={subcategory.id} value={subcategory.slug}>
                      {subcategory.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tags Management */}
          <div>
            <Label>Tags</Label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Add a tag"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag(e)}
                />
                <Button type="button" onClick={handleAddTag}>
                  Add Tag
                </Button>
              </div>
              
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm flex items-center gap-1"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Pricing Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Price (USD)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price_cents / 100}
                onChange={(e) => handleChange('price_cents', Math.round(parseFloat(e.target.value || 0) * 100))}
                required
              />
            </div>

            <div>
              <Label>Pricing Model</Label>
              <Select
                value={formData.pricing_model}
                onValueChange={(value) => handleChange('pricing_model', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed Price</SelectItem>
                  <SelectItem value="hourly">Hourly Rate</SelectItem>
                  <SelectItem value="per_lead">Per Lead</SelectItem>
                  <SelectItem value="package">Package Deal</SelectItem>
                  <SelectItem value="project">Project-based</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => handleChange('is_active', e.target.checked)}
            />
            <Label htmlFor="is_active">Active Listing</Label>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Listing'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Admin Profile Management Component
const AdminProfileManagement = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    role_filter: 'all'
  });
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, [pagination.page, filters]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        per_page: pagination.per_page.toString(),
        ...filters
      });

      const response = await axios.get(`${API}/admin/profiles?${params.toString()}`);
      setProfiles(response.data.profiles);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        total_pages: response.data.pagination?.total_pages || 0
      }));
    } catch (error) {
      console.error('Failed to fetch profiles:', error);
      toast.error('Failed to fetch profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleEditProfile = async (profile) => {
    try {
      // Fetch detailed profile information
      const response = await axios.get(`${API}/admin/profiles/${profile.user_id}`);
      setSelectedProfile(response.data);
      setIsEditModalOpen(true);
    } catch (error) {
      toast.error('Failed to fetch profile details');
    }
  };

  const handleUpdateProfile = async (userId, updatedData, profileType) => {
    try {
      if (profileType === 'basic') {
        await axios.put(`${API}/admin/users/${userId}/basic`, updatedData);
      } else if (profileType === 'seller') {
        await axios.put(`${API}/admin/profiles/${userId}/seller`, updatedData);
      } else if (profileType === 'buyer') {
        await axios.put(`${API}/admin/profiles/${userId}/buyer`, updatedData);
      }

      toast.success('Profile updated successfully');
      setIsEditModalOpen(false);
      setSelectedProfile(null);
      fetchProfiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleSearch = (searchTerm) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleDisplay = (role) => {
    switch (role) {
      case 'rnkee': return 'Seller';
      case 'rnker': return 'Buyer';
      case 'admin': return 'Admin';
      case 'gm': return 'GM';
      default: return role;
    }
  };

  const handleExportProfiles = async () => {
    try {
      const params = new URLSearchParams({
        ...filters,
        page: 1,
        per_page: 10000 // Get all profiles
      });

      const response = await axios.get(`${API}/admin/profiles?${params.toString()}`);
      const exportData = (response.data.profiles || []).map(profile => ({
        name: profile.name,
        email: profile.email,
        role: getRoleDisplay(profile.role),
        company: profile.company || '',
        phone: profile.phone || '',
        has_profile: profile.has_profile ? 'Yes' : 'No',
        status: profile.is_disabled ? 'Disabled' : 'Active',
        created_date: new Date(profile.created_at).toLocaleDateString()
      }));

      const csvContent = [
        'Name,Email,Role,Company,Phone,Has Profile,Status,Created Date',
        ...exportData.map(row => Object.values(row).map(val => 
          typeof val === 'string' && val.includes(',') ? `"${val}"` : val
        ).join(','))
      ].join('\n');

      const now = new Date().toISOString().split('T')[0];
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rnkem_profiles_export_${now}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${exportData.length} profiles exported successfully`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export profiles');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Profile Management</h2>
          <p className="text-gray-600 mt-1">Edit buyer and seller profiles across the platform</p>
        </div>
        <Button
          variant="outline"
          onClick={handleExportProfiles}
          disabled={loading}
        >
          Export All Profiles
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Search Profiles</Label>
              <Input
                placeholder="Search by name, email, company..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
            
            <div>
              <Label>Role Filter</Label>
              <Select
                value={filters.role_filter}
                onValueChange={(value) => setFilters(prev => ({ ...prev, role_filter: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="rnkee">Sellers</SelectItem>
                  <SelectItem value="rnker">Buyers</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                  <SelectItem value="gm">GM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profiles Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>All Profiles ({pagination.total})</CardTitle>
            <div className="text-sm text-gray-500">
              Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} profiles
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading profiles...</p>
            </div>
          ) : profiles.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No profiles found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Profile Status</TableHead>
                    <TableHead>Account Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {profiles.map((profile) => (
                    <TableRow key={profile.user_id} className={profile.is_disabled ? 'opacity-60 bg-gray-50' : ''}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{profile.name}</p>
                          <p className="text-sm text-gray-500">{profile.email}</p>
                          {profile.company && (
                            <p className="text-xs text-gray-400">{profile.company}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          profile.role === 'admin' ? 'bg-red-100 text-red-800' :
                          profile.role === 'gm' ? 'bg-purple-100 text-purple-800' :
                          profile.role === 'rnkee' ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {getRoleDisplay(profile.role)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          profile.has_profile ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {profile.has_profile ? 'Complete' : 'Incomplete'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          profile.is_disabled ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {profile.is_disabled ? 'Disabled' : 'Active'}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(profile.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditProfile(profile)}
                          >
                            Edit Profile
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex justify-between items-center mt-4">
                <div className="text-sm text-gray-500">
                  Page {pagination.page} of {pagination.total_pages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page <= 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page >= pagination.total_pages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Profile Modal */}
      {isEditModalOpen && selectedProfile && (
        <AdminProfileEditModal
          profileData={selectedProfile}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedProfile(null);
          }}
          onUpdate={handleUpdateProfile}
        />
      )}
    </div>
  );
};

// Admin Seller Approvals Page Component
const AdminSellerApprovalsPage = () => {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsModal, setDetailsModal] = useState({ open: false, loading: false, data: null });
  const [filters, setFilters] = useState({
    status: 'pending',
    search: '',
    seller_type: 'all_types'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0
  });

  useEffect(() => {
    fetchPendingApprovals();
  }, [filters, pagination.page]);

  const fetchPendingApprovals = async () => {
    try {
      setLoading(true);
      
      // Convert frontend filter values to backend expected values
      const backendFilters = {
        ...filters,
        status: filters.status === 'all' ? '' : filters.status,
        seller_type: filters.seller_type === 'all_types' ? '' : filters.seller_type
      };
      
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        per_page: pagination.per_page.toString(),
        ...backendFilters
      });

      const response = await axios.get(`${API}/admin/seller-approvals?${params.toString()}`);
      
      // Add null checks and default values
      const responseData = response.data || {};
      const sellers = responseData.sellers || [];
      const total = responseData.total || 0;
      
      setPendingApprovals(sellers);
      setPagination(prev => ({
        ...prev,
        total: total,
        total_pages: Math.ceil(total / prev.per_page)
      }));
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
      toast.error(`Failed to fetch pending approvals: ${error.response?.data?.detail || error.message}`);
      
      // Set empty state on error
      setPendingApprovals([]);
      setPagination(prev => ({
        ...prev,
        total: 0,
        total_pages: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (userId) => {
    try {
      setDetailsModal({ open: true, loading: true, data: null });
      
      const response = await axios.get(`${API}/admin/seller-approvals/${userId}`);
      setDetailsModal({ open: true, loading: false, data: response.data });
    } catch (error) {
      console.error('Failed to fetch seller details:', error);
      toast.error('Failed to fetch seller details');
      setDetailsModal({ open: false, loading: false, data: null });
    }
  };

  const handleApprovalAction = async (userId, action, reason = '') => {
    try {
      await axios.post(`${API}/admin/seller-approvals/${userId}/review`, {
        action: action,
        denial_reason: reason
      });
      
      toast.success(`Seller ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
      fetchPendingApprovals();
      
      // Close details modal if open
      if (detailsModal.open && detailsModal.data?.user_id === userId) {
        setDetailsModal({ open: false, loading: false, data: null });
      }
    } catch (error) {
      console.error(`Failed to ${action} seller:`, error);
      toast.error(`Failed to ${action} seller: ${error.response?.data?.detail || error.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge className={`${config.color} font-medium`}>
        {config.label}
      </Badge>
    );
  };

  const formatSellerType = (type) => {
    return type === 'individual' ? 'Individual' : 'Company';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Seller Approvals</h1>
          <p className="text-gray-600">Review and approve seller profile applications</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="seller_type">Seller Type</Label>
              <Select value={filters.seller_type} onValueChange={(value) => setFilters({...filters, seller_type: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_types">All Types</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="company">Company</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="search">Search</Label>
              <Input
                id="search"
                placeholder="Search by name or email..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
              />
            </div>
            
            <div className="flex items-end">
              <Button 
                onClick={() => setFilters({ status: 'pending', search: '', seller_type: 'all_types' })}
                variant="outline"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Approvals List */}
      <Card>
        <CardHeader>
          <CardTitle>
            Seller Applications ({pagination?.total || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <SkeletonTable rows={5} />
          ) : !pendingApprovals || pendingApprovals.length === 0 ? (
            <EmptyTable
              title="No Applications Found"
              description="No seller applications match your current filters."
              icon="👤"
            />
          ) : (
            <div className="space-y-4">
              {(pendingApprovals || []).map((approval) => (
                <div key={approval.user_id} className="border rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-lg">{approval.name}</h3>
                        {getStatusBadge(approval.approval_status)}
                        <Badge variant="outline">
                          {formatSellerType(approval.seller_type)}
                        </Badge>
                      </div>
                      <p className="text-gray-600">{approval.email}</p>
                      {approval.company && (
                        <p className="text-gray-600">Company: {approval.company}</p>
                      )}
                      {approval.phone && (
                        <p className="text-gray-600">Phone: {approval.phone}</p>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleViewDetails(approval.user_id)}
                        variant="outline"
                        size="sm"
                      >
                        View Details
                      </Button>
                      {approval.approval_status === 'pending' && (
                        <>
                          <Button
                            onClick={() => handleApprovalAction(approval.user_id, 'approve')}
                            className="bg-green-600 hover:bg-green-700"
                            size="sm"
                          >
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleApprovalAction(approval.user_id, 'reject')}
                            variant="destructive"
                            size="sm"
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {approval.bio && (
                    <div>
                      <Label className="font-medium">Bio:</Label>
                      <p className="text-gray-700 mt-1">{approval.bio}</p>
                    </div>
                  )}
                  
                  {approval.services && approval.services.length > 0 && (
                    <div>
                      <Label className="font-medium">Services:</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {approval.services.map((service, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {service}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="text-sm text-gray-500">
                    Applied: {new Date(approval.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <div className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} applications
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  variant="outline"
                  size="sm"
                >
                  Previous
                </Button>
                <Button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.total_pages}
                  variant="outline"
                  size="sm"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Seller Details Modal */}
      {detailsModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Seller Profile Details</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDetailsModal({ open: false, loading: false, data: null })}
                >
                  ✕ Close
                </Button>
              </div>

              {detailsModal.loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
              ) : detailsModal.data ? (
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold text-lg mb-3">Basic Information</h3>
                      <div className="space-y-2">
                        <div><strong>Name:</strong> {detailsModal.data.name}</div>
                        <div><strong>Email:</strong> {detailsModal.data.email}</div>
                        <div><strong>Company:</strong> {detailsModal.data.company || 'N/A'}</div>
                        <div><strong>Seller Type:</strong> {detailsModal.data.seller_type || 'individual'}</div>
                        <div><strong>Status:</strong> {getStatusBadge(detailsModal.data.approval_status)}</div>
                        {detailsModal.data.submitted_at && (
                          <div><strong>Submitted:</strong> {new Date(detailsModal.data.submitted_at).toLocaleDateString()}</div>
                        )}
                        {detailsModal.data.location && (
                          <div><strong>Location:</strong> {detailsModal.data.location}</div>
                        )}
                      </div>
                    </div>

                    {/* Company Information (if company type) */}
                    {detailsModal.data.seller_type === 'company' && (
                      <div>
                        <h3 className="font-semibold text-lg mb-3">Company Information</h3>
                        <div className="space-y-2">
                          {detailsModal.data.company_brand_name && (
                            <div><strong>Brand Name:</strong> {detailsModal.data.company_brand_name}</div>
                          )}
                          {detailsModal.data.company_website && (
                            <div><strong>Website:</strong> 
                              <a href={detailsModal.data.company_website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                                {detailsModal.data.company_website}
                              </a>
                            </div>
                          )}
                          {detailsModal.data.hq_city && detailsModal.data.hq_state && (
                            <div><strong>Headquarters:</strong> {detailsModal.data.hq_city}, {detailsModal.data.hq_state}</div>
                          )}
                          {detailsModal.data.company_logo_url && (
                            <div>
                              <strong>Logo:</strong>
                              <img src={detailsModal.data.company_logo_url} alt="Company Logo" className="mt-2 w-16 h-16 object-cover rounded" />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Bio */}
                  {(detailsModal.data.bio || detailsModal.data.company_bio) && (
                    <div>
                      <h3 className="font-semibold text-lg mb-3">
                        {detailsModal.data.seller_type === 'company' ? 'Company Bio' : 'Bio'}
                      </h3>
                      <p className="text-gray-700 bg-gray-50 p-4 rounded">
                        {detailsModal.data.company_bio || detailsModal.data.bio}
                      </p>
                    </div>
                  )}

                  {/* Individual Profile Sections (only for individual sellers) */}
                  {detailsModal.data.seller_type !== 'company' && (
                    <>
                      {/* Education */}
                      {detailsModal.data.education && detailsModal.data.education.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-lg mb-3">Education</h3>
                          <div className="space-y-3">
                            {detailsModal.data.education.map((edu, index) => (
                              <div key={index} className="bg-gray-50 p-4 rounded">
                                <div className="font-medium">{edu.degree} {edu.field_of_study && `in ${edu.field_of_study}`}</div>
                                <div className="text-gray-600">{edu.institution}</div>
                                <div className="text-gray-500 text-sm">
                                  {edu.start_year} - {edu.end_year || 'Present'}
                                </div>
                                {edu.description && (
                                  <div className="text-gray-700 mt-2">{edu.description}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Work Experience */}
                      {detailsModal.data.work_experience && detailsModal.data.work_experience.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-lg mb-3">Work Experience</h3>
                          <div className="space-y-3">
                            {detailsModal.data.work_experience.map((work, index) => (
                              <div key={index} className="bg-gray-50 p-4 rounded">
                                <div className="font-medium">{work.job_title}</div>
                                <div className="text-gray-600">{work.company}</div>
                                <div className="text-gray-500 text-sm">
                                  {work.start_date} - {work.end_date || 'Present'}
                                  {work.location && ` • ${work.location}`}
                                </div>
                                {work.description && (
                                  <div className="text-gray-700 mt-2">{work.description}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Professional Certifications */}
                      {detailsModal.data.certifications && detailsModal.data.certifications.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-lg mb-3">Professional Certifications</h3>
                          <div className="space-y-3">
                            {detailsModal.data.certifications.map((cert, index) => (
                              <div key={index} className="bg-gray-50 p-4 rounded">
                                <div className="font-medium">{cert.name}</div>
                                <div className="text-gray-600">{cert.issuing_organization}</div>
                                <div className="text-gray-500 text-sm">
                                  Issued: {cert.issue_date}
                                  {cert.expiration_date && ` • Expires: ${cert.expiration_date}`}
                                </div>
                                {cert.credential_id && (
                                  <div className="text-gray-500 text-sm">ID: {cert.credential_id}</div>
                                )}
                                {cert.credential_url && (
                                  <div>
                                    <a href={cert.credential_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                                      View Credential
                                    </a>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Skills */}
                  {detailsModal.data.skills && detailsModal.data.skills.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-lg mb-3">Skills</h3>
                      <div className="flex flex-wrap gap-2">
                        {detailsModal.data.skills.map((skill, index) => (
                          <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Company Highlights (for company sellers) */}
                  {detailsModal.data.seller_type === 'company' && detailsModal.data.company_highlights && detailsModal.data.company_highlights.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-lg mb-3">Company Highlights</h3>
                      <ul className="space-y-2">
                        {detailsModal.data.company_highlights.map((highlight, index) => (
                          <li key={index} className="flex items-start">
                            <span className="text-green-600 mr-2">•</span>
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Portfolio URLs */}
                  {((detailsModal.data.portfolio_urls && detailsModal.data.portfolio_urls.length > 0) || 
                    (detailsModal.data.company_portfolio_urls && detailsModal.data.company_portfolio_urls.length > 0)) && (
                    <div>
                      <h3 className="font-semibold text-lg mb-3">Portfolio</h3>
                      <div className="space-y-2">
                        {(detailsModal.data.portfolio_urls || detailsModal.data.company_portfolio_urls || []).map((url, index) => (
                          <div key={index}>
                            <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              {url}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Social Links */}
                  {((detailsModal.data.social_links && Object.keys(detailsModal.data.social_links).length > 0) ||
                    (detailsModal.data.company_social_links && Object.keys(detailsModal.data.company_social_links).length > 0)) && (
                    <div>
                      <h3 className="font-semibold text-lg mb-3">Social Links</h3>
                      <div className="space-y-2">
                        {Object.entries(detailsModal.data.company_social_links || detailsModal.data.social_links || {}).map(([platform, url]) => (
                          <div key={platform} className="flex items-center">
                            <span className="capitalize font-medium w-20">{platform}:</span>
                            <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-2">
                              {url}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Service Areas */}
                  {detailsModal.data.service_areas && detailsModal.data.service_areas.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-lg mb-3">Service Areas</h3>
                      <div className="flex flex-wrap gap-2">
                        {detailsModal.data.service_areas.map((area, index) => (
                          <span key={index} className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">
                            {area}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Languages */}
                  {detailsModal.data.languages && detailsModal.data.languages.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-lg mb-3">Languages</h3>
                      <div className="space-y-1">
                        {detailsModal.data.languages.map((language, index) => (
                          <div key={index} className="flex justify-between items-center">
                            <span>{language.language}</span>
                            <span className="text-gray-600 text-sm">{language.proficiency}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Additional Profile Information */}
                  <div className="bg-gray-50 p-4 rounded">
                    <h3 className="font-semibold text-lg mb-3">Additional Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {detailsModal.data.phone && (
                        <div><strong>Phone:</strong> {detailsModal.data.phone}</div>
                      )}
                      {detailsModal.data.years_of_experience && (
                        <div><strong>Years of Experience:</strong> {detailsModal.data.years_of_experience}</div>
                      )}
                      {detailsModal.data.hourly_rate && (
                        <div><strong>Hourly Rate:</strong> ${detailsModal.data.hourly_rate}</div>
                      )}
                      {detailsModal.data.availability && (
                        <div><strong>Availability:</strong> {detailsModal.data.availability}</div>
                      )}
                      {detailsModal.data.timezone && (
                        <div><strong>Timezone:</strong> {detailsModal.data.timezone}</div>
                      )}
                      {detailsModal.data.created_at && (
                        <div><strong>Profile Created:</strong> {new Date(detailsModal.data.created_at).toLocaleDateString()}</div>
                      )}
                      {detailsModal.data.updated_at && (
                        <div><strong>Last Updated:</strong> {new Date(detailsModal.data.updated_at).toLocaleDateString()}</div>
                      )}
                    </div>
                  </div>

                  {/* Listings */}
                  {detailsModal.data.listings && detailsModal.data.listings.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-lg mb-3">Listings ({detailsModal.data.listings.length})</h3>
                      <div className="space-y-3">
                        {detailsModal.data.listings.map((listing, index) => (
                          <div key={listing.id || index} className="border rounded p-3">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-medium">{listing.title}</h4>
                                <p className="text-gray-600 text-sm">{listing.description}</p>
                                <div className="text-sm text-gray-500 mt-1">
                                  Category: {listing.service_category} → {listing.service_subcategory}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-medium">${(listing.price_cents / 100).toFixed(2)}</div>
                                <div className={`text-xs px-2 py-1 rounded ${listing.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                  {listing.is_active ? 'Active' : 'Draft'}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Denial Reason (if denied) */}
                  {detailsModal.data.denial_reason && (
                    <div>
                      <h3 className="font-semibold text-lg mb-3 text-red-600">Denial Reason</h3>
                      <p className="text-red-700 bg-red-50 p-4 rounded">
                        {detailsModal.data.denial_reason}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {detailsModal.data.approval_status === 'pending' && (
                    <div className="flex gap-3 pt-4 border-t">
                      <Button
                        onClick={() => handleApprovalAction(detailsModal.data.user_id, 'approve')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Approve Seller
                      </Button>
                      <Button
                        onClick={() => {
                          const reason = prompt('Please provide a reason for rejection:');
                          if (reason) {
                            handleApprovalAction(detailsModal.data.user_id, 'deny', reason);
                          }
                        }}
                        variant="destructive"
                      >
                        Reject Seller
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Failed to load seller details.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Admin Points Management Page - Phase 5
const AdminPointsPage = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobsRunning, setJobsRunning] = useState({
    verifyAll: false,
    checkBonuses: false,
    detectAnomalies: false
  });
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    document.title = 'Points Management | Admin | Rnkem';
    fetchLeaderboard();
    fetchAlerts();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API}/points/admin/leaderboard?limit=50`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data.sellers || []);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await fetch(`${API}/points/admin/alerts?resolved=false&limit=20`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const handleRunJob = async (jobType) => {
    setJobsRunning(prev => ({ ...prev, [jobType]: true }));
    
    const endpoints = {
      verifyAll: '/points/admin/jobs/verify-all',
      checkBonuses: '/points/admin/jobs/check-bonuses',
      detectAnomalies: '/points/admin/jobs/detect-anomalies'
    };

    try {
      const response = await fetch(`${API}${endpoints[jobType]}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Job completed successfully');
        
        // Refresh data after job completes
        fetchLeaderboard();
        fetchAlerts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to run job');
      }
    } catch (error) {
      console.error('Error running job:', error);
      toast.error('Failed to run job');
    } finally {
      setJobsRunning(prev => ({ ...prev, [jobType]: false }));
    }
  };

  const handleRecalculateSeller = async (sellerId) => {
    setRecalculating(true);
    try {
      const response = await fetch(`${API}/points/admin/sellers/${sellerId}/recalculate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(
          data.discrepancy === 0 
            ? 'Points verified - no changes needed' 
            : `Points updated: ${data.old_total} → ${data.new_total}`
        );
        fetchLeaderboard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to recalculate points');
      }
    } catch (error) {
      console.error('Error recalculating points:', error);
      toast.error('Failed to recalculate points');
    } finally {
      setRecalculating(false);
      setSelectedSeller(null);
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      const response = await fetch(`${API}/points/admin/alerts/${alertId}/resolve`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Alert resolved');
        fetchAlerts();
      } else {
        toast.error('Failed to resolve alert');
      }
    } catch (error) {
      console.error('Error resolving alert:', error);
      toast.error('Failed to resolve alert');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Points Management</h1>
            <p className="text-gray-600 mt-1">Monitor and manage the seller points system</p>
          </div>
          <Link to="/admin">
            <Button variant="outline">
              ← Back to Admin
            </Button>
          </Link>
        </div>

        {/* Background Jobs Section */}
        <Card>
          <CardHeader>
            <CardTitle>Background Jobs</CardTitle>
            <p className="text-sm text-gray-600">Manually trigger system maintenance jobs</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                onClick={() => handleRunJob('verifyAll')}
                disabled={jobsRunning.verifyAll}
                className="flex items-center justify-center gap-2"
              >
                {jobsRunning.verifyAll ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Verify All Points
                  </>
                )}
              </Button>
              
              <Button
                onClick={() => handleRunJob('checkBonuses')}
                disabled={jobsRunning.checkBonuses}
                className="flex items-center justify-center gap-2"
              >
                {jobsRunning.checkBonuses ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <Award className="h-4 w-4" />
                    Check Bonuses
                  </>
                )}
              </Button>
              
              <Button
                onClick={() => handleRunJob('detectAnomalies')}
                disabled={jobsRunning.detectAnomalies}
                className="flex items-center justify-center gap-2"
              >
                {jobsRunning.detectAnomalies ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Detecting...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4" />
                    Detect Anomalies
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Alerts Section */}
        {alerts.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                System Alerts ({alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <div 
                    key={alert.id}
                    className="border border-red-200 bg-red-50 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-red-100 text-red-800">
                            {alert.type}
                          </Badge>
                          <Badge className="bg-orange-100 text-orange-800">
                            {alert.severity}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">
                          Seller: <span className="font-mono">{alert.seller_id}</span>
                        </p>
                        {alert.count && (
                          <p className="text-sm text-gray-600">
                            Count: {alert.count}
                          </p>
                        )}
                        {alert.samples && alert.samples.length > 0 && (
                          <details className="mt-2">
                            <summary className="text-sm text-blue-600 cursor-pointer">
                              View samples ({alert.samples.length})
                            </summary>
                            <pre className="text-xs bg-white p-2 rounded mt-1 overflow-auto">
                              {JSON.stringify(alert.samples, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResolveAlert(alert.id)}
                      >
                        Resolve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Leaderboard Section */}
        <Card>
          <CardHeader>
            <CardTitle>Points Leaderboard</CardTitle>
            <p className="text-sm text-gray-600">Top sellers by total points</p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Loader className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No sellers with points yet
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rank</TableHead>
                    <TableHead>Seller ID</TableHead>
                    <TableHead>Total Points</TableHead>
                    <TableHead>Initial Orders</TableHead>
                    <TableHead>Reorders</TableHead>
                    <TableHead>Reviews</TableHead>
                    <TableHead>Bonuses</TableHead>
                    <TableHead>Reorder %</TableHead>
                    <TableHead>Avg Rating</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leaderboard.map((seller, index) => (
                    <TableRow key={seller.seller_id}>
                      <TableCell className="font-bold">
                        {index + 1}
                        {index === 0 && <span className="ml-1">🥇</span>}
                        {index === 1 && <span className="ml-1">🥈</span>}
                        {index === 2 && <span className="ml-1">🥉</span>}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {seller.seller_id.slice(0, 8)}...
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-amber-100 text-amber-800">
                          {seller.points_total} pts
                        </Badge>
                      </TableCell>
                      <TableCell>{seller.breakdown?.initial_orders || 0}</TableCell>
                      <TableCell>{seller.breakdown?.reorders || 0}</TableCell>
                      <TableCell>{seller.breakdown?.reviews || 0}</TableCell>
                      <TableCell>{seller.breakdown?.bonuses || 0}</TableCell>
                      <TableCell>
                        {seller.stats?.reorder_percentage 
                          ? `${(seller.stats.reorder_percentage * 100).toFixed(1)}%`
                          : '0%'}
                      </TableCell>
                      <TableCell>
                        {seller.stats?.avg_rating 
                          ? seller.stats.avg_rating.toFixed(2)
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRecalculateSeller(seller.seller_id)}
                          disabled={recalculating && selectedSeller === seller.seller_id}
                        >
                          {recalculating && selectedSeller === seller.seller_id ? (
                            <Loader className="h-3 w-3 animate-spin" />
                          ) : (
                            'Recalc'
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Enhanced Admin Layout Component with Responsive Design & Accessibility
const AdminLayout = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const adminMenuItems = [
    { 
      path: '/admin', 
      label: 'Dashboard', 
      icon: '📊',
      description: 'Overview & Monthly Metrics'
    },
    { 
      path: '/admin/users', 
      label: 'User Management', 
      icon: '👥',
      description: 'Users, Admins, VIP Status'
    },
    { 
      path: '/admin/categories', 
      label: 'Categories', 
      icon: '🏷️',
      description: 'Service Categories & Subcategories'
    },
    { 
      path: '/admin/commission', 
      label: 'Commission', 
      icon: '💰',
      description: 'Rates & Commission Management'
    },
    { 
      path: '/admin/listings', 
      label: 'Listings', 
      icon: '📋',
      description: 'All Platform Listings'
    },
    { 
      path: '/admin/profiles', 
      label: 'Profiles', 
      icon: '👤',
      description: 'Buyer & Seller Profiles'
    },
    { 
      path: '/admin/seller-approvals', 
      label: 'Seller Approvals', 
      icon: '✅',
      description: 'Review & Approve Seller Applications'
    }
  ];

  const isActivePath = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin' || location.pathname === '/admin/dashboard';
    }
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Enhanced Admin Header with Mobile Support */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label="Toggle sidebar"
                aria-expanded={sidebarOpen}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Admin Portal</h1>
                <p className="text-sm text-gray-700 hidden sm:block">Welcome back, {user?.name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4">
              <Badge className="bg-emerald-100 text-emerald-800 font-medium border border-emerald-300 hidden sm:inline-flex">
                {user?.role?.toUpperCase()} ACCESS
              </Badge>
              <Link 
                to="/" 
                className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium p-2 sm:p-1 rounded-md hover:bg-blue-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label="Return to main site"
              >
                ← <span className="hidden sm:inline">Back to Main Site</span><span className="sm:hidden">Main</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="max-w-7xl mx-auto flex relative">
        {/* Enhanced Admin Sidebar Navigation - Mobile Responsive */}
        <div className={`
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          fixed lg:relative lg:flex
          w-72 bg-white shadow-lg lg:shadow-sm min-h-screen z-40
          transition-transform duration-300 ease-in-out
        `}>
          <div className="p-4 lg:p-6 w-full">
            {/* Mobile close button */}
            <div className="flex justify-between items-center mb-4 lg:hidden">
              <h2 className="text-lg font-semibold text-gray-900">Navigation</h2>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Close sidebar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <nav className="space-y-2" role="navigation" aria-label="Admin navigation">
              {adminMenuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center p-3 rounded-lg transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isActivePath(item.path)
                      ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 border border-transparent hover:border-gray-200'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  aria-current={isActivePath(item.path) ? 'page' : undefined}
                >
                  <span className="text-xl mr-3 group-hover:scale-110 transition-transform" role="img" aria-hidden="true">
                    {item.icon}
                  </span>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{item.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                      {item.description}
                    </div>
                  </div>
                </Link>
              ))}
            </nav>
            
            {/* Enhanced Quick Stats in Sidebar */}
            <div className="mt-6 lg:mt-8 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span role="img" aria-hidden="true">⚡</span>
                Quick Stats
              </h3>
              <AdminQuickStats />
            </div>
          </div>
        </div>

        {/* Enhanced Main Content Area with Better Spacing */}
        <div className="flex-1 p-4 sm:p-6 lg:p-8 min-h-screen">
          <div className="max-w-none">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Quick Stats Component
const AdminQuickStats = () => {
  const [stats, setStats] = useState({
    total_users: 0,
    total_listings: 0,
    active_orders: 0,
    monthly_gmv: 0
  });

  useEffect(() => {
    // This would fetch real quick stats from an API
    // For now, using placeholder data
    setStats({
      total_users: 156,
      total_listings: 89,
      active_orders: 23,
      monthly_gmv: 12450
    });
  }, []);

  return (
    <div className="space-y-2 text-sm">
      <div className="flex justify-between">
        <span className="text-gray-600">Users</span>
        <span className="font-medium">{stats.total_users}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-600">Listings</span>
        <span className="font-medium">{stats.total_listings}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-600">Active Orders</span>
        <span className="font-medium">{stats.active_orders}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-600">Monthly GMV</span>
        <span className="font-medium">${(stats.monthly_gmv / 100).toLocaleString()}</span>
      </div>
    </div>
  );
};

// Admin Profile Edit Modal Component
const AdminProfileEditModal = ({ profileData, isOpen, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [basicInfo, setBasicInfo] = useState({
    name: profileData.user.name || '',
    email: profileData.user.email || '',
    company: profileData.user.company || '',
    phone: profileData.user.phone || ''
  });
  const [sellerProfile, setSellerProfile] = useState({
    bio: profileData.profile?.bio || '',
    categories: profileData.profile?.categories || [],
    portfolio_urls: profileData.profile?.portfolio_urls || [],
    skills: profileData.profile?.skills || [],
    languages: profileData.profile?.languages || [{"language": "English", "proficiency": "Native"}],
    response_time: profileData.profile?.response_time || "within 24 hours",
    website_url: profileData.profile?.website_url || '',
    linkedin_url: profileData.profile?.linkedin_url || '',
    certifications: profileData.profile?.certifications || [],
    education: profileData.profile?.education || [],
    work_experience: profileData.profile?.work_experience || [],
    online_courses: profileData.profile?.online_courses || []
  });
  const [buyerProfile, setBuyerProfile] = useState({
    preferences: profileData.profile?.preferences || {}
  });

  const handleSaveBasicInfo = async () => {
    setLoading(true);
    try {
      await onUpdate(profileData.user.id, basicInfo, 'basic');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSellerProfile = async () => {
    setLoading(true);
    try {
      await onUpdate(profileData.user.id, sellerProfile, 'seller');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBuyerProfile = async () => {
    setLoading(true);
    try {
      await onUpdate(profileData.user.id, buyerProfile, 'buyer');
    } finally {
      setLoading(false);
    }
  };

  const addListItem = (setState, field, newItem) => {
    setState(prev => ({
      ...prev,
      [field]: [...prev[field], newItem]
    }));
  };

  const removeListItem = (setState, field, index) => {
    setState(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const updateListItem = (setState, field, index, updatedItem) => {
    setState(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? updatedItem : item)
    }));
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'basic', label: 'Basic Info', show: true },
    { id: 'seller', label: 'Seller Profile', show: profileData.user.role === 'rnkee' },
    { id: 'buyer', label: 'Buyer Profile', show: profileData.user.role === 'rnker' }
  ].filter(tab => tab.show);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-semibold">Edit Profile</h3>
            <p className="text-sm text-gray-500">{profileData.user.name} ({profileData.user.email})</p>
          </div>
          <Button variant="outline" onClick={onClose}>✕</Button>
        </div>

        {/* Tabs */}
        <div className="border-b mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={basicInfo.name}
                  onChange={(e) => setBasicInfo(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={basicInfo.email}
                  onChange={(e) => setBasicInfo(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div>
                <Label>Company</Label>
                <Input
                  value={basicInfo.company}
                  onChange={(e) => setBasicInfo(prev => ({ ...prev, company: e.target.value }))}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={basicInfo.phone}
                  onChange={(e) => setBasicInfo(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end pt-4">
              <Button onClick={handleSaveBasicInfo} disabled={loading}>
                {loading ? 'Saving...' : 'Save Basic Info'}
              </Button>
            </div>
          </div>
        )}

        {/* Seller Profile Tab */}
        {activeTab === 'seller' && profileData.user.role === 'rnkee' && (
          <div className="space-y-6">
            <div>
              <Label>Bio</Label>
              <textarea
                className="w-full p-2 border rounded-md"
                rows="4"
                value={sellerProfile.bio}
                onChange={(e) => setSellerProfile(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Tell potential buyers about yourself..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Response Time</Label>
                <Select
                  value={sellerProfile.response_time}
                  onValueChange={(value) => setSellerProfile(prev => ({ ...prev, response_time: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="within 1 hour">Within 1 hour</SelectItem>
                    <SelectItem value="within 6 hours">Within 6 hours</SelectItem>
                    <SelectItem value="within 24 hours">Within 24 hours</SelectItem>
                    <SelectItem value="within 2-3 days">Within 2-3 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Website URL</Label>
                <Input
                  value={sellerProfile.website_url}
                  onChange={(e) => setSellerProfile(prev => ({ ...prev, website_url: e.target.value }))}
                  placeholder="https://yourwebsite.com"
                />
              </div>
            </div>

            <div>
              <Label>LinkedIn URL</Label>
              <Input
                value={sellerProfile.linkedin_url}
                onChange={(e) => setSellerProfile(prev => ({ ...prev, linkedin_url: e.target.value }))}
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>

            {/* Skills */}
            <div>
              <Label>Skills</Label>
              <div className="space-y-2">
                {sellerProfile.skills.map((skill, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={skill}
                      onChange={(e) => updateListItem(setSellerProfile, 'skills', index, e.target.value)}
                      placeholder="Enter skill"
                    />
                    <Button
                      variant="outline"
                      onClick={() => removeListItem(setSellerProfile, 'skills', index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => addListItem(setSellerProfile, 'skills', '')}
                >
                  Add Skill
                </Button>
              </div>
            </div>

            {/* Service Categories */}
            <div>
              <Label>Service Categories</Label>
              <div className="space-y-2">
                {sellerProfile.categories.map((category, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={category}
                      onChange={(e) => updateListItem(setSellerProfile, 'categories', index, e.target.value)}
                      placeholder="Enter service category"
                    />
                    <Button
                      variant="outline"
                      onClick={() => removeListItem(setSellerProfile, 'categories', index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => addListItem(setSellerProfile, 'categories', '')}
                >
                  Add Category
                </Button>
              </div>
            </div>

            {/* Languages */}
            <div>
              <Label>Languages</Label>
              <div className="space-y-2">
                {sellerProfile.languages.map((lang, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={lang.language || ''}
                      onChange={(e) => updateListItem(setSellerProfile, 'languages', index, {...lang, language: e.target.value})}
                      placeholder="Language"
                      className="flex-1"
                    />
                    <Select
                      value={lang.proficiency || 'Beginner'}
                      onValueChange={(value) => updateListItem(setSellerProfile, 'languages', index, {...lang, proficiency: value})}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Beginner">Beginner</SelectItem>
                        <SelectItem value="Intermediate">Intermediate</SelectItem>
                        <SelectItem value="Advanced">Advanced</SelectItem>
                        <SelectItem value="Native">Native</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      onClick={() => removeListItem(setSellerProfile, 'languages', index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => addListItem(setSellerProfile, 'languages', {language: '', proficiency: 'Beginner'})}
                >
                  Add Language
                </Button>
              </div>
            </div>

            {/* Education */}
            <div>
              <Label>Education</Label>
              <div className="space-y-3">
                {sellerProfile.education.map((edu, index) => (
                  <div key={index} className="border p-3 rounded-md space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={edu.degree || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'education', index, {...edu, degree: e.target.value})}
                        placeholder="Degree (e.g., Bachelor of Computer Science)"
                      />
                      <Input
                        value={edu.institution || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'education', index, {...edu, institution: e.target.value})}
                        placeholder="Institution"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={edu.year || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'education', index, {...edu, year: e.target.value})}
                        placeholder="Year (e.g., 2020)"
                      />
                      <Button
                        variant="outline"
                        onClick={() => removeListItem(setSellerProfile, 'education', index)}
                        className="justify-self-end"
                      >
                        Remove Education
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => addListItem(setSellerProfile, 'education', {degree: '', institution: '', year: ''})}
                >
                  Add Education
                </Button>
              </div>
            </div>

            {/* Work Experience */}
            <div>
              <Label>Work Experience</Label>
              <div className="space-y-3">
                {sellerProfile.work_experience.map((work, index) => (
                  <div key={index} className="border p-3 rounded-md space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={work.title || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'work_experience', index, {...work, title: e.target.value})}
                        placeholder="Job Title"
                      />
                      <Input
                        value={work.company || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'work_experience', index, {...work, company: e.target.value})}
                        placeholder="Company"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        value={work.duration || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'work_experience', index, {...work, duration: e.target.value})}
                        placeholder="Duration (e.g., 2020-2022)"
                      />
                      <Input
                        value={work.location || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'work_experience', index, {...work, location: e.target.value})}
                        placeholder="Location"
                      />
                      <Button
                        variant="outline"
                        onClick={() => removeListItem(setSellerProfile, 'work_experience', index)}
                      >
                        Remove
                      </Button>
                    </div>
                    <textarea
                      className="w-full p-2 border rounded-md"
                      rows="2"
                      value={work.description || ''}
                      onChange={(e) => updateListItem(setSellerProfile, 'work_experience', index, {...work, description: e.target.value})}
                      placeholder="Job description and key achievements..."
                    />
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => addListItem(setSellerProfile, 'work_experience', {title: '', company: '', duration: '', location: '', description: ''})}
                >
                  Add Work Experience
                </Button>
              </div>
            </div>

            {/* Certifications */}
            <div>
              <Label>Certifications</Label>
              <div className="space-y-3">
                {sellerProfile.certifications.map((cert, index) => (
                  <div key={index} className="border p-3 rounded-md space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={cert.name || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'certifications', index, {...cert, name: e.target.value})}
                        placeholder="Certification Name"
                      />
                      <Input
                        value={cert.issuer || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'certifications', index, {...cert, issuer: e.target.value})}
                        placeholder="Issuing Organization"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        value={cert.year || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'certifications', index, {...cert, year: e.target.value})}
                        placeholder="Year Obtained"
                      />
                      <Input
                        value={cert.credential_url || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'certifications', index, {...cert, credential_url: e.target.value})}
                        placeholder="Credential URL (optional)"
                      />
                      <Button
                        variant="outline"
                        onClick={() => removeListItem(setSellerProfile, 'certifications', index)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => addListItem(setSellerProfile, 'certifications', {name: '', issuer: '', year: '', credential_url: ''})}
                >
                  Add Certification
                </Button>
              </div>
            </div>

            {/* Online Courses */}
            <div>
              <Label>Online Courses</Label>
              <div className="space-y-3">
                {sellerProfile.online_courses.map((course, index) => (
                  <div key={index} className="border p-3 rounded-md space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={course.name || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'online_courses', index, {...course, name: e.target.value})}
                        placeholder="Course Name"
                      />
                      <Input
                        value={course.provider || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'online_courses', index, {...course, provider: e.target.value})}
                        placeholder="Course Provider"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        value={course.year || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'online_courses', index, {...course, year: e.target.value})}
                        placeholder="Year Completed"
                      />
                      <Input
                        value={course.certificate_url || ''}
                        onChange={(e) => updateListItem(setSellerProfile, 'online_courses', index, {...course, certificate_url: e.target.value})}
                        placeholder="Certificate URL (optional)"
                      />
                      <Button
                        variant="outline"
                        onClick={() => removeListItem(setSellerProfile, 'online_courses', index)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => addListItem(setSellerProfile, 'online_courses', {name: '', provider: '', year: '', certificate_url: ''})}
                >
                  Add Online Course
                </Button>
              </div>
            </div>

            {/* Portfolio URLs */}
            <div>
              <Label>Portfolio URLs</Label>
              <div className="space-y-2">
                {sellerProfile.portfolio_urls.map((url, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={url}
                      onChange={(e) => updateListItem(setSellerProfile, 'portfolio_urls', index, e.target.value)}
                      placeholder="https://portfolio-example.com"
                    />
                    <Button
                      variant="outline"
                      onClick={() => removeListItem(setSellerProfile, 'portfolio_urls', index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => addListItem(setSellerProfile, 'portfolio_urls', '')}
                >
                  Add Portfolio URL
                </Button>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={handleSaveSellerProfile} disabled={loading}>
                {loading ? 'Saving...' : 'Save Seller Profile'}
              </Button>
            </div>
          </div>
        )}

        {/* Buyer Profile Tab */}
        {activeTab === 'buyer' && profileData.user.role === 'rnker' && (
          <div className="space-y-6">
            <div>
              <Label>Buyer Preferences</Label>
              <textarea
                className="w-full p-2 border rounded-md"
                rows="4"
                value={JSON.stringify(buyerProfile.preferences, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setBuyerProfile(prev => ({ ...prev, preferences: parsed }));
                  } catch (error) {
                    // Invalid JSON, don't update
                  }
                }}
                placeholder='{"preferred_categories": [], "budget_range": ""}'
              />
              <p className="text-xs text-gray-500 mt-1">Enter preferences as JSON format</p>
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={handleSaveBuyerProfile} disabled={loading}>
                {loading ? 'Saving...' : 'Save Buyer Profile'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;